import type { ZielinventarDokument } from '../../shared/guide-zielinventar';
import type { RichtungDokument } from '../../shared/guide-richtung';
import type { Hauptstatus } from '../../shared/guide-hauptstatus';
import type { Schnellbuehne } from '../../shared/schnellbuehnen';
import CollapsibleSection from './CollapsibleSection';
import { useGuideSend } from './GuideSendContext';
import { merkeEingaben, lokalerRaum, type Verschoben } from './guide-antwort-sofort';
import { teileNachEigentum } from './guide-partner-eigentum';
import { verschobeneAbschluesse } from './guide-abschluss-sofort';
import { platzTitel, beendeOhnePlatz, fordereBuehneAn, ladeBeendetOhneErgebnis, klickeBeendetWeg, starteBuehneErneut, type PlatzAktionErgebnis, type BeendetOhneErgebnis, type StartGescheitert } from './guide-chat-ohne-buehne-raum';
import type { Job } from './GuideSendQueue';
import './GuideStatusleiste.css';
import './GuideArbeitsliste.css';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';
import { AssistentStatus } from './AssistentStatus';
/**
 * DER HOF ALS SPALTE — die Guide-Ansicht, in der es nur noch Raeume gibt.
 *
 * Verbindlich ist `koordination/HOFSTAAT-OBJEKTE.md`; die Anordnung stammt aus der
 * Skizze in `koordination/entscheidungen/2026-09-11-hofstaat-story.html`, Abschnitt
 * "Wie der Guide dafuer aussehen muesste". Von oben nach unten:
 *
 *   Zeitstrahl des Tages · Vor dir · Vorraum | Warteraum ·
 *   Sitzungssaal | Pausenraum · Filterleiste
 *
 * WARUM DIESE DATEI UEBERHAUPT EXISTIERT. Bis zum 11.09.2026 zeigte die Spalte zwei
 * Welten uebereinander: oben die neue Buehnenreihe, darunter unveraendert die alte
 * (Karten-Reihe, "Alle Boards · 19 Themen", Bloecke). Die alte Haelfte hing an einer
 * VIERSTUFIGEN Sichtbarkeitskette — Thema in einem Block, Block offen, Thema hat eine
 * offene Karte, Karte hat Optionen —, und von 483 Beratern kamen so 61 bei Rafael an.
 * Jede dieser vier Huerden hing am Thema oder an seinem Block.
 *
 * DESHALB DIE EINZIGE REGEL DIESER DATEI: **Sichtbarkeit haengt am RAUM und an nichts
 * sonst.** Kein Thema, kein Block, kein Kartenzustand redet mit. Das Thema steht als
 * kleine Zeile ueber der Frage — als Etikett, nicht als Tor.
 *
 * UND: KEIN AUFKLAPPEN UEBER LEBENDEN BUEHNEN. Rafaels Massstab lautet "ein Klick zu
 * jeder Buehne". Ein <details> waere schon wieder eine Stufe, also steht jede lebende
 * Buehne sofort im DOM. Zugeklappt ist einzig, was NICHT mehr lebt (die Beendeten) —
 * die sind ueber ihren Zaehler erreichbar und draengen nicht.
 *
 * DER HAUSFILTER WIRD HIER GESETZT, NICHT NUR GELESEN. Er lag bisher in den
 * Schaltern der Coach-Uebersicht (`cu-h1`/`cu-h2`); mit ihr verschwindet seine
 * einzige Bedienstelle. Ohne diese Leiste haette der Filter weitergewirkt, ohne dass
 * ihn noch jemand umstellen kann — ein stiller Filter ist genau die Krankheit, gegen
 * die dieser Umbau gebaut ist.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { aktuellerStatusfilter, ergebnisDa, STATUS_FILTER_WORT, STATUS_REIHENFOLGE, STATUS_SYMBOL, STATUS_WORT, STATUS_ZEILE_WORT, wartetGrund, ohneSchrittZeile, ergebnisZusatz, anderesGrund, abhaengigkeitWort, offeneAbhaengigkeiten, sichtbareStatus, DIVERS_SCHLUESSEL, DIVERS_ERKLAERUNG, zuordnungsGrund, filtereNachStatus, pausenGrund, statusVon, statusZaehler, ALLE_AUSWAHL, ansichtSitzung, sitzungFuerNutzer, zieleAusGruppen, hinweisNach, merkeAnsichtSitzung, sternSichtbar, wirksameAnsicht, PRIORITAET_WORT, aufwandWort, brauchtDich, datumDe, begrenzeGruppen, dringendsteBedarf, fristDatum, gruppiere, kompaktInfoZeilen, naechsterTermin, tabKurzname, sichtbareGruppen, tabNachTaste, tageBis, unterzielKurz, wartendeNachZielen, wartetRaum, wirksameAuswahl, zeitsummeKurz, zeitsummeWort, zielstandWort, type Ansicht, type AnsichtsHinweis, type Auswahl, type Status, type HinweisEreignis, type InfoZeile, type OberthemaBuehne, type Oberthemagruppe, type Unterzielgruppe, type WartReihenfolge } from './guide-oberthemen';
import './GuideOberthemen.css';
import { HeuteDran } from './GuideHeuteDran';
import { guideAnsichtSteuerung, useAnsichtKopplung, useGuideAnsichtSteuerung } from '../lib/guideAnsichtSteuerung';
import { useGuideAnsichtSitzung } from '../lib/useGuideAnsichtSitzung';
import { setzeGuideHausfilter, useGuideHausfilter } from './GuideFlaeche';
import { useAuth } from '../contexts/AuthContext';
import { useAngeheftetId } from '../lib/buehnenAnheften';

import type { Layoutrezept } from './layoutrezept';
import { useLiveChannel } from '../hooks/useLiveChannel';
import { GuideHttpFehler, guideNetzFehler } from '../lib/guideNetz';
import { antwortHaengtKurz, antwortHaengtLang, type AntwortHaengt } from './guide-antwort-haengt';

const API = '/api';

/** Wie der Server eine Zustellung meldet — zugestellt oder ausdruecklich nicht. */
export type Zustellung =
  | { stand: 'zugestellt'; am: string; an: string }
  | { stand: 'unzugestellt'; am: string; fehler: string };

/** Eine Entscheidung an der Buehne. Form und Pflichtfelder stehen im Server;
 *  `zustand` ist gerechnet und darf nie zurueckgeschrieben werden. */
export interface Entscheidung {
  id: string; titel: string; entscheidung: string;
  optionen: { kuerzel: string; text: string; folge: string }[];
  empfehlung: string; gegenargument: string; ohne_entscheidung: string;
  stufe: number; gestellt_am: string; gestellt_von: string;
  /**
   * `art` unterscheidet den Klick vom geschriebenen Satz (Server: `AntwortSchema` in
   * lib/guide-buehnen.ts, seit 13.09.2026). OPTIONAL, weil eine aeltere Server-Fassung
   * das Feld nicht mitschickt — Leser pruefen deshalb auf `=== 'freitext'` und
   * behandeln alles andere als Options-Antwort, statt umgekehrt.
   * `kuerzel` fehlt bei einer freien Antwort; `text` traegt dann Rafaels Wortlaut.
   */
  antwort?: { art?: 'option' | 'freitext'; kuerzel?: string; text: string; am: string; von: string; notiz?: string };
  zustellung?: Zustellung;
  verarbeitet?: { am: string; von: string; betroffene: string[] };
  zurueckgezogen?: { am: string; von: string; beleg?: { eingabe: string; sitzung: string; am: string }; mit_abloesung?: { buehne: string; grund: string } };
  /**
   * DIESE FRAGE STEHT WOANDERS (Server: `UmzugSchema`, seit 13.09.2026). Gemessen war
   * der Fall: beim Buendeln bekam eine Frage eine neue Kennung, Rafaels Antwort landete
   * an der neuen, die alte blieb offen — fuenfmal. Die alte Fassung zeigt jetzt nach
   * vorn, und genau deshalb darf die Flaeche sie nicht weiter als offene Frage
   * anbieten: ein Klick dort geht ins Leere (der Server weist ihn mit 409 ab).
   */
  umgezogen?: { buehne: string; entscheidung: string; am: string; von: string; grund: string };
  /** Bei einer uebernommenen Frage: wo sie vorher stand. */
  vorgaenger?: { buehne: string; entscheidung: string };
  zustand: 'offen' | 'umgezogen' | 'zurueckgezogen' | 'unzugestellt' | 'gesammelt' | 'zugestellt' | 'verarbeitet';
}

/**
 * WAS RAFAEL NOCH BEANTWORTEN KANN — die EINE Stelle, die das entscheidet.
 *
 * Drei Ausgaenge nehmen eine Frage aus seiner Reihe: eine Antwort, ein Zurueckziehen
 * und (seit 13.09.2026) ein Umzug. Der dritte kam dazu, nachdem fuenf Fragen offen
 * standen, deren Antwort unter einer Zwillings-Kennung auf einer anderen Buehne lag.
 * Die Bedingung steht hier als Funktion und nicht viermal als Ausdruck: bis zum
 * 13.09. war sie an vier Stellen ausgeschrieben, und eine vierte Bedingung haette
 * genau eine davon vergessen.
 */
export function istOffeneFrage(e: Pick<Entscheidung, 'antwort' | 'zustand'>): boolean {
  return !e.antwort && e.zustand !== 'zurueckgezogen' && e.zustand !== 'umgezogen';
}

/**
 * EIN VORSCHLAG — das leichte Gegenstueck zu `Entscheidung` (Rafael, 17.09.2026
 * ~12:4xZ + 13:0xZ, Kanzler-Freigabe 12:57Z + 13:0xZ). Kein `folge` je Option,
 * keine `empfehlung`, kein `gegenargument`, keine `stufe` — siehe `VorschlagSchema`
 * in server/lib/guide-buehnen.ts. `zustand` gibt es hier nicht: nur `antwort`
 * gesetzt oder nicht, kein Zurueckziehen, kein Umzug.
 */
export interface Vorschlag {
  zurueckgezogen?: { am: string; von: string; beleg?: { eingabe: string; sitzung: string } };
  id: string; text: string;
  optionen: { kuerzel: string; text: string }[];
  gestellt_am: string; gestellt_von: string;
  antwort?: { art?: 'option' | 'freitext'; kuerzel?: string; text: string; am: string; von: string; notiz?: string };
}

export function istOffenerVorschlag(v: Pick<Vorschlag, 'antwort' | 'zurueckgezogen'>): boolean {
  return !v.antwort && !v.zurueckgezogen;
}

export interface Nachricht { id: string; text: string; am: string; von: string; zustellung?: Zustellung }

/**
 * Der Klick auf eine Sitzung OHNE Berater (Regel 6, zweite Haelfte: "ein Klick zu
 * jeder lebenden Sitzung"). `eigenesHaus` ist das Haus, das diese Zeile geliefert hat
 * (`GET /guide/sitzungen` meldet es als `haus` im Antwortkopf) — der Aufrufer
 * vergleicht es mit `haus` der Zeile, um zwischen dem sicheren Im-Haus-Weg
 * (`sitzungAlsBuehne`) und dem Weg ins andere Haus (`/guide/berater/:id/zeigen`) zu
 * unterscheiden. Diese Datei trifft die Wahl nicht selbst — das bleibt GuidePanel.tsx
 * vorbehalten, das schon beide Wege kennt.
 */
export interface SitzungOhneBerater {
  sessionId: string; name: string; haus?: string; eigenesHaus?: string | null;
  accountId?: string; projectId?: string;
}

/**
 * DER GEMESSENE VERSTOSS GEGEN "EINE BUEHNE, EIN BERATER" (Server:
 * `pruefeEinsZuEins` in lib/hof-urteil.ts, ausgeliefert als
 * `je_haus.<haus>.eins_zu_eins`).
 */
interface EinsZuEins {
  doppelt_besetzt: Array<{ buehne: string; berater: number }>;
  berater_mehrfach: Array<{ berater: string; buehnen: string[] }>;
}

/**
 * DER VERSTOSS GEHOERT AN DIE FLAECHE, NICHT NUR IN EINE ANTWORT.
 *
 * Rafael, 13.09.2026 07:09:02Z: "das eine session auf zwei buehnen arbeitet darf
 * konstruktionsbedingt nicht gehen - da muss ein fehler kommen", 07:12:27Z: "da muss
 * das cui sonst fehler ausgeben oder im guide anzeigen damit der assistent das
 * problem erkennt". Gemessen wurde er seit dem 12.09. — gesehen hat ihn niemand: die
 * Zahl stand nur im JSON, und im Urteil nur dann, wenn zufaellig eine Runde lief.
 *
 * Er steht GANZ OBEN und nicht bei den Raeumen: er ist keine Eigenschaft eines
 * Raumes, sondern die Aussage, dass die Raumzahlen selbst gerade nicht stimmen.
 */
/** Ein Verstoss gegen Rafaels Zielbild (Server: lib/hof-invarianten.ts). Rot, nie versteckt. */
interface InvariantenVerstoss { buehne: string; art: string; text: string; korrektur?: { raum: string; grund: string } }
function InvariantenBand({ verstoesse }: { verstoesse: InvariantenVerstoss[] | undefined }) {
  if (!verstoesse?.length) return null;
  return (
    <div data-ai-id="guide-invarianten" style={{ color: 'var(--tn-rosa)', fontSize: 10.5, marginBottom: 4, borderBottom: '1px solid var(--tn-border)', padding: '4px 0' }}>
      <strong>Zielbild verletzt · {verstoesse.length}</strong> — der Assistent korrigiert im Fünf-Minuten-Takt, was er darf; der Rest steht hier, bis jemand handelt.
      <ul style={{ margin: '3px 0 0 14px', padding: 0, lineHeight: 1.5 }}>
        {verstoesse.map((v, i) => <li key={i}>{v.buehne !== '*' && <code>{v.buehne}</code>} {v.text}{v.korrektur ? ` → ${v.korrektur.raum}` : ''}</li>)}
      </ul>
    </div>
  );
}

function EinsZuEinsBand({ befund }: { befund: EinsZuEins | undefined }) {
  if (!befund) return null;
  const zeilen = [
    ...befund.doppelt_besetzt.map(d => `${d.buehne}: ${d.berater} lebende Berater statt einem`),
    ...befund.berater_mehrfach.map(m => `Berater ${m.berater} hängt an ${m.buehnen.length} Bühnen: ${m.buehnen.join(', ')}`),
  ];
  if (!zeilen.length) return null;
  return (
    <details data-ai-id="guide-eins-zu-eins-verletzt" style={{
      border: '1px solid var(--tn-red)', borderRadius: 9, padding: '4px 6px', marginBottom: 3,
      fontSize: 11, color: 'var(--tn-red)', background: 'rgba(247,118,142,0.07)',
    }}>
      <summary style={{ cursor: 'pointer' }}><span role="alert">{zeilen.length} widersprüchliche Berater-Zuordnungen</span></summary>
      <div style={{ marginTop: 4, color: 'var(--tn-text-muted)' }}>
        Solange das so ist, sagen die Zahlen in den Räumen nicht mehr, wie viele Chats wirklich
        arbeiten — und bei einer Antwort weiß niemand, welche Sache nachzuarbeiten ist.
      </div>
      <ul style={{ margin: '5px 0 0 16px', padding: 0 }}>
        {zeilen.map(z => <li key={z}>{z}</li>)}
      </ul>
    </details>
  );
}

/**
 * WIE ES DEM BERATER EINER SITZUNG GEHT (Server: `beraterZustaende` in
 * routes/guide.ts, ausgeliefert an jeder Buehne als `berater[]`).
 *
 * `lebt` UND `blockiert` SIND ZWEI ACHSEN, nicht ein Zustand: ein Berater im
 * Konto-Limit lebt und arbeitet trotzdem nicht. Genau diese Verwechslung hat Rafael
 * am Morgen des 13.09. irritiert — die Karte schrieb "ueberholt · 30 min", was wie
 * normaler Rueckstand aussieht.
 */
export interface BeraterZustand {
  sitzung: string;
  lebt: boolean;
  /** Nicht gefinished — Rafaels Bezugsgroesse fuer den Zaehlabgleich, weiter als `lebt`. */
  offen: boolean;
  /** Offene Sub-Sitzungen dieses Beraters (transitiv). "1 Berater + N Arbeiter". */
  arbeiter: string[];
  blockiert: string | null;
  modell: string | null;
  titel: string | null;
  konto: string | null;
  /** Prozentzahlen sind `null`, wenn das Konto NICHT gemessen werden konnte —
   *  `herkunft` sagt, welcher der drei Faelle es ist. Eine 0 waere eine Luege. */
  kontingent: {
    woche_prozent: number | null; fenster_prozent: number | null;
    stand: 'safe' | 'warning' | 'critical';
    herkunft: 'cli' | 'scraper' | 'stale' | 'missing' | 'unmeasured';
    grund: string | null;
  } | null;
  taetigkeit: { art: string; wort: string; seit: string | null } | null;
}

/**
 * DIE GEGENUEBERSTELLUNG "BUEHNEN MIT BERATER" GEGEN "LEBENDE SITZUNGEN"
 * (Server: `abgleichFuer` in routes/guide-buehnen-routen.ts).
 */
interface Abgleich {
  haus: 'dev' | 'partner';
  chats_offen: number;
  davon_berater: number;
  davon_arbeiter: number;
  davon_aemter: number;
  davon_pruefungen?: number;
  davon_eigenstaendig?: number;
  buehnen_mit_berater: number;
  buehnen_lebend: number;
  chats_ohne_buehne: Array<{
    sitzung: string; titel: string | null; modell: string | null; konto: string | null;
    tot: boolean; seit: string | null; zustand: string | null;
    /** Wann der Chat angelegt wurde (ISO) oder null, wenn der Server es nicht weiss. */
    gestartet?: string | null;
    /** Nach drei erfolglosen Anlaeufen der Grund, sonst null — siehe `weckdefektOhneBuehne`. */
    weckdefekt?: {
      versuche: number; seit: string; letzterVersuch: string | null;
      ursache: string; satz: string; nachgesehen: string[];
    } | null;
    /** Warum kein Platz (Server: lib/guide-platz.ts). Fehlt bei einem aelteren Server. */
    grund?: 'ohne-buehne' | 'auftraggeber-beendet' | 'frueheres-amt' | 'zuordnung-gescheitert';
    grund_text?: string;
    auftraggeber?: string;
    /** N5: früheres Amt, dessen Beenden bei der Ablöse scheiterte. */
    erneut_beenden?: boolean;
  }>;
  buehnen_ohne_berater: string[];
  /** Bühnen, deren Berater-Start scheiterte (Baustein 8). Fehlt bei älterem Server. */
  buehnen_start_gescheitert?: StartGescheitert[];
  /** Ämter mit arbeitenden Unteraufträgen (Server `abgleichFuer`, Baustein 4). Fehlt bei älterem Server. */
  aemter_arbeit?: Array<{ sitzung: string; amt: string; arbeitende: number }>;
  /** Von Partnern selbst begonnene Chats ohne Bühne (Server `abgleichFuer`, 20.10.2026): erlaubt,
   *  kein Defekt, kein Anstupser. `partner` = Nutzerkennung des Eigentümers. Fehlt bei älterem Server. */
  freie_chats?: Array<{
    partner: string | null; sitzung: string; titel: string | null; modell: string | null; konto: string | null;
    tot: boolean; seit: string | null; zustand: string | null; gestartet?: string | null;
  }>;
}

/**
 * FREIE CHATS — EIN EIGENER ABSCHNITT, KEIN DEFEKT (Rafael, 20.10.2026, b-partner-chats-ohne-buehne-20261020).
 *
 * Ein Partner darf Chats ohne Bühne beginnen (hofbetrieb.md: „Ausgenommen sind … die Chats seiner
 * Partner"; 23.09.2026: keine Bühnenpflicht, keine Hof-Nachrichten). Bis heute blendete Rafaels
 * Sicht sie ganz aus, und in der Sicht des Partners selbst standen sie als „Interne Zuordnung noch
 * offen" — Patrick las seine eigenen Gespräche als Fehler. Hier stehen sie grau, je Partner
 * gruppiert, zugeklappt, mit dem Satz, dass nichts zu tun ist. Kein Knopf zum Schließen, kein
 * Anstupsen: der Hof mischt sich in Kundenchats nicht ein.
 */
function FreieChats({ abgleich }: { abgleich: Abgleich | undefined }) {
  const liste = abgleich?.freie_chats ?? [];
  if (!liste.length) return null;
  const jePartner = new Map<string, typeof liste>();
  for (const z of liste) {
    const k = z.partner || '(Eigentümer unbekannt)';
    jePartner.set(k, [...(jePartner.get(k) ?? []), z]);
  }
  const gruppen = [...jePartner.entries()].sort((a, b) => a[0].localeCompare(b[0], 'de'));
  return (
    <details data-ai-id="guide-freie-chats" style={{ fontSize: 11, color: 'var(--tn-text-muted)', marginBottom: 10, padding: '4px 0' }}>
      <summary style={{ cursor: 'pointer' }}>
        Freie Chats · {liste.length}
        <span style={{ fontWeight: 400 }}> · von Partnern selbst begonnen, ohne Bühne — erlaubt, nichts zu tun</span>
      </summary>
      <div style={{ padding: '4px 8px', overflowWrap: 'anywhere' }}>
        {gruppen.map(([partner, chats]) => (
          <div key={partner} data-ai-id="guide-freie-chats-partner" style={{ marginTop: 3 }}>
            <strong>{partner}</strong> ({chats.length})
            <ul style={{ margin: '2px 0 0 16px', padding: 0 }}>
              {chats.map(z => (
                <li key={z.sitzung} title={z.zustand ?? undefined}>
                  {z.titel || z.sitzung.slice(0, 8)}
                  {z.modell && <span> [{z.modell}]</span>}
                  {z.tot ? ' · ruht' : ' · läuft'}
                  {z.seit && <span> · seit {dauer(z.seit)}</span>}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </details>
  );
}

/**
 * DIE ZAHL IM GUIDE UND DIE ZAHL IN "ALLE CHATS" MUESSEN DIESELBE SEIN.
 *
 * Rafael, 13.09.2026 07:38:41Z (mit Bild): "es sollte eigentlich das gleiche sein wie
 * die nummer im all chats - jede buehne einen berater". Beide Zahlen gab es schon,
 * gegeneinander gehalten hat sie nie jemand — und wenn sie abwichen, war nicht zu
 * sehen, warum.
 *
 * STIMMT ES, STEHT NUR EINE ZEILE DA. Ein Band, das im Normalfall schweigt, waere
 * hier falsch: die Uebereinstimmung IST die Auskunft, die er sucht ("passt das
 * gerade?"). Weicht es ab, kommen beide Richtungen dazu, mit Namen statt Zahlen.
 *
 * ES IST KEIN FEHLER, SONDERN EINE MESSUNG. Eine Sitzung ohne Buehne kann voellig in
 * Ordnung sein — Rafael faengt Chats ausserhalb des Hofes an, und ob sie
 * hineingehoeren, ist seine Frage (07:15:02Z), nicht die des Servers. Deshalb Grau
 * und nicht Rot; rot ist der 1:1-Verstoss darueber, denn der DARF nicht sein.
 */
function AbgleichZeile({ abgleich }: { abgleich: Abgleich | undefined }) {
  if (!abgleich) return null;
  const tote = abgleich.chats_ohne_buehne.filter(c => c.tot);
  const lebendeOhne = abgleich.chats_ohne_buehne.filter(c => !c.tot);
  const stimmt = !abgleich.chats_ohne_buehne.length && !abgleich.buehnen_ohne_berater.length;
  return (
    <details data-ai-id="guide-abgleich" style={{
      fontSize: 10.5, color: 'var(--tn-text-muted)', marginBottom: 3,
      borderBottom: '1px solid var(--tn-border)', padding: '4px 0',
    }}>
      <summary style={{ cursor: 'pointer', color: stimmt ? 'var(--tn-text-muted)' : 'var(--tn-orange)' }}>
        {abgleich.buehnen_mit_berater}/{abgleich.buehnen_lebend} Bühnen mit zugeordnetem Chat
        {abgleich.chats_ohne_buehne.length > 0 && ` · ${abgleich.chats_ohne_buehne.length} Chats ohne Bühne`}
        {abgleich.buehnen_ohne_berater.length > 0 && ` · ${abgleich.buehnen_ohne_berater.length} ohne Berater`}
        {!!abgleich.freie_chats?.length && ` · ${abgleich.freie_chats.length} freie Partner-Chats`}
      </summary>
      <div style={{ maxHeight: 240, overflowY: 'auto', overflowWrap: 'anywhere', padding: '6px 8px' }}>
      {/*
        DIE AUFSCHLUESSELUNG IST DIE ANTWORT auf "warum sind es mehr Chats als
        Buehnen?" — ohne sie sieht jede Delegation und jedes Amt wie ein Fehler aus.
      */}
      <span style={{ color: stimmt ? 'var(--tn-green)' : 'var(--tn-orange)', fontWeight: 600 }}>
        {abgleich.chats_offen} offene Chats = {abgleich.davon_berater} Berater
        {abgleich.davon_arbeiter > 0 && ` + ${abgleich.davon_arbeiter} Arbeiter`}
        {abgleich.davon_aemter > 0 && ` + ${abgleich.davon_aemter} Ämter`}
        {!!abgleich.davon_pruefungen && ` + ${abgleich.davon_pruefungen} Prüfchats`}
        {!!abgleich.davon_eigenstaendig && ` + ${abgleich.davon_eigenstaendig} eigenständige Chats`}
        {abgleich.chats_ohne_buehne.length > 0 && ` + ${abgleich.chats_ohne_buehne.length} ohne Bühne`}
      </span>
      {' · '}{abgleich.buehnen_mit_berater} von {abgleich.buehnen_lebend} Bühnen mit zugeordnetem Chat
      {stimmt && ' — die Zahlen passen zusammen.'}
      {!!lebendeOhne.length && (
        <div style={{ marginTop: 3 }} data-ai-id="guide-abgleich-chat-ohne-buehne">
          <strong>Chat ohne Bühne ({lebendeOhne.length}):</strong>{' '}
          {lebendeOhne.map(s => `${s.titel || s.sitzung.slice(0, 8)}${s.modell ? ` [${s.modell}]` : ''}`).join(' · ')}
        </div>
      )}
      {/*
        TOTE CHATS STEHEN GETRENNT UND FARBIG. Rafael, 13.09.2026: "Tote chats muessen
        in den Pausenraum und dort angezeigt werden - dann kann der Assistent
        beschliessen ob sie gefinished werden oder im Pausenraum bleiben." Angezeigt
        werden sie hier; die ENTSCHEIDUNG des Assistenten ist noch nicht gebaut (er
        darf heute keine Sitzung schliessen), und das steht ausdruecklich dabei,
        statt eine Handlung vorzutaeuschen, die niemand ausfuehrt.
      */}
      {!!tote.length && (
        /* Bernstein statt Rot, aus demselben Grund wie an der Zeile: "ohne laufenden
         * Prozess" ist richtig gemessen, aber ein roter Block daneben liest sich als
         * Verlust. Weckbar ist kein Schaden. */
        <div style={{ marginTop: 4, color: 'var(--tn-orange)' }} data-ai-id="guide-abgleich-tote-chats">
          <strong>Offene Chats ohne laufenden Prozess — ruhend, weckbar ({tote.length}):</strong>
          <ul style={{ margin: '2px 0 0 16px', padding: 0 }}>
            {tote.map(s => (
              <li key={s.sitzung} title={s.zustand ?? undefined}>
                {s.titel || s.sitzung.slice(0, 8)}
                {s.konto && <span style={{ color: 'var(--tn-text-muted)' }}> · {s.konto}</span>}
                {s.seit && <span style={{ color: 'var(--tn-text-muted)' }}> · seit {dauer(s.seit)}</span>}
              </li>
            ))}
          </ul>
          <div style={{ color: 'var(--tn-text-muted)' }}>
            Diese Chats haben keine Bühne. Für sie wurde noch keine Zuordnung oder Abschlussentscheidung getroffen.
            Das ist kein Nachweis eines Kontolimits.
          </div>
        </div>
      )}
      {!!abgleich.buehnen_ohne_berater.length && (
        <div style={{ marginTop: 3 }} data-ai-id="guide-abgleich-buehne-ohne-berater">
          <strong>Bühne ohne Berater ({abgleich.buehnen_ohne_berater.length}):</strong>{' '}
          {abgleich.buehnen_ohne_berater.join(' · ')}
        </div>
      )}
      </div>
    </details>
  );
}

/** Wie der Server eine Buehne ausliefert (`fuerAnsicht` in lib/guide-buehnen.ts). */
export interface BuehneAnsicht {
  schnellbuehne?: Schnellbuehne;
  unteraufgaben?: Array<{ sitzung: string; titel: string | null; zustand: string; arbeitet: boolean }>;
  /** Present only when the canonical Guide server supports the task API. */
  aufgaben?: unknown;
  arbeit_laeuft?: boolean;
  arbeitsmessung?: 'arbeitet' | 'nicht_arbeitend' | 'unbekannt';
  /** Berater + Arbeiter dieser Bühne, die gerade arbeiten (Server `arbeitendeSitzungen`). */
  arbeitende_sitzungen?: number;
  arbeitsgang?: { seit: string; versuche: number; zuletzt?: string; fehler?: string; ruht?: string };
  berater_messfehler?: string;
  /** Ein Abschluss-Auftrag fuer diese Buehne wartet oder laeuft gerade im Worker
   *  (server: `ansichtMitMessung`). Fehlt bei alter Server-Fassung, dann wie `false`. */
  schliesst_gerade?: boolean;
  id: string; titel: string; thema: string; haus: 'dev' | 'partner'; raum: string;
  hauptstatus?: Hauptstatus; hauptstatus_text?: string; arbeitsinformation?: string;
  antwortbedarf_status?: { zustand: string; hatPersoenlichenBedarf: boolean; antwortEinzuarbeiten: boolean; darfAufMenschenWarten: boolean; bedarfe?: Array<{ status: string }> };
  vorlagepruefung?: {status:string; befund:string; am:string; beratungsMassstab?:number};
  richtung?: RichtungDokument;
  zielinventar?: ZielinventarDokument;
  beratungsfassungen?: Array<{id:string; am:string; anlass:string}>;
  saal_bereich?: 'arbeitet' | 'zuarbeit' | 'gestoert' | null;
  /** Schalter „beantwortet = eingefroren" fuer das Haus dieser Buehne (Server: `fuerAnsicht`,
   *  Rafael 03.10.2026). Nur wenn an: Kettenzeile ueber der Buehne, Straenge in der Historie.
   *  Fehlt bei alter Server-Fassung, dann wie `false`. */
  kette_an?: boolean;
  /** Wem die Buehne gehoert (BuehneSchema). Ein Partner-Eigentum steht nicht in Rafaels Hof — guide-partner-eigentum.ts. */
  eigentuemer?: string | null;
  /** Nur an einer MIT DIR GETEILTEN Buehne (Partner-Tor, Rafael 30.10.2026): wer sie geteilt hat. */
  geteilt_von?: { id: string; name: string; am?: string; shareId?: string };
  /** Server: `partnerBuehne` (Feld ODER Partner-Sitzung). Fehlt bei alter Server-Fassung. */
  partner_eigen?: boolean;
  /** Der GESETZTE Raum (gespeichert). `raum` ist der gerechnete: bei gemessener Arbeit
   *  Sitzungssaal, sonst gleich `raum_gesetzt`. Fehlt bei alter Server-Fassung. */
  raum_gesetzt?: string;
  /** Server `beraterStill` (HOFDREI, 16.10.2026): „In Arbeit", aber niemand arbeitet gemessen — die Stillstandsmarke.
   *  `laut` ab der Schwelle der Wache. Fehlt bei alter Server-Fassung, dann keine Marke. */
  berater_still?: { berater: string | null; seit: string | null; konto_aus: boolean; amt: boolean; laut: boolean; satz: string } | null;
  /** Server `ruhendesWartenAn` (HOFDREI-Zusatz): ehrliches Warten — `bis` ist der echte Prüftermin. Fehlt bei alter Fassung. */
  ruhend?: { grund: 'pruefen_ab' | 'zuarbeit_lebt'; bis: string | null; ende: string | null; wer: string } | null;
  /** Was die Wache gegen den Stillstand getan hat (Server `planeStillstand`): Anstupser, Umstellung, Meldung. */
  wache_still?: { seit: string; versuche: number; zuletzt?: string; gemeldet?: string; fehler?: string;
    umgestellt?: { am: string; sitzung: string; nach: string | null; fehler?: string } };
  braucht_entscheidung?: boolean;
  /** Vollständige Beratung vom Server belegt, keine technische Phasenvermutung. */
  beratung_bereit?: boolean;
  arbeitsplan?: { ziel: string; fertigWenn?: string; abschlussBeleg?: string; beratung?: { belegAm: string; zielabdeckung: string; einordnung: string; empfehlung: string }; meilenstein?: string; naechsterSchritt: string; zustaendig: string; phase: string;
    belegAm: string; rueckmeldungAm?: string; minuten?: { min: number; max: number };
    prioritaet?: 'frist' | 'entblocken' | 'ziel' | 'spaeter'; frist?: string; energie?: 'leicht' | 'mittel' | 'konzentriert'; nutzerAktion?: string;
    abhaengigkeiten?: Array<{ buehne?: string; sitzung?: string; wartetAuf: string; erledigt: boolean; beleg: string }> };
  /** Der lesbare Name, wenn `thema` eine Board-Kennung ist. Fehlt bei alter Server-Fassung. */
  thema_name?: string | null;
  /** Unterziel (Meilenstein-Kennung des Board-Themas), sein Name und der Zieltermin-Text des Boards. Fehlt bei alter Server-Fassung und bei Bestand. */
  unterziel?: string | null; unterziel_name?: string | null; unterziel_ziel?: string | null; unterziel_nicht_lesbar?: boolean;
  /**
   * DIESE BUEHNE DARF SCHWEIGEN — und braucht deshalb keinen Berater.
   *
   * `dauerprotokoll` (GUIDE.md 36) ist eine dauerhafte Ablageflaeche: nie fertig,
   * keine Frage, kein Arbeiter. `fertig` heisst: alles getan, es wartet nur noch
   * Rafaels Klick. In BEIDEN Faellen ist „kein Berater" der vereinbarte Zustand und
   * kein Mangel — die Karte muss das unterscheiden, sonst leuchtet eine Ablage rot.
   */
  stumm_erlaubt?: { von: string; am: string; grund: string; art: 'fertig' | 'dauerprotokoll' };
  /** Was zuletzt ZUR SACHE gesagt wurde (Server: `standDerSache`). Fehlt bei alter Fassung. */
  stand?: { text: string; am: string | null; art: 'nachricht' | 'keiner' };
  textblock: string; rezept: Layoutrezept | null; workspace?: string;
  /** Seit 11.09. echte Objekte statt Kartenkennungen — siehe `EntscheidungSchema`
   *  in server/lib/guide-buehnen.ts. Der `zustand` wird vom Server gerechnet. */
  entscheidungen: Entscheidung[];
  /** Leichte Vorschlaege — siehe `Vorschlag`. Fehlt bei alter Server-Fassung; dann
   *  wie eine leere Liste behandeln, nicht wie einen Fehler. */
  vorschlaege?: Vorschlag[];
  /** Rafaels freies Wort an diesen Berater, auch ohne dass eine Frage dastand. */
  nachrichten: Nachricht[];
  sitzungen: string[];
  /** Jede Frage beantwortet — sie war dran und darf weiterziehen. */
  dran_gewesen: boolean;
  /** Wie oft sie aus dem Vorrat zurueckgerufen wurde: die Irrtuemer des Assistenten. */
  ruecklaeufer: number;
  offen: { entscheidungen: number; unzugestellt: number; unverarbeitet: number; unzugestellte_nachrichten: number };
  /** `ruht` = zustaendig, aber gerade kein Prozess (Server: `belegung`, seit
   *  13.09.2026). NICHT als „unbesetzt" zeigen — „pausiert darf keinesfalls fehlend
   *  heissen" (Rafael 09:31:59Z / Codex). */
  belegung: 'besetzt' | 'ruht' | 'unbesetzt' | 'unbekannt';
  /**
   * WARUM HIER NICHTS GESCHIEHT — 'aufgelaufen' heisst: niemand arbeitet, obwohl noch
   * etwas anliegt; 'pausiert' heisst: bewusst weggelegt. Rafael, 13.09.2026 06:56:14Z:
   * "dort dann erkennbar sein muss ob eine session aktiv pausiert ist oder ob sie
   * aufgelaufen ist und nicht weiterarbiten kann - das ist ja was anderes".
   * Fehlt, wenn jemand arbeitet oder Rafael selbst am Zug ist — und bei alter Fassung.
   */
  stillstand?: { art: 'aufgelaufen' | 'pausiert' | 'blockiert'; seit: string; letzter_berater?: string; grund: string };
  /** Baustein 12: Rafaels Wort hängt — Server `haengendeAntwort`. Fehlt bei alter Fassung. */
  antwort_haengt?: AntwortHaengt | null;
  /** Historisches Feld aus alten Serverantworten; wird nicht als Nutzeraufgabe angezeigt. */
  handgriff?: string | null;
  /** Sein Wort, solange der Berater es nicht eingearbeitet hat (Server `handgriffUndWort`). Fehlt bei alter Fassung. */
  wort_offen_seit?: string | null;
  /** Einmalig gestempelt, wenn der Bühnenmodus für den zugeordneten Chat auf "aus"
   *  steht (server: `sichereChatBuehne`, `buehnenmodus-schalter.ts`) — die Bühne bleibt
   *  stehen, löst aber ab diesem Zeitpunkt keine Pflicht mehr aus. Wird nie zurückgesetzt. */
  buehnenmodus_ungepflegt_seit?: string;
  /**
   * WER AN DIESER BUEHNE SITZT — in der Reihenfolge von `sitzungen`, der letzte ist
   * der aktuelle. Rafael, 13.09.2026 07:38:41Z: "ich muesste hier genau sehen welche
   * session gerade arbeitet und so weiter ... jede buehne einen berater". Fehlt bei
   * alter Server-Fassung; dann zeigt die Karte weiter nur `belegung`.
   */
  berater?: BeraterZustand[];
  zugang: { entscheiden: boolean; ueberholt: boolean; grund: string };
  unantastbar: boolean;
  angelegt_am: string; angelegt_von: string;
  auftrag: { von: string; am: string; worum: string };
  verlauf: Array<{ am: string; von: string; was: string; grund: string }>;
  abschluss?: { art: 'erledigt' | 'abgeloest'; am: string; von: string; grund: string };
  nachfolger?: string;
  /** Eigene Ergebnisbuehnen zu Rafaels Wort an dieser Buehne, ohne Abloesung (Server `antwortBuehnen`,
   *  nur `GET /buehnen/:id`, Rafael 29.10.2026 10:15:11Z). Fehlt, wenn es keine gibt. */
  beantwortet_auf?: Array<{ id: string; titel: string; angelegt_am: string | null; wort_am: string }>;
  /** Bei einer 1:N-Abloesung (GUIDE.md Abschnitt 52): alle Nachfolger AUSSER dem
   *  primaeren `nachfolger`. */
  weitere_nachfolger?: string[];
  /** Woraus diese Buehne entstanden ist — die Gegenrichtung zu `nachfolger`.
   *  Mindestens einer bei Vereinigung (`vereinige()`), sonst nicht gesetzt.
   *  Siehe `BuehneSchema.vorgaenger` in server/lib/guide-buehnen.ts. */
  vorgaenger?: string[];
  bild?: { am: string; pfad: string } | { am: string; fehlt: string };
  /** Gespeicherte Aufnahme der letzten fachlichen Sichtprüfung. Ältere Aufnahmen
   *  bleiben im `verlauf` als „angesehen“-Einträge erhalten. */
  gesehen?: {
    am: string; von: string; bild: string; bytes: number; seite_am: string | null;
    aufnahme_am?: string; fassung?: string;
  };
  /** Rafael hat diese Buehne schon einmal vor sich gehabt (Punkt B, 21.09.2026).
   *  NICHT dasselbe wie `gesehen` darueber — das ist der Sichtbeleg des BERATERS mit
   *  Aufnahme. Setzt nur `POST /:id/vorgelegt` aus Rafaels eigener Flaeche; bewegt die
   *  Buehne nicht und holt sie nicht von selbst zurueck. */
  rafael_gesehen?: { am: string };
  /** Reihenfolge vor Rafael, vom Amt gesetzt (1 = zuerst); fehlt = FIFO dahinter. BUEH1, 11.10.2026. */
  vorlage_rang?: { rang: number; am: string; von: string; grund: string };
  vorgezogen?: { von: string; am: string; grund: string };
  /** Tragweite 0-3; Pflicht in den fertigen Raeumen, dort nie unter 2. */
  stufe?: number;
  /** Das Urteil beim Einreihen: warum diese Buehne neben den anderen im Vorrat steht. */
  unabhaengig_weil?: string;
}

/** Ein historisches Bühnenbild wird über den vorhandenen Guide-Proxy gelesen.
 *  Auf PARTNER liegt der zentrale Bestand nicht auf derselben Platte; ein direkter
 *  `/downloads`-Pfad würde dort deshalb trotz vorhandener Aufnahme 404 liefern. */
export function archivPfadUrl(pfad: string | null | undefined): string | null {
  if (!pfad || !/^buehnen-bilder\/[a-zA-Z0-9_-][a-zA-Z0-9_.-]*\.png$/.test(pfad)) return null;
  return `/api/guide/buehnen-bild?pfad=${encodeURIComponent(pfad)}`;
}

export function archivBildUrl(b: Pick<BuehneAnsicht, 'bild'>): string | null {
  return b.bild && 'pfad' in b.bild ? archivPfadUrl(b.bild.pfad) : null;
}

/** Was die Stufe fuer Rafael bedeutet — die Zahl allein sagt ihm nichts. Exportiert,
 *  weil GuidePanel.tsx (EntscheidungsBlock, der Klickpunkt selbst) dieselbe Erklaerung
 *  braucht: Rafael fragte, was Stufe 1/2 bedeuten, obwohl die Zahl an jeder Karte
 *  stand — nur eben ohne Wort dazu, und dort nicht einmal als Tooltip. */
export const STUFE_WORT: Record<number, string> = {
  0: 'Umsetzungsdetail — entscheidet der Berater',
  1: 'in deinem Sinn entschieden — Einspruch möglich',
  2: 'Schnellfrage',
  3: 'volle Entscheidung',
};

/**
 * DIE ANZEIGE PRUEFT DIE OFFENE FRAGE, NICHT DAS GESPEICHERTE FELD (Assistent Haus Dev
 * `b83312fc`, 12.09.2026, in Rafaels Sinn entschieden — Stufe 1).
 *
 * `b.stufe` bleibt am Objekt stehen, auch wenn die letzte offene Entscheidung beantwortet
 * oder zurueckgezogen wurde — und das MUSS so bleiben: `stufe` ist zugleich die
 * Voraussetzung, ueberhaupt eine neue Entscheidung an dieser Buehne stellen zu duerfen
 * (PATCH stufe vor POST entscheidungen, sonst 400 am Server). Ein Reset am Objekt wuerde
 * das erneute Fragen kaputt machen.
 *
 * Rafaels Klage war aber nie der Speicher, sondern die ANZEIGE: eine "Stufe 2"-Marke ohne
 * Frage dahinter ist genau das Rauschen, ueber das er sich wiederholt beschwert hat
 * ("warum gibt es berater im sitzungssaal die nicht arbeiten", "die frage zum linkedin
 * post ist leer"). Deshalb hier gefiltert, nicht am Server.
 */
/** Anzahl offener Angebote; Fragen und freiwillige Vorschläge werden getrennt beschriftet. */
export function fragenAnDich(b: BuehneAnsicht): number {
  return (b.offen?.entscheidungen ?? 0) + (b.vorschlaege ?? []).filter(istOffenerVorschlag).length;
}

export function vorlagenMarke(b: BuehneAnsicht): string {
  const fragen = b.offen?.entscheidungen ?? 0;
  const vorschlaege = (b.vorschlaege ?? []).filter(istOffenerVorschlag).length;
  return [fragen ? `${fragen} ${fragen === 1 ? 'Frage' : 'Fragen'} an dich` : '',
    vorschlaege ? `${vorschlaege} ${vorschlaege === 1 ? 'Vorschlag' : 'Vorschläge'}` : ''].filter(Boolean).join(' · ');
}

function stufeSichtbar(b: BuehneAnsicht): boolean {
  return b.stufe !== undefined && b.offen.entscheidungen > 0;
}

/*
 * „VOR MIR" STEHT FUER ZWEI RAEUME (Rafael, 23.09.2026): *„nimm Vor mir raus oder mach
 * so dass grafisch vor mir und vorraum in einem reiter landen - weil akteull hab ich die
 * trennung zwicshen vorruam und vor mir nie sinnvoll umgesetzt - nenn es einfach "vor
 * mir"“*. Reine Anzeige: der Server fuehrt `vor_dem_koenig` und `vorraum` weiter als
 * zwei Raeume mit ihren eigenen Pruefungen — nur das Etikett ist dasselbe.
 */
export const RAUM_WORT: Record<string, string> = {
  vor_dem_koenig: 'Für dich vorbereitet', vorraum: 'Für dich vorbereitet', warteraum: 'Für dich vorbereitet',
  sitzungssaal: 'In Arbeit', pausenraum: 'Von dir zurückgestellt', abgeschlossen: 'Abgeschlossen',
};
/** Die Belegung im Wortlaut — der Server-Wert `ruht` (zustaendig, kein Prozess) heisst auf der
 *  Flaeche seit 03.10.2026 „still – wacht bei deiner Nachricht auf“ (e-ruht-abschaffen-20261003). */
export function belegungWort(belegung: string): string {
  return belegung === 'ruht' ? 'still – wacht bei deiner Nachricht auf' : belegung;
}
/**
 * DIE STATUSZEILE DER OFFENEN BUEHNE ALS SATZ (Rafael, 03.10.2026 17:16: „Mach es auch mehr
 * selbsterklärend.. Bühne ist für einen Außenstehenden nicht plausibel"). Bis dahin stand dort
 * „<thema> · Sitzungssaal · besetzt · 1 Vorschlag/Vorschläge · alles beantwortet" — Raum- und
 * Belegungsnamen sind Hof-Innensprache. Jetzt: was von DIR erwartet wird, was der Berater tut,
 * worum es geht. Reine Ableitung aus denselben Feldern (`offen`, `vorschlaege`, `zustandsKlasse`);
 * die Innensprache steht weiter im Tooltip (`innen`), fuer wen sie etwas sagt.
 */
/** Use the server's current need projection before any legacy room hint. */
function persoenlicherStand(b: BuehneAnsicht): string | null {
  const bedarf = b.antwortbedarf_status;
  if (!bedarf) return null;
  const teile: string[] = [];
  if (bedarf.antwortEinzuarbeiten) teile.push('Deine Antwort liegt zur Einarbeitung vor');
  if (bedarf.darfAufMenschenWarten || bedarf.bedarfe?.some(x => x.status === 'offen'))
    teile.push('Persönliche Auswahl ist offen');
  if (bedarf.bedarfe?.some(x => x.status === 'fehler')
    || (!bedarf.bedarfe && bedarf.zustand === 'nacharbeit' && bedarf.hatPersoenlichenBedarf && !bedarf.antwortEinzuarbeiten))
    teile.push('Persönliche Auswahl muss noch bedienbar vorbereitet werden');
  if (bedarf.zustand === 'ungewiss') teile.push('Persönlicher Klärungsbedarf noch nicht vollständig geprüft');
  // Historical, already processed answers do not create another personal task.
  return teile.length ? teile.join(' · ') : null;
}

export function buehnenStatusSatz(b: BuehneAnsicht): { satz: string; innen: string } {
  const fragen = b.offen?.entscheidungen ?? 0;
  const vorschlaegeOffen = (b.vorschlaege ?? []).filter(istOffenerVorschlag).length;
  const anzahl = (n: number, eins: string, viele: string) => `${n} ${n === 1 ? eins : viele}`;
  /* Was von Rafael erwartet wird — im Sitzungssaal mit offener Frage: die Frage steht, aber die Arbeit läuft noch
   * (sein Wort wird eingearbeitet oder jemand arbeitet gemessen); die Reihe „Wartet auf dich" bekommt sie erst,
   * wenn niemand mehr arbeitet. Darum hier „sobald …", nicht „Wartet auf deine Antwort". */
  /* Ein noch nicht verarbeitetes Wort liegt beim Berater. Historische Aktionsfelder
   * erzeugen keine Nutzeraufgabe, auch wenn ein aelterer Server sie noch liefert. */
  const wortUhr = b.wort_offen_seit ? uhr(b.wort_offen_seit) : null;
  const persoenlich = persoenlicherStand(b);
  const fuerDich = b.raum === 'pausenraum' ? 'Bewusst auf später gelegt'
    : persoenlich ? persoenlich
    : wortUhr && fragen === 0 ? `Dein Wort von ${wortUhr} liegt beim Berater`
    : b.raum === 'sitzungssaal' && fragen > 0 ? `${anzahl(fragen, 'Frage', 'Fragen')} an dich offen${vorschlaegeOffen ? ', dazu ' + anzahl(vorschlaegeOffen, 'Vorschlag', 'Vorschläge') : ''} · aktuelle Vorbereitung noch nicht vollständig geprüft`
    : fragen > 0
    ? `Wartet auf deine Antwort: ${anzahl(fragen, 'Frage', 'Fragen')} offen${vorschlaegeOffen ? `, dazu ${anzahl(vorschlaegeOffen, 'Vorschlag', 'Vorschläge')}` : ''}`
    : vorschlaegeOffen > 0 ? `${anzahl(vorschlaegeOffen, 'Vorschlag', 'Vorschläge')} zum Ansehen`
    : b.raum === 'warteraum' && ergebnisDa(b) ? 'Beratung liegt vor — keine Antwortpflicht'
    : 'Keine persönliche Frage hinterlegt';
  /* Was der Berater tut — derselbe Satz wie rechts an der Zeile (`zustandRechts`), eine Quelle. */
  const rechts = zustandRechts(b).text;
  const berater = b.raum === 'pausenraum' ? 'Keine Weiterarbeit bis zur Wiederaufnahme'
    : wortUhr && b.raum === 'sitzungssaal' ? `Berater muss auf dein Wort von ${wortUhr} reagieren`
    : b.arbeitsmessung === 'unbekannt' ? 'Aktuelle Tätigkeit nicht bestätigt'
    : b.arbeitsmessung === 'arbeitet' ? 'Laufende Arbeit gemessen'
    : b.raum === 'warteraum' ? fragen > 0 ? 'Antwortabhängiger Teil wartet auf dich' : ergebnisDa(b) ? 'Richtung, Tiefe und Abschluss bestimmst du' : 'Nächster Schritt liegt beim Berater'
    : b.saal_bereich === 'zuarbeit' ? `Berater ${rechts ?? 'wartet auf Zuarbeit'}`
    : rechts ? `Berater: ${rechts}` : 'Berater ist gerade still';
  const innen = [`Raum: ${RAUM_WORT[b.raum] ?? b.raum}`, `Belegung: ${belegungWort(b.belegung)}`,
    b.entscheidungen.length ? `${b.offen.entscheidungen} von ${b.entscheidungen.length} Entscheidung(en) offen` : null,
    b.vorschlaege?.length ? `${anzahl(b.vorschlaege.length, 'Vorschlag', 'Vorschläge')} insgesamt` : null].filter(Boolean).join(' · ');
  return { satz: `${fuerDich} · ${berater} · Thema: ${b.thema}`, innen };
}

/** Der Untertitel je Raum — Rafaels eigene Worte aus dem Bauplan, gekuerzt. */
const RAUM_SATZ: Record<string, string> = {
  vor_dem_koenig: 'fertig, wartet auf deine Antwort',
  vorraum: 'der Vorrat: fertig und unabhängig',
  /*
   * FRUEHER: "arbeitet deine Antwort ein". Der Kanzler hat den Satz am 11.09.
   * widerlegt — b-partner-gespraechs-sichtbarkeit wartet auf den Nachtlauf und
   * arbeitet gar nichts ein. Das ist die REIHEN-Ueberschrift, sie gilt fuer alle;
   * was mit der EINZELNEN Buehne geschieht, sagt `b.zugang.grund`, und der kommt
   * vom Server aus ihrem Verlauf. Hier steht deshalb nur noch, was immer stimmt.
   */
  warteraum: 'eine Frage oder ein Vorschlag an dich ist offen',
  /* HOFDREI (Rafael 16.10.2026): alles ohne Frage und ohne Pause steht hier — auch ein stiller Berater, dann mit Marke. */
  sitzungssaal: 'hier arbeitet ein Berater oder wartet auf Zuarbeit; arbeitet gerade niemand, steht das an der Zeile',
  pausenraum: 'zurückgestellt, jederzeit einsehbar',
};

/**
 * FARBE BEDEUTET ZUSTAND, NIE DEKORATION (Bauplan, "Wie die Räume aussehen sollen").
 *
 * Gold ist reserviert für "du bist dran" — kein anderer Raum bekommt sie, sonst
 * verliert sie ihre Wirkung. Die Zuordnung ist bewusst eine feste Tabelle statt
 * pro-Buehne berechneter Farbe: das Thema faerbt nicht (siehe Regel im Bauplan).
 */
const RAUM_FARBE: Record<string, string> = {
  vor_dem_koenig: 'var(--tn-orange)',
  vorraum: 'var(--tn-green)',
  warteraum: 'var(--tn-purple-dim, var(--tnf-a78bfa))',
  sitzungssaal: 'var(--tn-cyan)',
  pausenraum: 'var(--tn-text-muted)',
};


function uhr(iso?: string | null): string | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? new Date(t).toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit' }) : null;
}

/** Wie lange schon — in Worten, ohne Nachkommastellen-Genauigkeit vorzutaeuschen. */
function dauer(iso?: string | null, jetzt = Date.now()): string | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  const min = Math.max(0, Math.round((jetzt - t) / 60000));
  if (min < 1) return 'gerade eben';
  if (min < 60) return `${min} min`;
  const std = Math.floor(min / 60);
  if (std < 24) return `${std} h`;
  const tage = Math.floor(std / 24);
  return tage === 1 ? '1 Tag' : `${tage} Tage`;
}

/** Zuerst der Vorlagerang des Amts, Buehnen ohne Rang dahinter; Gleichstand = 0 (dann FIFO). */
function nachVorlageRang(a: BuehneAnsicht, b: BuehneAnsicht): number {
  const ra = a.vorlage_rang?.rang ?? Infinity, rb = b.vorlage_rang?.rang ?? Infinity;
  return ra === rb ? 0 : ra < rb ? -1 : 1;
}

/**
 * SEIT WANN STEHT SIE IN DIESEM RAUM.
 *
 * Nicht `angelegt_am`: eine Buehne, die seit gestern existiert und seit zehn Minuten
 * vor dem Koenig steht, wartet seit zehn Minuten. Die Angabe kommt aus dem Verlauf —
 * dem einzigen Ort, an dem die Bewegung steht. Findet sich kein Raumeintrag, ist die
 * Buehne seit ihrer Anlage nicht bewegt worden; dann ist `angelegt_am` richtig.
 */
function seitImRaum(b: BuehneAnsicht): string {
  for (let i = b.verlauf.length - 1; i >= 0; i--) {
    const e = b.verlauf[i];
    if (e.was.startsWith('Raum ') && e.was.endsWith(`-> ${b.raum}`)) return e.am;
    if (e.was === `angelegt in ${b.raum}`) return e.am;
  }
  return b.angelegt_am;
}

/**
 * WANN ZULETZT ETWAS GESCHAH — der Schluessel, nach dem die Raeume sortieren.
 *
 * Rafael, 12.09. 04:51Z: *"Es gibt keine zeitliche Sortierung. Das muessen wir mal oben
 * auch noch reingeben, sodass ich sehe, welche Challenges neu sind, weil aktuell hier
 * ganz oben Sachen angezeigt werden, die eigentlich alle veraltet sind."*
 *
 * NICHT `seitImRaum`, obwohl danach frueher sortiert wurde: das misst die Zeit IM RAUM,
 * und das ist eine andere Frage. `b-betrieb-konten` stand am 12.09. 04:55Z seit 11,0 h
 * im Sitzungssaal und war damit die oberste Zeile — ihr Berater hatte aber Sekunden
 * zuvor noch gearbeitet. Lange im Raum heisst nicht alt. Gefragt ist, wann zuletzt
 * ueberhaupt etwas geschah; das ist der juengste der beiden Zeitpunkte.
 */
function letzteRegung(b: BuehneAnsicht): string {
  const raum = seitImRaum(b);
  const letzter = b.verlauf[b.verlauf.length - 1]?.am;
  return letzter && letzter > raum ? letzter : raum;
}

/**
 * WAS GERADE MIT IHR GESCHIEHT — der letzte Verlaufseintrag.
 *
 * Rafael, 11.09.: "Da formieren sich gerade im Warteraum drei neue. Die bekommen
 * gerade neue Themen oder die bauen gerade neue Buehnen." Der Warteraum ist keine
 * graue Liste, sondern der Ort, an dem man der Arbeit zusieht — also muss dort
 * stehen, WAS geschieht und SEIT WANN.
 *
 * BEWUSST KEINE FORTSCHRITTSANGABE (offene Frage der Skizze, hier entschieden):
 * es gibt nichts, woraus sich ein Prozentsatz oder eine Restzeit RECHNEN liesse —
 * eine Buehne kennt keine Schritte, nur Ereignisse. Eine erfundene Fortschrittsangabe
 * waere eine stille Behauptung ueber etwas, das niemand gemessen hat. Was es wirklich
 * gibt, ist der letzte Schritt mit seinem Grund und seinem Zeitpunkt; das steht da.
 */
function letzteBewegung(b: BuehneAnsicht): { was: string; grund: string; am: string } | null {
  const e = b.verlauf[b.verlauf.length - 1];
  return e ? { was: e.was, grund: e.grund, am: e.am } : null;
}

// ── Der Zeitstrahl ────────────────────────────────────────────────────

/**
 * WIE SICH DIE BERATER HEUTE FORMIERT HABEN.
 *
 * Gerechnet, nicht gefuehrt: die Kurve ist die Zahl der lebenden Buehnen ueber den
 * Tag, aus `angelegt_am` und `abschluss.am`; die Punkte sind Raumwechsel aus dem
 * `verlauf`. Beides steht am Objekt — niemand fuehrt dafuer Buch, und es gibt keine
 * zweite Quelle, die veralten koennte.
 *
 * WAS HIER NICHT STEHT: Rafaels Antworten. Die Skizze zeigt sie als zweite Spur, und
 * sie waeren die interessantere — aber sie haengen heute an Karten, und der
 * Kartenbestand ist seit 11.09. 07:4xZ archiviert. Eine leere Spur einzuzeichnen
 * hiesse behaupten, es habe keine Antworten gegeben; deshalb fehlt sie ganz, mit
 * diesem Satz als Begruendung. Sie gehoert nachgetragen, sobald Antworten am
 * Buehnen-Objekt haengen (Schritt 6, der Kreislauf).
 */
function Zeitstrahl({ buehnen }: { buehnen: BuehneAnsicht[] }) {
  const tagesbeginn = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d.getTime(); }, []);
  const jetzt = Date.now();

  const { punkte, marken, hoch, tief, stand, bewegungen } = useMemo(() => {
    const ereignisse: Array<{ t: number; d: number }> = [];
    let stand = 0;
    for (const b of buehnen) {
      const an = Date.parse(b.angelegt_am);
      const ab = b.abschluss ? Date.parse(b.abschluss.am) : NaN;
      if (Number.isFinite(an)) { if (an < tagesbeginn) stand += 1; else ereignisse.push({ t: an, d: +1 }); }
      if (Number.isFinite(ab)) { if (ab < tagesbeginn) stand -= 1; else ereignisse.push({ t: ab, d: -1 }); }
    }
    ereignisse.sort((a, b) => a.t - b.t);
    const verlauf: Array<{ t: number; n: number }> = [{ t: tagesbeginn, n: stand }];
    for (const e of ereignisse) { stand += e.d; verlauf.push({ t: e.t, n: stand }); }
    verlauf.push({ t: jetzt, n: stand });

    const wechsel = buehnen.flatMap(b => b.verlauf
      .filter(e => e.was.startsWith('Raum ') && Date.parse(e.am) >= tagesbeginn)
      .map(e => ({ t: Date.parse(e.am), titel: b.titel, was: e.was, grund: e.grund })))
      .filter(e => Number.isFinite(e.t))
      .sort((a, b) => a.t - b.t);

    const hoch = Math.max(...verlauf.map(v => v.n));
    const tief = Math.min(...verlauf.map(v => v.n));
    const spanne = Math.max(1, jetzt - tagesbeginn);
    // Eigenes Koordinatensystem, absichtlich schmal (480 breit): die Spalte ist
    // 440-860 px breit, also bleibt der Massstab nahe 1:1 und die Beschriftung
    // lesbar. Ein 980er Kasten war der erste Entwurf — im Bild schrumpfte die
    // Schrift auf die Haelfte und niemand konnte die Achse lesen.
    const x = (t: number) => 6 + ((t - tagesbeginn) / spanne) * 468;
    const y = (n: number) => hoch === tief ? 26 : 42 - ((n - tief) / (hoch - tief)) * 34;
    // Treppe statt Gerade: zwischen zwei Ereignissen aendert sich nichts, und eine
    // schraege Linie wuerde Zwischenwerte behaupten, die es nie gab.
    const punkte: string[] = [];
    verlauf.forEach((v, i) => {
      if (i > 0) punkte.push(`${x(v.t).toFixed(1)},${y(verlauf[i - 1].n).toFixed(1)}`);
      punkte.push(`${x(v.t).toFixed(1)},${y(v.n).toFixed(1)}`);
    });
    return { punkte: punkte.join(' '), marken: wechsel.map(w => ({ ...w, x: x(w.t) })), hoch, tief, stand, bewegungen: wechsel.length };
  }, [buehnen, tagesbeginn, jetzt]);

  const heute = new Date(tagesbeginn).toLocaleDateString('de-AT', { weekday: 'long', day: '2-digit', month: '2-digit' });

  return (
    <details name="guide-status" data-ai-id="guide-zeitstrahl" style={{ marginBottom: 8, padding: '4px 0', fontSize: 11, color: 'var(--tn-text-muted)' }}>
      <summary style={{ cursor: 'pointer' }}>
        Heute · {bewegungen} Wechsel
      </summary>
      <div style={{ padding: '6px 8px' }}>
      <ReihenTitel titel="Der Tag" satz={`${heute} — wie sich deine Berater formiert haben`} />
      {/*
        BESCHRIFTUNG IN HTML, KURVE IN SVG. Text im SVG skaliert mit dem Kasten mit und
        wird in einer schmalen Spalte unlesbar klein — genau das ist im ersten Bild
        passiert. Was Rafael LESEN soll, steht deshalb daneben, nicht darin.
      */}
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, color: 'var(--tn-text-muted)' }}>
        <span>{hoch === tief ? `${stand} Berater, unverändert` : `Höchststand heute: ${hoch}`}</span>
        <span>jetzt: {stand}</span>
      </div>
      <svg viewBox="0 0 480 52" width="100%" height="48" preserveAspectRatio="none" role="img"
        aria-label={`Zahl der lebenden Bühnen heute: zwischen ${tief} und ${hoch}, jetzt ${stand}; ${bewegungen} Raumwechsel`}>
        <line x1="6" y1="48" x2="474" y2="48" stroke="var(--tn-border)" strokeWidth="1" />
        <polyline points={punkte} fill="none" stroke="var(--tn-accent,#7aa2f7)" strokeWidth="2" />
        {marken.map((m, i) => (
          <circle key={i} cx={m.x.toFixed(1)} cy="48" r="3" fill="#e0af68">
            <title>{`${uhr(new Date(m.t).toISOString())} · ${m.titel}\n${m.was} — ${m.grund}`}</title>
          </circle>
        ))}
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, color: 'var(--tn-text-muted)', marginTop: -4 }}>
        <span>00:00</span>
        <span>{uhr(new Date(jetzt).toISOString())}</span>
      </div>
      <div style={{ fontSize: 10.5, color: 'var(--tn-text-muted)', marginTop: 3, lineHeight: 1.45 }}>
        {bewegungen === 0
          ? 'Heute hat sich am Hof noch nichts bewegt — kein Raumwechsel seit Mitternacht.'
          : `${bewegungen} Raumwechsel heute (die Punkte — fahr darüber) · gerechnet aus den Zeitstempeln der Bühnen, nicht geführt.`}

      </div>
      </div>
    </details>
  );
}

// ── Bausteine ─────────────────────────────────────────────────────────

/**
 * „WARTET AUF DICH" HEISST: WARTET AUF DICH. Drei Zustände, kein vierter (HOFDREI, Rafael 16.10.2026: *„es gibt
 * nur ‚wartet auf mich', ‚in Arbeit' oder ‚aktiv pausiert'"*). Der Server rechnet den Raum (`wirksamerRaum`): eine
 * Bühne ohne offene Frage und ohne Pause steht im Sitzungssaal — „In Arbeit", bei stillem Berater mit Marke.
 * Die frühere Reihe „Liegt beim Berater" (HOFRAUM2, 15.10.) gibt es nicht mehr.
 */
export function istAktiveVorlage(b: Pick<OberthemaBuehne, 'raum' | 'raum_gesetzt' | 'braucht_entscheidung' | 'offen'>): boolean {
  // Nur eine tatsächliche offene Frage/Zustimmung zählt; historische Räume sind kein Nutzerauftrag.
  return wartetRaum(b.raum) && brauchtDich(b);
}

export function istImSitzungssaal(b: {raum: string}): boolean { return b.raum === 'sitzungssaal'; }

function ReihenTitel({ titel, satz, farbe }: { titel: string; satz?: string; farbe?: string }) {
  return (
    <div className="guide-al-titel" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '.07em', color: 'var(--tn-text-muted)', margin: '0 0 7px', display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
      <span style={{ fontWeight: 700, color: farbe ?? 'inherit' }}>{titel}</span>
      {satz && <span style={{ textTransform: 'none', letterSpacing: 0, opacity: 0.75, fontSize: '0.9em' }}>— {satz}</span>}
    </div>
  );
}

/**
 * WAS FRUEHER NUR IM TOOLTIP STAND, GEHT JETZT AUCH PER TIPPEN (Arbeitsliste, 03.10.2026).
 *
 * Zustand im Wortlaut, Chat, Modell und Konto standen bis hier ausschliesslich im
 * `title` der Karte — am Tablet gibt es kein Hovern, die Auskunft war dort schlicht
 * nicht erreichbar. Der `title` bleibt (Maus), dazu kommt dieser kleine Knopf neben
 * der Karte: er klappt denselben Text unter der Karte auf. Neben, nicht in der Karte —
 * die Karte ist selbst ein <button>, ein Knopf im Knopf waere ungueltiges Markup.
 */
function InfoKnopf({ id, offen, onUmschalten, schwebend }: { id: string; offen: boolean; onUmschalten: () => void; schwebend?: boolean }) {
  return (
    <button
      type="button"
      className="guide-touch-knopf"
      data-ai-id={`guide-karte-info-${id}`}
      aria-expanded={offen}
      aria-controls={offen ? `guide-karte-info-text-${id}` : undefined}
      aria-label="Details zur Karte: Zustand, Chat, Modell, Konto"
      title="Details: Zustand im Wortlaut, Chat, Modell, Konto"
      onClick={e => { e.stopPropagation(); onUmschalten(); }}
      style={{
        flexShrink: 0, fontSize: 11, lineHeight: 1, padding: '4px 5px', borderRadius: 5, cursor: 'pointer',
        border: `1px solid ${offen ? 'var(--tn-accent,var(--tn-link))' : 'var(--tn-border)'}`,
        background: schwebend ? 'var(--tn-bg)' : 'transparent',
        color: offen ? 'var(--tn-text)' : 'var(--tn-text-muted)',
        ...(schwebend ? { position: 'absolute', top: 5, right: 5, zIndex: 1 } : {}),
      }}
    >ⓘ</button>
  );
}

function InfoText({ text, zeilen, id }: { text: string; zeilen?: InfoZeile[]; id?: string }) {
  return (
    <div id={id} className="guide-karte-meta" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontSize: 11, lineHeight: 1.45, color: 'var(--tn-text-muted)', padding: '6px 9px', margin: '2px 0 5px', borderLeft: '2px solid var(--tn-border)' }}>
      {text}
      {/* Inhalte, die die kompakte Karte/Zeile nicht mehr als eigene Zeile zeigt (Zweitpruefung 24.10.2026) — per Klick/Tap/Tastatur erreichbar, nicht nur per Hover. */}
      {zeilen?.map((z, i) => <div key={i} data-ai-id={z.ai} style={{ marginTop: 4 }}>{z.text}</div>)}
    </div>
  );
}

/**
 * DIE GEMEINSAMEN ATTRIBUTE JEDER KLICKSTELLE.
 *
 * `data-guide-aktion="buehne"` + `data-guide-ziel` ist der Vertrag, gegen den der
 * Hof-Test misst (hofstaat-buehne.contract.json, dom.klick_buehne) — und zugleich die
 * Weiche, ueber die auch Links in gelieferten HTML-Flaechen laufen. Eine gesperrte
 * Buehne traegt zusaetzlich `data-guide-zugang="gesperrt"`: sie ist SICHTBAR (Rafael
 * sieht, dass und woran gearbeitet wird) und oeffnet trotzdem nichts.
 */
function klickAttribute(b: BuehneAnsicht) {
  return {
    'data-guide-aktion': 'buehne',
    'data-guide-ziel': b.id,
    'data-guide-zugang': b.zugang.ueberholt ? 'ueberholt' : 'gueltig',
    'data-guide-entscheiden': b.zugang.entscheiden ? 'ja' : 'nein',
    'data-ai-id': `guide-buehne-${b.id}`,
  } as const;
}

/**
 * DER THEMENNAME, WIE IHN RAFAEL LESEN SOLL.
 *
 * `thema` traegt laut Bauplan die KENNUNG aus seinem Fokus-Board, nie den Namen (der
 * gehoert dem Board und wird dort umbenannt). Aufgeloest wird beim Anzeigen, vom
 * Server (`thema_name`). Faellt der Wert aus — alte Server-Fassung, Board nicht
 * lesbar, oder `thema` ist Freitext statt Kennung —, steht der Text selbst da, wie
 * bisher. Nur die nackte Kennung darf nicht stehenbleiben: am 12.09.2026 zeigte genau
 * eine Buehne "theme_1784090875096_z90npu" statt "Vertrieb & Marketing".
 */
function themaWort(b: BuehneAnsicht): string {
  return b.thema_name || b.thema;
}

/** Oberthema › Unterziel — die zwei oberen der drei Titel (die dritte Bezeichnung ist `titel`). Fehlt das Unterziel, steht das sichtbar da. */
function zielPfad(b: BuehneAnsicht): string {
  const unterziel = b.unterziel_name ?? (b.unterziel ? `${b.unterziel} (im gewählten Board-Thema nicht gefunden)` : 'ohne Unterziel');
  return `${themaWort(b)} › ${unterziel}`;
}

/**
 * DIE ZWEITE ZEILE EINER BUEHNE — was zuletzt ZUR SACHE gesagt wurde.
 *
 * Hier stand bis zum 12.09.2026 `letzteBewegung(b).was`, also der `verlauf`: "Raum
 * sitzungssaal -> warteraum", "stufe — -> 2", "Nachricht n-x zugestellt". Das ist die
 * Buchhaltung des Hofes und sagt Rafael ueber die SACHE nichts. Sein Befund dazu,
 * 14:12:00.719Z: *"Dann kommt auf keiner der unteren Buehnen nur Komma Sport, die
 * sollen eigentlich geaendert werden. Das ist ein Bug."*
 *
 * GERECHNET WIRD AM SERVER (`standDerSache`), nicht hier: sonst haette die naechste
 * Ansicht ihre eigene Vorstellung davon, was "der Stand" ist — und die erste, die ein
 * Feld vergisst, zeigt wieder Buchhaltung. Fehlt das Feld (alte Server-Fassung),
 * bleibt die Zeile leer statt falsch.
 *
 * Seit 28.09.2026 steht der Stand MEHRZEILIG (drei Zeilen) und ohne das vorangestellte
 * „Stand:" — das ist Formular, kein Inhalt.
 */
function standText(b: BuehneAnsicht): string | null {
  if (b.raum === 'vorraum' && b.unabhaengig_weil) return `unabhängig: ${b.unabhaengig_weil}`;
  /* Die weiteren Pflichtfelder des Stand-Blocks (Zustand, Zustaendig, Naechster
   * Schritt …) sind Hofbuchhaltung und stehen im Tooltip — an der Karte nur die Sache. */
  const t = b.stand?.text.split(/\n\s*(?:Zustand|Zuständig|Nächster Schritt|Weiter bei|Beleg):/)[0]
    .replace(/^\s*Stand:\s*/i, '').replace(/\n\s*Stand:\s*/g, ' — ').replace(/\s+/g, ' ').trim();
  return t || null;
}

/**
 * DER BERATER, DER GERADE AN DER BUEHNE SITZT — der LETZTE Eintrag, nicht der erste.
 *
 * `sitzungen` ist Historie (Abloesen stehen in Reihenfolge darin, tote bleiben
 * stehen). Wer den ersten naehme, zeigte auf jeder abgeloesten Buehne den Chat von
 * gestern. Gibt es gar keinen, ist das eine eigene Aussage — dann steht "kein
 * Berater" da und nicht ein leeres Feld.
 */
export function aktuellerBerater(b: BuehneAnsicht): BeraterZustand | null {
  if (b.berater_messfehler) return null;
  const liste = b.berater;
  if (!liste?.length) return null;
  /* Der lebende gewinnt gegen den zuletzt eingetragenen: nach einer Abloese steht
   * der neue Chat zwar hinten, aber wenn ausnahmsweise ein frueherer noch laeuft,
   * ist DER der Berater, an den eine Nachricht ginge (`beraterDerBuehne`). */
  /* Danach der ZUSTAENDIGE (offen, aber ohne Prozess) — er ist der, den eine Nachricht
   * erreicht. Ohne diese Stufe zeigte die Karte den zuletzt Eingetragenen, und das war
   * nach einer Abloese der BEENDETE Vorgaenger: „kein Berater", obwohl einer wartete. */
  return [...liste].reverse().find(z => z.lebt)
    ?? [...liste].reverse().find(z => z.offen)
    ?? liste[liste.length - 1];
}

/**
 * DIE TECHNIK DES BERATERS STEHT NUR NOCH IM TOOLTIP — Rafael, 28.09.2026, am Bild:
 * *„mich interessiert weder die Sessionnummer hier noch welches Modell das ist […] die
 * Prozent zum Beispiel interessieren mich schon nicht mehr"*, und auf die Frage nach
 * dem Konto: „auch Konto nur beim Drueberfahren". Bis dahin stand hier ein Abzeichen
 * (`BeraterMarke`: Chat-Kennung, Modell, Konto, Prozent, „blockiert") in jeder Zeile
 * und drueckte den Titel auf null Breite. Die Auskunft bleibt vollstaendig erhalten —
 * als Text beim Drueberfahren, mit Herkunft der Messung wie vorher.
 */
function beraterTitel(b: BuehneAnsicht): string | null {
  if (b.berater_messfehler) return `Beraterstatus unbekannt: ${b.berater_messfehler}`;
  const berater = aktuellerBerater(b);
  if (!berater) return null;
  const k = berater.kontingent;
  return [
    `Berater ${berater.sitzung}${berater.lebt ? '' : ' (kein Prozess)'}`,
    berater.titel ? `Chat: ${berater.titel}` : null,
    berater.modell ? `Modell: ${berater.modell}` : null,
    berater.konto ? `Konto: ${berater.konto}` : null,
    /* Die Herkunft steht IMMER dabei: "41 %" und "nicht gemessen" sind zwei
       verschiedene Auskuenfte, und die zweite darf nicht wie eine Zahl aussehen. */
    k ? (k.woche_prozent === null
      ? `Kontingent: nicht gemessen (${k.herkunft})`
      : `Kontingent: Woche ${k.woche_prozent} % · 5-h-Fenster ${k.fenster_prozent} % (${k.herkunft})`) : null,
    k?.grund,
    berater.arbeiter.length > 0 ? `${berater.arbeiter.length} offene Sub-Sitzung(en): ${berater.arbeiter.map(s => s.slice(0, 8)).join(', ')}` : null,
    berater.blockiert ? `blockiert: ${berater.blockiert}` : null,
    berater.taetigkeit ? `${berater.taetigkeit.art}: ${berater.taetigkeit.wort}` : 'Sitzungsliste kennt diesen Chat nicht mehr',
  ].filter(Boolean).join('\n');
}

/** Unteraufgaben gehören zur Bühne; Details öffnen keine zusätzliche Aufgabe für Rafael. */
export function UnteraufgabenDetails({ b }: { b: BuehneAnsicht }) {
  if (!b.unteraufgaben?.length) return null;
  return <details data-ai-id={`guide-unteraufgaben-${b.id}`} style={{ fontSize: 11, margin: '2px 8px 6px', color: 'var(--tn-text-muted)' }}>
    <summary style={{ cursor: 'pointer' }}>Prüfungen und Unteraufgaben ({b.unteraufgaben.length})</summary>
    <ul style={{ margin: '4px 0', paddingLeft: 18 }}>{b.unteraufgaben.map(z =>
      <li key={z.sitzung}>{z.titel || 'Unteraufgabe'} · {z.arbeitet ? 'in Arbeit' : z.zustand}</li>)}</ul>
  </details>;
}

/** SICHTBAR STATT STILL — Rafael, 17.09.2026: "ein Abschluss, der laeuft, muss
 * sichtbar laufen — und wenn er in einer Schlange steht, muss man das sehen."
 * Das ist eine Rueckmeldung auf SEINEN Klick, keine Technik — sie bleibt als Wort. */
/** Diese Buehne ist auf DIESEM Geraet angeheftet (lib/buehnenAnheften.ts) und bleibt im Hintergrund offen. */
function AnheftMarke({ b }: { b: BuehneAnsicht }) {
  const { user } = useAuth();
  const id = useAngeheftetId(user?.id ?? 'local');
  if (id !== b.id) return null;
  return (
    <span data-ai-id={`guide-angeheftet-${b.id}`}
      title="Angeheftet: diese Bühne bleibt auf diesem Gerät im Hintergrund geöffnet — Eingaben bleiben stehen, Fernsteuerung erreicht sie. Lösen über den Knopf an der Bühne."
      style={{ fontSize: 9.5, fontWeight: 600, color: 'var(--tn-orange, #e0af68)', flexShrink: 0, whiteSpace: 'nowrap' }}>
      📌 angeheftet
    </span>
  );
}

function SchliessMarke({ b }: { b: BuehneAnsicht }) {
  if (!b.schliesst_gerade) return null;
  return (
    <span data-ai-id={`guide-schliesst-${b.id}`}
      title="Abschluss steht im Auftrags-Worker — wartend oder gerade dabei. Die Schlange arbeitet seriell, das kann bis zu einer Minute dauern."
      style={{ fontSize: 9.5, fontWeight: 600, color: 'var(--tn-link)', flexShrink: 0, whiteSpace: 'nowrap' }}>
      wird geschlossen…
    </span>
  );
}

/**
 * DER ZUSTAND ALS FARBE, NICHT ALS TEXT — Rafael, 28.09.2026: *„könnte man das über
 * Farbcodierungen machen, wo man unten einfach eine Legende hingibt. […] Das braucht
 * man auch nicht überall hinzuschreiben und dann kriege ich den ganzen Platz darunter."*
 *
 * Fuenf Klassen, eine Legende (`ZustandsLegende`) fuer den ganzen Guide. Der Wortlaut
 * aus `zustandRechts` geht nicht verloren: er steht als erste Zeile im Tooltip.
 * Die Zuordnung folgt der Rangfolge von `zustandRechts` — eine Stoerung schlaegt
 * alles, „arbeitet" schlaegt das Warten; ausserhalb des Sitzungssaals ist ein lebender,
 * aber stiller Berater der Normalfall und bekommt deshalb Grau, nicht Gelb.
 */
/*
 * „RUHT“ GIBT ES NICHT MEHR (Rafael, 03.10.2026 07:32:49Z, e-ruht-abschaffen-20261003 a, und
 * 07:44:43Z, v-pausenraum-buehnen-nach-dem-bau a). Seine Frage dazu: *„was heißt ruht? … es gibt
 * nur ‚wartet auf mich‘ … oder ‚arbeitet im Sitzungssaal‘“*. Was bisher grau „ruht“ hiess, ist
 * jetzt zweierlei: eine Bühne, die Rafael selbst auf später gelegt hat (Pausenraum) —
 * „zurückgestellt von Rafael“ — und ein stiller Berater, der bei der nächsten Nachricht aufwacht.
 */
type ZustandsKlasse = 'arbeitet' | 'wartet' | 'gestoert' | 'kein' | 'zurueckgestellt' | 'still';
const ZUSTAND_FARBE: Record<ZustandsKlasse, string> = {
  arbeitet: 'var(--tn-green)', wartet: 'var(--tn-link)', gestoert: 'var(--tn-orange)', kein: 'var(--tn-rosa)', zurueckgestellt: 'var(--tn-dim)', still: 'var(--tn-dim)',
};
const ZUSTAND_LEGENDE: Array<[ZustandsKlasse, string]> = [
  ['arbeitet', 'arbeitet'], ['wartet', 'technisches Warten / Zuarbeit'], ['gestoert', 'gerade arbeitet niemand / Stand nicht messbar'],
  ['kein', 'kein Berater'], ['still', 'kurze Pause zwischen zwei Zügen'], ['zurueckgestellt', 'zurückgestellt von Rafael'],
];
/** Grau ist nie „ruht“: im Pausenraum hat Rafael zurückgestellt, sonst ist der Berater nur still. */
const grau = (b: BuehneAnsicht): ZustandsKlasse => b.raum === 'pausenraum' ? 'zurueckgestellt' : 'still';

/** Aeltere Server liefern noch keinen Bereich; nur belegtes Warten darf als Zuarbeit gelten. */
export function saalGruppe(b: BuehneAnsicht): 'arbeitet' | 'zuarbeit' | 'gestoert' {
  return b.saal_bereich ?? (b.arbeit_laeuft ? 'arbeitet' : b.ruhend ? 'zuarbeit' : 'gestoert');
}


export function SaalBereiche({ zeilen, zeigeZeilen }: {
  zeilen: BuehneAnsicht[]; zeigeZeilen: (zeilen: BuehneAnsicht[]) => ReactNode;
}) {
  const bereiche = [
    { id: 'arbeitet', titel: 'Arbeitet gerade', hinweis: 'Laufende Sitzungen und ihre Arbeit.' },
    { id: 'zuarbeit', titel: 'Wartet auf Zuarbeit', hinweis: 'Andere Sitzungen, Hintergrundtests oder Serverkapazität. Keine Antwort von dir nötig.' },
    { id: 'gestoert', titel: 'Braucht Klärung durch den Berater', hinweis: 'Kein laufender Arbeitsgang und keine bestätigte Zuarbeit. Die zuständige Sitzung muss weiterführen.' },
  ] as const;
  return <>{bereiche.map(g => {
    const liste = zeilen.filter(b => saalGruppe(b) === g.id);
    if (!liste.length) return null;
    return <div key={g.id} data-ai-id={`guide-saal-${g.id}`} style={{ margin: '8px 0 12px' }}>
      <div style={{ fontSize: 12, fontWeight: 650, color: g.id === 'gestoert' ? 'var(--tn-orange)' : 'var(--tn-text)' }}>{g.titel} · {liste.length}</div>
      <div style={{ fontSize: 11, color: 'var(--tn-text-muted)', margin: '2px 0 5px' }}>{g.hinweis}</div>
      {zeigeZeilen(liste)}
    </div>;
  })}</>;
}

export function zustandsKlasse(b: BuehneAnsicht): ZustandsKlasse {
  /* EINE QUELLE (Bauauftrag B, 21.10.2026): Raum und Saalbereich kommen aus `buehnenLage` am Server — dieselbe
   * Ableitung, die auch Zugprüfung, Stillstand und Wache lesen. Bis hierher rechnete diese Funktion mit sieben
   * weiteren Feldern nach (`arbeit_laeuft`, `arbeitsgang`, `stumm_erlaubt`, `berater.taetigkeit`, `blockiert`, …)
   * und kam dabei zu anderen Ergebnissen als die Reihe, in der die Karte stand (Rafael 20.10. 09:02Z: „da steht
   * berater arbeitet gerade aber die session steht nicht im sitzungssaal"). */
  if (b.raum === 'pausenraum') return 'zurueckgestellt';
  if (b.raum === 'warteraum') return 'wartet';
  /* Fail loud (Zweitprüfung 21.10.2026, B4): ein Messfehler ist kein stiller Berater — orange, nicht grau. */
  if (b.berater_messfehler) return 'gestoert';
  if (b.saal_bereich === 'zuarbeit') return 'wartet';
  if (b.saal_bereich === 'arbeitet') return 'arbeitet';
  /* Kein offener Berater eingetragen: niemand kann weiterführen — eigene Farbe (rosa), nicht „still". */
  if (b.berater_still && b.berater_still.berater === null) return 'kein';
  /* `gestoert` am Server heisst: niemand arbeitet gemessen. Unter der Wachschwelle ist das die normale Lücke
   * zwischen zwei Zügen (grau), darüber eine Störung (orange) — `berater_still.laut` trägt die Schwelle. */
  return b.berater_still?.laut ? 'gestoert' : 'still';
}

function ZustandsLegende() {
  return (
    <div data-ai-id="guide-zustand-legende"
      style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', fontSize: 10.5, color: 'var(--tn-text-muted)', margin: '2px 0 10px' }}>
      <span>Randfarbe der Karten:</span>
      {ZUSTAND_LEGENDE.map(([k, wort]) => (
        <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <i aria-hidden="true" style={{ width: 10, height: 10, borderRadius: 2, background: ZUSTAND_FARBE[k], display: 'inline-block' }} />
          {wort}
        </span>
      ))}
      <span style={{ opacity: 0.8 }}>· ⓘ neben der Karte (oder Maus darauf): Zustand im Wortlaut, Chat, Modell, Konto</span>
    </div>
  );
}

/** Drei Zeilen, dann Schluss — Rafael, 28.09.2026 („Ja, drei Zeilen"); der Rest steht in der Buehne. */
const ZEILEN_KLAMMER = (n: number): React.CSSProperties => ({
  display: '-webkit-box', WebkitLineClamp: n, WebkitBoxOrient: 'vertical', overflow: 'hidden', overflowWrap: 'anywhere',
});

/** Eine Nebenzeile an der Karte (Grund, Hinweis, Fehlermeldung) ist EINE Zeile — voll im Tooltip.
 *  Am 28.09. sprengte eine lange ENOENT-Meldung sonst die ganze Karte. */
const EINZEILER: React.CSSProperties = {
  fontSize: 10, marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
};

/**
 * WAS RECHTS AN DER ZEILE STEHT — im Sitzungssaal der LIVE-Zustand, sonst das Alter.
 *
 * Rafael, 13.09.2026 07:38:41Z: "die im sitzungsraum arbeiten wirklich ausser sie
 * haben probleme". Der Sitzungssaal ist der Raum, in dem gearbeitet wird; dort ist
 * "wie alt ist der letzte Eintrag" die falsche Auskunft. Bis heute stand dort
 * "ueberholt · 30 min" — beides zugleich falsch gelesen: `ueberholt` ist eine Aussage
 * ueber die GUELTIGKEIT des Standes (der Assistent tagt), keine ueber den Berater,
 * und die Minuten sahen aus wie Rueckstand, waehrend die Sitzung gesperrt war.
 *
 * In den anderen Raeumen bleibt es beim Alter: dort wird nicht gearbeitet, und der
 * Live-Zustand einer Sitzung sagt ueber eine fertige Buehne im Vorrat nichts.
 */
export function zustandRechts(b: BuehneAnsicht): { text: string | null; farbe?: string } {
  if (b.raum === 'pausenraum') return { text: 'pausiert' };
  if (b.arbeitsmessung === 'unbekannt') return { text: 'Aktuelle Tätigkeit nicht bestätigt', farbe: 'var(--tn-orange)' };
  if (b.arbeitsmessung === 'arbeitet') return { text: 'Laufende Arbeit gemessen', farbe: 'var(--tn-green)' };
  const persoenlich = persoenlicherStand(b);
  if (persoenlich) return { text: persoenlich };
  if (b.arbeitsmessung === 'nicht_arbeitend') return {
    text: b.saal_bereich === 'zuarbeit'
      ? (b.ruhend ? ruhendText(b.ruhend) : 'Keine eigene laufende Arbeit; Zuarbeit belegt')
      : 'Derzeit keine laufende Arbeit nachgewiesen',
  };
  if (b.raum === 'warteraum') return { text: b.stumm_erlaubt?.art === 'dauerprotokoll'
    ? 'Ablage · kein Berater nötig' : fragenAnDich(b) > 0 ? 'wartet auf deine Antwort' : ergebnisDa(b) ? 'Beratung liegt vor — keine Antwortpflicht' : 'Nächster Schritt liegt beim Berater' };
  /* Sein noch nicht eingearbeitetes Wort: der Berater ist dran — kein „wartet auf …“ aus einem älteren Stand. */
  if (b.wort_offen_seit) return { text: `muss auf dein Wort von ${uhr(b.wort_offen_seit) ?? '—'} reagieren`, farbe: 'var(--tn-orange)' };
  /* Ohne Messung behauptet die Zeile weder Arbeit noch Stille — und sie sagt laut, dass die Messung fehlt
   * (Zweitprüfung 21.10.2026, B4: „wird gerade gemessen" in Grau ließ einen dauerhaften Ausfall wie einen kurzen
   * Messvorgang aussehen). Der Grund steht im Tooltip (`beraterTitel`). */
  if (b.berater_messfehler) return { text: 'Beraterstand nicht messbar', farbe: 'var(--tn-orange)' };
  if (b.saal_bereich === 'zuarbeit') return { text: b.ruhend ? ruhendText(b.ruhend) : 'wartet auf Zuarbeit', farbe: 'var(--tn-link)' };
  if (b.saal_bereich === 'arbeitet') return { text: 'arbeitet', farbe: 'var(--tn-green)' };
  /* Sitzungssaal, aber niemand arbeitet gemessen: die Stillstandsmarke, in Rafaels Worten (`stillMarke`). Was die
   * Wache technisch getan hat (Anstupser, Umstellung), steht im ⓘ-Text und im Sitzungssaal, nicht an der Zeile. */
  return stillMarke(b) ?? { text: 'gerade arbeitet niemand', farbe: 'var(--tn-orange)' };
}

function titelZeile(b: BuehneAnsicht): string {
  const bew = letzteBewegung(b);
  return [b.titel, `${themaWort(b)} · ${RAUM_WORT[b.raum] ?? b.raum} · ${belegungWort(b.belegung)}`,
    b.arbeitsgang?.fehler ? b.arbeitsgang.fehler : null,
    b.stillstand ? `${b.stillstand.art} seit ${b.stillstand.seit}: ${b.stillstand.grund}` : null,
    b.antwort_haengt ? antwortHaengtLang(b.antwort_haengt) : null,
    stufeSichtbar(b) && b.stufe !== undefined ? `Stufe ${b.stufe}: ${STUFE_WORT[b.stufe] ?? 'unbekannt'}` : null,
    // Das Urteil des Assistenten gehoert sichtbar dorthin, wo es gilt: es ist die
    // Begruendung, warum diese Buehne neben den anderen im Vorrat stehen kann.
    b.unabhaengig_weil ? `unabhängig, weil: ${b.unabhaengig_weil}` : null,
    b.unantastbar ? 'steht in der ersten Reihe und wird nicht mehr umsortiert' : null,
    // Der GRUND der letzten Bewegung passt nicht in die Zeile, gehoert aber dazu:
    // "was zuletzt geschah" ohne "warum" ist die halbe Auskunft.
    bew ? `zuletzt ${uhr(bew.am)}: ${bew.was} — ${bew.grund}` : null,
    b.zugang.grund].filter(Boolean).join('\n');
}

/**
 * Wenn nichts mehr OFFEN ist, heisst das nicht "alle beantwortet" — ein
 * Zurueckziehen ist ein dritter Ausgang neben Antwort und Schweigen
 * (server/lib/guide-buehnen.ts, entscheidungsZustand). Eine zurueckgezogene
 * Frage als "beantwortet" anzuzeigen liess Rafael Erledigung sehen, wo nichts
 * entschieden wurde.
 */
function erledigtText(entscheidungen: BuehneAnsicht['entscheidungen']): string {
  const n = entscheidungen.length;
  const zurueckgezogen = entscheidungen.filter(e => e.zustand === 'zurueckgezogen').length;
  const umgezogen = entscheidungen.filter(e => e.zustand === 'umgezogen').length;
  const beantwortet = n - zurueckgezogen - umgezogen;
  /* Ein Umzug ist der vierte Ausgang (siehe `istOffeneFrage`) und steht deshalb auch
   * hier: "alle beantwortet" waere bei einer umgezogenen Frage schlicht falsch. */
  const teile = [
    beantwortet ? `${beantwortet} beantwortet` : null,
    zurueckgezogen ? `${zurueckgezogen} zurückgezogen` : null,
    umgezogen ? `${umgezogen} woanders weitergestellt` : null,
  ].filter(Boolean);
  if (teile.length === 1 && beantwortet === n) return `${n} Entscheidung(en), alle beantwortet`;
  return `${n} Entscheidung(en): ${teile.join(', ')}`;
}

/** Eine Karte der ersten Reihe — gross, weil hier die Frage steht, die ansteht. */
/* Keine ✉-Sendemarke mehr an Karte und Zeile (Rafael, 18.10.2026: der Sendestatus ist für ihn
 * „überhaupt keine info"). Ein laufender Abschluss verschiebt die Karte ohnehin sofort
 * (`verschobeneAbschluesse`), eine Antwort ebenso (`merkeEingaben`). */
function ErsteReiheKarte({ b, offen, onOeffnen, kompakt }: {
  b: BuehneAnsicht; offen: boolean; onOeffnen: (b: BuehneAnsicht) => void;
  /** Kompakte Karte fuer den begrenzten Warteraum-Block: ohne Standtext und Arbeitsplanzeile (beides steht im Tooltip bzw. unter ⓘ). */
  kompakt?: boolean;
}) {
  const [info, setInfo] = useState(false);
  const infoText = [zustandRechts(b).text, titelZeile(b), b.stand?.text, beraterTitel(b)].filter(Boolean).join('\n\n');
  /* KOMPAKTE KARTE: Arbeitsplan-Schritt, Minuten, „vorgezogen von …“ stehen unter ⓘ und im Tooltip statt als eigene Zeilen. */
  const zusatz: InfoZeile[] = kompakt ? kompaktInfoZeilen({ id: b.id, arbeitsplan: b.arbeitsplan ?? null, vorgezogen: b.vorgezogen ?? null }, { uhr, jetzt: Date.now() }) : [];
  return (
    <div style={{ position: 'relative', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
    <InfoKnopf id={b.id} offen={info} onUmschalten={() => setInfo(o => !o)} schwebend />
    <button
      type="button"
      className="guide-karte"
      {...klickAttribute(b)}
      title={kompakt ? [infoText, ...zusatz.map(x => x.text)].filter(Boolean).join('\n\n') : infoText}
      onClick={() => onOeffnen(b)}
      style={{
        flex: 1, textAlign: 'left', cursor: 'pointer', padding: kompakt ? '7px 10px' : '11px 12px', borderRadius: 9,
        background: offen ? 'var(--tn-accent,var(--tn-link))22' : 'var(--tn-bg)',
        border: `1.5px solid ${offen ? 'var(--tn-accent,var(--tn-link))' : fragenAnDich(b) > 0 ? RAUM_FARBE.vor_dem_koenig : RAUM_FARBE.warteraum}`,
        boxShadow: `inset 4px 0 0 0 ${ZUSTAND_FARBE[zustandsKlasse(b)]}`,
        color: 'var(--tn-text)', minWidth: 0,
      }}
    >
      <div className="guide-karte-kicker" style={{ fontSize: 10.5, color: 'var(--tn-text-muted)', textTransform: 'uppercase', letterSpacing: '.05em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 30 }}>
        {zielPfad(b)}
        {/* Dezente Herkunftsmarke: der Vorraum steht seit 23.09. mit unter „Vor mir“,
            der Server fuehrt ihn aber weiter als eigenen Raum (Vorrat, unabhaengig). */}
        {/* Die MARKE statt eines eigenen Raums (Rafael 29.09.2026: „Vor mir“ und Warteraum
            zusammengelegt). Wer eine Frage an Rafael hat, steht vorn und traegt sie sichtbar. */}
        {fragenAnDich(b) > 0 && (
          <span data-ai-id={`guide-marke-frage-${b.id}`} title={vorlagenMarke(b)}
            style={{ color: RAUM_FARBE.vor_dem_koenig, fontWeight: 700, marginLeft: 6 }}>· {vorlagenMarke(b)}</span>
        )}
      </div>
      <div className="guide-karte-titel" style={{ fontSize: kompakt ? 13 : 14, fontWeight: 600, margin: kompakt ? '3px 0 3px' : '5px 0 6px', lineHeight: 1.35, ...(kompakt ? ZEILEN_KLAMMER(2) : {}) }}>{b.titel}</div>
      {b.geteilt_von && <div data-ai-id={`guide-geteilt-von-${b.id}`} title={`Diese Bühne ist mit dir geteilt${b.geteilt_von.am ? ` (seit ${new Date(b.geteilt_von.am).toLocaleString('de-AT')})` : ''}. Dein Wort steht unter deinem Namen.`} style={{ fontSize: 11, color: 'var(--tn-blue)', marginBottom: 3 }}>Geteilt von {b.geteilt_von.name}</div>}
      {/* Seit 28.09.2026 dieselbe Karte wie in den Raeumen: der Inhalt statt Kennung,
          Modell und Konto (die stehen im Tooltip, `beraterTitel`). */}
      {standText(b) && !kompakt && (
        <div className="guide-karte-text" style={{ fontSize: 12, lineHeight: 1.4, marginBottom: 5, color: 'var(--tn-text-muted)', ...ZEILEN_KLAMMER(3) }}>{standText(b)}</div>
      )}
      <AnheftMarke b={b} /><SchliessMarke b={b} />
      <div className="guide-karte-meta" style={{ fontSize: 11, color: 'var(--tn-text-muted)' }}>
        {(b.offen?.entscheidungen ?? 0) === 0 && (b.vorschlaege ?? []).some(istOffenerVorschlag) ? 'Freiwillige Auswahl — kein Arbeitsstopp'
          : b.entscheidungen.length === 0 ? 'Ergebnis zum Lesen und Ansprechen'
          : b.offen.entscheidungen === 0 ? erledigtText(b.entscheidungen)
            : `${b.offen.entscheidungen} von ${b.entscheidungen.length} offen`}
        {/*
          DIE STUFE STEHT DA, WEIL SIE ERKLAERT, WARUM DAS HIER LIEGT. Eine
          Schnellfrage (2) und eine volle Entscheidung (3) verlangen Verschiedenes von
          Rafael; er soll das sehen, bevor er anfaengt zu lesen. Fehlt sie, steht
          nichts — eine erfundene Zahl waere ein Urteil, das niemand gefaellt hat.
          MIT WORT, NICHT NUR ZAHL — die blosse Zahl beantwortete Rafaels eigene Frage
          nicht ("was bedeutet Stufe 1?"), obwohl sie an jeder Karte stand.
        */}
        {stufeSichtbar(b) && b.stufe !== undefined && ` · Stufe ${b.stufe} (${STUFE_WORT[b.stufe] ?? 'unbekannt'})`}
        {/* Wie lange laesst er das schon liegen — relativ, weil das die Frage ist. */}
        {' · seit '}{dauer(seitImRaum(b))}
        {/*
          KEINE HOF-INNENMARKEN MEHR AN DER KARTE (Bauauftrag B, 21.10.2026). Bis hierher standen hier „unbesetzt",
          „Vorlage ausstehend (2/3)", „Fehler: 3 Anstupser ohne Vorlage", „aufgelaufen seit …" und „blockiert seit …"
          — aus drei verschiedenen Quellen (`belegung`, `arbeitsgang`, dem im Fünf-Minuten-Takt geschriebenen
          `stillstand`), die der Reihe widersprachen, in der die Karte stand: b-reflux-aufrecht-tagesstandard lag am
          16.10. 05:40:23Z auf Rafaels Klick im Pausenraum und trug 20 Sekunden später „aufgelaufen" (Rafael 05:41Z:
          „was genau heißt diee aufgelaufen - und hab ich das reflux thema nicht in den pausenraum geschoben?").
          Der Zustand steht in Rafaels Worten rechts an der Zeile (`zustandRechts`) und als Randfarbe (`zustandsKlasse`),
          beides aus derselben Server-Ableitung wie die Reihe.
        */}
      </div>
      {b.arbeitsplan && !kompakt && <div data-ai-id="guide-arbeitsplan" style={{ fontSize: 11, marginTop: 4, color: 'var(--tn-text-muted)' }}
        title={`${b.arbeitsplan.ziel} · ${b.arbeitsplan.zustaendig} · gemeldet: ${b.arbeitsplan.phase} · Beleg ${b.arbeitsplan.belegAm}`}>
        {b.arbeitsplan.naechsterSchritt}
        {b.arbeitsplan.minuten && ` · ca. ${b.arbeitsplan.minuten.min}–${b.arbeitsplan.minuten.max} Min.`}
        {b.arbeitsplan.rueckmeldungAm && Date.parse(b.arbeitsplan.rueckmeldungAm) < Date.now() &&
          <span style={{ color: 'var(--tn-orange)' }}> · Rückmeldung überfällig</span>}
      </div>}
      {/* Baustein 12 (a): laut AN DER BÜHNE, nicht nur im Verlauf — mit Grund und Reset-Zeit. */}
      {b.antwort_haengt && (
        <div data-ai-id={`guide-marke-antwort-haengt-${b.id}`} title={antwortHaengtLang(b.antwort_haengt)}
          style={{ fontSize: 11, fontWeight: 600, color: 'var(--tn-rosa)', marginTop: 3 }}>
          ⚠ {antwortHaengtKurz(b.antwort_haengt)} · seit {dauer(b.antwort_haengt.seit)}
        </div>
      )}
      {b.vorgezogen && !kompakt && (
        <div style={{ fontSize: 10.5, color: 'var(--tn-orange)', marginTop: 3 }}>
          vorgezogen von {b.vorgezogen.von} ({uhr(b.vorgezogen.am)}) — {b.vorgezogen.grund}
        </div>
      )}
    </button>
    {info && <InfoText id={`guide-karte-info-text-${b.id}`} text={infoText} zeilen={kompakt ? zusatz : undefined} />}
      <UnteraufgabenDetails b={b} />
    </div>
  );
}

/** Eine Zeile in einem der hinteren Raeume — knapp, aber mit allem, was ihn erklaert. */


/**
 * Der Pausenraum-Knopf an der Karte. Bewusst schmal und stumm gehalten: in der Liste
 * zaehlt die Zeile, nicht der Knopf. Den Zustand seines Auftrags zeigt daneben ohnehin
 * schon `guide-zeile-sendung-*`.
 */
function ZeileAufSpaeter({ b }: { b: BuehneAnsicht }) {
  const { enqueue, jobs, queue } = useGuideSend();
  const pending = jobs.find(j => j.stage.id === b.id && j.raum === 'pausenraum');
  return (
    <button
      type="button"
      className="guide-touch-knopf"
      data-ai-id={`guide-zeile-pausenraum-${b.id}`}
      title="Auf später: schiebt diese Bühne ausdrücklich in den Pausenraum"
      onClick={(e) => {
        e.stopPropagation();
        try {
          if (!pending) enqueue(b as any, undefined, undefined, false, 'pausenraum');
          else if (pending.state !== 'sending') queue?.retry(pending.id);
        } catch { /* Der Zustand steht in der Sendezeile darunter. */ }
      }}
      style={{ flexShrink: 0, fontSize: 10, lineHeight: 1, padding: '4px 5px', borderRadius: 5, cursor: 'pointer',
        border: '1px solid var(--tn-border)', background: 'transparent',
        color: pending?.state === 'blocked' ? 'var(--tn-rosa)' : 'var(--tn-text-muted)' }}
    >{pending ? '…' : 'Auf später'}</button>
  );
}

/**
 * „· N SITZUNGEN ARBEITEN" (Rafael 09.10.2026, b-guide-jede-sitzung-ein-platz-20261009, Baustein 4):
 * die Raumköpfe zählen Bühnen, an der Zeile steht, wie viele Sitzungen dahinter arbeiten — erst ab
 * zwei, denn einer ist der Berater selbst und steht schon als Randfarbe da.
 */
/**
 * „IN ARBEIT — BERATER STILL SEIT 14 H" (HOFDREI, Rafael 16.10.2026). Kein Zustand, eine Marke am Zustand „In Arbeit":
 * unter der Schwelle der Wache gedämpft (Zuggrenze), darüber orange, und rot, sobald die Wache aufgegeben und dem
 * Kanzler gemeldet hat. Was die Wache getan hat (Anstupser, Umstellung), steht dahinter.
 */
export function stillMarke(b: Pick<BuehneAnsicht, 'berater_still' | 'wache_still'>, jetzt = Date.now()): { text: string; farbe: string } | null {
  const st = b.berater_still;
  if (!st) return null;
  const w = b.wache_still;
  /* Kein offener Berater eingetragen (Server: `berater: null`): das ist kein stiller Berater, sondern keiner — niemand
   * kann den Auftrag weiterführen. Eigener Wortlaut und eigene Farbe (Zweitprüfung 21.10.2026, B4). */
  if (st.berater === null) return { text: 'kein Berater · niemand kann weiterführen', farbe: 'var(--tn-rosa)' };
  const d = st.seit ? dauer(st.seit, jetzt) : null;
  /* Unter der Schwelle ist Stille die normale Lücke zwischen zwei Zügen — kein Alarm, nur die Zeit. */
  if (!st.laut) return { text: d ? `still seit ${d}` : 'still', farbe: 'var(--tn-text-muted)' };
  /* Rafaels Lesart (16.10.2026): „In Arbeit" heisst, jemand arbeitet. Tut gerade niemand, steht genau das da —
   * ohne „aufgelaufen", „Anstupser 2/3" oder „umgestellt auf …" (Rafael 16.10. 05:41Z: „was genau heißt diee
   * aufgelaufen"; 18.10.: Technik- und Zustellstatus gehören in den Sitzungssaal, nicht in seine Ansicht). */
  const teile = [d ? `gerade arbeitet niemand · seit ${d}` : 'gerade arbeitet niemand'];
  if (st.konto_aus) teile.push('KI-Zugang gerade nicht verfügbar');
  if (w?.gemeldet) teile.push('Kanzler ist informiert');
  return { text: teile.join(' · '), farbe: w?.gemeldet || w?.fehler ? 'var(--tn-rosa)' : 'var(--tn-orange)' };
}

/** Zählung im Kopf von „In Arbeit": wie viele davon gemessen still stehen (über der Schwelle). */
export function stillZusatz(zeilen: Array<Pick<BuehneAnsicht, 'berater_still'>>): string {
  const n = zeilen.filter(b => b.berater_still?.laut).length;
  return n ? ` · ${n} davon still` : '';
}

/** „wartet auf extern:… bis 16.10. 20:15Z" — `bis` ist der echte Termin; ohne Termin „solange sie arbeitet". */
export function ruhendText(r: { grund: string; bis: string | null; wer: string }): string {
  const wer = r.wer.startsWith('session:') ? `Sitzung ${r.wer.slice(8, 16)}` : r.wer.replace(/^extern:/, '');
  if (!r.bis) return `wartet auf ${wer} · solange sie arbeitet`;
  const d = new Date(r.bis);
  const zwei = (n: number) => String(n).padStart(2, '0');
  return `wartet auf ${wer} · bis ${zwei(d.getUTCDate())}.${zwei(d.getUTCMonth() + 1)}. ${zwei(d.getUTCHours())}:${zwei(d.getUTCMinutes())}Z`;
}

export function arbeitendeSatz(b: Pick<BuehneAnsicht, 'raum' | 'arbeitende_sitzungen'>): string | null {
  const n = b.arbeitende_sitzungen ?? 0;
  return b.raum === 'sitzungssaal' && n >= 2 ? `${n} Sitzungen arbeiten` : null;
}

function RaumZeile({ b, offen, onOeffnen, checkbox, ruhig, marken, kompakt }: {
  b: BuehneAnsicht; offen: boolean; onOeffnen: (b: BuehneAnsicht) => void;
  /** Ruhigere Zeile fuer die Ansicht „Nach Oberthema“: Oberthema und Unterziel stehen dort schon darueber, der Titel ist groesser, der Stand hat eine Zeile. Verhalten und Kennungen sind gleich. */
  ruhig?: boolean;
  /** NUR im Sitzungssaal und nur fuer Buehnen ohne offene Entscheidung gesetzt
   *  (siehe `StapelLeiste`) — Rafaels Mehrfachauswahl fuer den Stapel-Abschluss. */
  checkbox?: { ausgewaehlt: boolean; onToggle: () => void };
  /** Nur mit `ruhig`: die Marken der Zeile. Sie stehen rechtsbuendig in einem eigenen Block links von der festen Spalte fuer Knoepfe und Alter. */
  marken?: ReactNode;
  /** Nur mit `ruhig`: EINE Zeile (ca. 32 px): Titel mit Ellipsis; Stand, Stufe und Alter stehen im Tooltip bzw. unter ⓘ. */
  kompakt?: boolean;
}) {
  /*
   * DER STAND STEHT IN JEDEM RAUM, nicht nur in zweien. Bis zum 12.09.2026 wurde die
   * zweite Zeile nur fuer Warteraum und Sitzungssaal gebaut — im Pausenraum stand
   * deshalb nur das Thema, egal was an der Buehne los war. Was hier steht, ist jetzt
   * die SACHE (siehe `standText`), und die interessiert in jedem Raum.
   */
  const stand = standText(b);
  const klasse = zustandsKlasse(b);
  const zustandWort = zustandRechts(b).text;
  /*
   * DER GRUND DER RAUMWACHE NUR, WENN ER NOCH GILT: bis 15.09. stand hier der letzte
   * Wacheneintrag, egal wohin die Buehne danach gezogen war — „Arbeit wieder
   * aufgenommen" unter einer Karte im Pausenraum (Rafaels Bild, 15.09.). Ein Grund
   * erklaert den Raum, in den er gefuehrt hat; steht die Buehne woanders, ist er
   * Geschichte und steht im Verlauf, nicht an der Karte.
   */
  const ruheraum = b.raum_gesetzt ?? b.raum;
  const raumgrund = [...b.verlauf].reverse().find(v => v.von === 'assistent (Raumwache)' && v.was.endsWith(`-> ${ruheraum}`));
  /* Gerechneter Raum ≠ gesetzter Raum: die Karte steht im Sitzungssaal, weil gemessen
   * gearbeitet wird, und faellt danach in den gesetzten Raum zurueck (`wirksamerRaum`). */
  const gehoben = b.raum_gesetzt !== undefined && b.raum_gesetzt !== b.raum;
  /*
   * AUSGEGRAUT HEISST "ABGESCHICKT", NICHT "WEG" — Rafael, 14.09.2026:
   * „jede Buehne, sobald sie abgeschickt ist … muss das irgendwie zumindest
   * ausgegraut sein. Aber ich will eine Buehne nur mehr als historisch sehen, wenn
   * ich sie einmal [abgenommen] habe."
   *
   * `stumm_erlaubt.art === 'fertig'` ist im Datenmodell genau dieser Zustand: die
   * Arbeit ist inhaltlich fertig und wartet NUR noch auf Rafaels Erledigt-Klick, den
   * ihm niemand abnehmen kann (`art:'erledigt'` ist 403 fuer jeden ausser ihm, siehe
   * guide-buehnen.ts). Sie bleibt deshalb in ihrem Raum stehen und verschwindet erst
   * mit seinem Urteil ins Archiv — gedaempft, damit sie die laufende Arbeit nicht
   * ueberdeckt, aber sichtbar, damit er sieht, dass gearbeitet wurde.
   *
   * `dauerprotokoll` ist ausdruecklich NICHT gemeint: eine dauerhafte Ablage ist nie
   * "abgeschickt" und wartet auf gar nichts.
   *
   * "reduzierter Kontrast" (Warteraum) / "leise" (Pausenraum) — Bauplan, Farbtabelle.
   */
  const abgeschickt = b.stumm_erlaubt?.art === 'fertig';
  /* In der Ansicht „Nach Oberthema“ (ruhig) wird der Warteraum NICHT gedaempft: dort steht, was dich braucht, und soll am deutlichsten sein. */
  const gedaempft = (b.raum === 'warteraum' && !ruhig) || b.raum === 'pausenraum' || abgeschickt;
  /*
   * BUEHNENMODUS AUS — DIE MARKIERUNG MUSS AUF DER FLAECHE ANKOMMEN (Kanzler mit
   * Rafaels erteilter voller Autonomie, 17.09.2026: "Eine stehengebliebene Buehne,
   * die aussieht wie eine lebende, ist eine Luege auf Rafaels Flaeche"). Das Feld
   * wird serverseitig genau EINMAL gestempelt (sichereChatBuehne, hof-buehnenpflicht.ts)
   * und nie wieder geloescht — dieselbe optische Sprache wie `zugang.ueberholt`
   * (gestrichelter Rand, gedaempfte Farbe), damit "wird nicht mehr durchgesetzt"
   * ueberall gleich aussieht, egal aus welchem Grund.
   */
  const ungepflegt = !!b.buehnenmodus_ungepflegt_seit;
  const [info, setInfo] = useState(false);
  const infoText = [zustandWort, b.berater_still?.satz, b.wache_still?.fehler, titelZeile(b), gehoben ? `danach: ${ruheraum === 'pausenraum' ? 'zurückgestellt von Rafael' : 'wartet'}` : null, b.stand?.text, beraterTitel(b)].filter(Boolean).join('\n\n');
  /* KOMPAKTE ZEILE: was nicht mehr als eigene Zeile dasteht, steht unter ⓘ (Klick/Tap/Tastatur) und im Tooltip. `infoText` enthaelt den Stand schon. */
  const standExtra = standText(b);
  const grundInfo = kompakt ? statusGrund(b) : null;
  const zuordnung = kompakt ? zuordnungsGrund(b) : null;
  const zusatz: InfoZeile[] = kompakt ? [...(zuordnung ? [{ ai: `guide-zuordnung-${b.id}`, text: `Unter „Divers“, weil: ${zuordnung}` }] : []), ...(grundInfo ? [{ ai: `guide-grund-voll-${b.id}`, text: `${grundInfo.voll} (${grundInfo.hinweis})` }] : []), ...kompaktInfoZeilen({
    id: b.id, stand: standExtra && standExtra !== b.stand?.text ? standExtra : null, arbeitsplan: b.arbeitsplan ?? null, vorgezogen: b.vorgezogen ?? null,
    ungepflegtSeit: ungepflegt ? (b.buehnenmodus_ungepflegt_seit ?? null) : null, raumgrund: !gehoben && raumgrund ? { am: raumgrund.am, grund: raumgrund.grund } : null,
    arbeitende: arbeitendeSatz(b), stufe: stufeSichtbar(b) ? (b.stufe ?? null) : null, alter: dauer(letzteRegung(b)),
  }, { uhr, jetzt: Date.now() })] : [];
  const zeile = (
    <button
      type="button"
      className="guide-karte"
      {...klickAttribute(b)}
      title={kompakt ? [infoText, ...zusatz.map(x => x.text)].filter(Boolean).join('\n\n') : infoText}
      onClick={() => onOeffnen(b)}
      style={{
        display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
        padding: kompakt ? '2px 8px 2px 10px' : ruhig ? '8px 10px 8px 12px' : '6px 8px 6px 9px', borderRadius: 6, marginBottom: checkbox || ruhig ? 0 : 3, minWidth: 0,
        background: offen ? 'var(--tn-accent,var(--tn-link))22' : 'transparent',
        border: `1px solid ${offen ? 'var(--tn-accent,var(--tn-link))' : ruhig ? 'transparent' : 'var(--tn-border)'}`,
        borderStyle: b.zugang.ueberholt || ungepflegt ? 'dashed' : 'solid',
        /* Die ZUSTANDSFARBE als Randstreifen (28.09.2026) — vorher stand hier die
           Raumfarbe, die der umgebende Kasten ohnehin schon traegt. Bleibt auch bei
           gestricheltem Rand kraeftig. */
        boxShadow: `inset 4px 0 0 0 ${ZUSTAND_FARBE[klasse]}`,
        opacity: gedaempft && !offen ? 0.72 : 1,
        color: b.zugang.ueberholt || ungepflegt ? 'var(--tn-text-muted)' : 'var(--tn-text)',
      }}
    >
      <div style={{ display: 'flex', gap: 5, alignItems: 'baseline', minWidth: 0 }}>
        {ungepflegt && (
          <span aria-hidden="true" title={`Bühnenmodus ist für diesen Chat aus (seit ${uhr(b.buehnenmodus_ungepflegt_seit!)}) — wird nicht mehr gepflegt, verschwindet aber nicht von selbst`} style={{ color: 'var(--tn-text-muted)', fontSize: 10 }}>⏻</span>
        )}
        {b.raum === 'warteraum' && (
          <span aria-hidden="true" title="Warteraum: einsehbar, aber hier nicht entscheidbar" style={{ color: RAUM_FARBE.warteraum, fontSize: 10 }}>🔒</span>
        )}
        {/* DER TITEL DARF UMBRECHEN (zwei Zeilen) — bis 28.09. teilte er sich eine Zeile
            mit Kennung, Modell, Konto und Zustand und war dabei oft null Zeichen breit. */}
        <span className="guide-karte-titel" style={{ fontSize: kompakt ? 13 : ruhig ? 14 : 13, fontWeight: 600, lineHeight: 1.3, flex: 1, minWidth: 0, ...ZEILEN_KLAMMER(2) }}>{b.titel}</span>
      <AnheftMarke b={b} /><SchliessMarke b={b} />
      </div>
      {b.geteilt_von && <div data-ai-id={`guide-geteilt-von-${b.id}`} title={`Diese Bühne ist mit dir geteilt${b.geteilt_von.am ? ` (seit ${new Date(b.geteilt_von.am).toLocaleString('de-AT')})` : ''}. Dein Wort steht unter deinem Namen.`} style={{ fontSize: 11, color: 'var(--tn-blue)', marginBottom: 3 }}>Geteilt von {b.geteilt_von.name}</div>}
      {/* DER INHALT DER SITZUNG, bis zu drei Zeilen — Rafael, 28.09.2026: „Dafür nehmen
          wir, dass da eine Beschreibung oder zumindest dieses Gesamtding der Konversation
          reinpasst." Gerechnet wird am Server (`standDerSache`), siehe `standText`. */}
      {grundInfo && grundInfo.zeile && (
        <div className="ob-zeilen-grund" data-ai-id={`guide-grund-${b.id}`} title={grundInfo.voll} style={{ fontSize: 11, lineHeight: 1.35, marginTop: 1, color: 'var(--tn-text-muted)', ...ZEILEN_KLAMMER(2) }}>{grundInfo.zeile}</div>
      )}
      {stand && !kompakt && (
        <div className="guide-karte-text" style={{ fontSize: 12, lineHeight: 1.4, marginTop: 2, color: 'var(--tn-text-muted)', ...ZEILEN_KLAMMER(ruhig ? 1 : 3) }}>
          {stand}
        </div>
      )}
      {!kompakt && <div className="guide-karte-meta" style={{ display: 'flex', gap: 6, fontSize: 10.5, marginTop: 3, color: 'var(--tn-text-muted)', opacity: 0.85, minWidth: 0 }}>
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {ruhig ? (stufeSichtbar(b) ? `Stufe ${b.stufe}` : null) : <>{zielPfad(b)}{stufeSichtbar(b) && ` · Stufe ${b.stufe}`}</>}
          {arbeitendeSatz(b) && <span data-ai-id={`guide-arbeitende-${b.id}`} style={{ color: 'var(--tn-green)' }}>{ruhig && !stufeSichtbar(b) ? '' : ' · '}{arbeitendeSatz(b)}</span>}
        </span>
        {/* KEIN ZWEITER RAUMNAME AUF DER KARTE (Rafael, 29.09.2026 ~08:21Z, am Bild:
            „da steht die bridge is in the warteraum aber eigenltich ist sie im
            sitzungsaal"). Bis dahin stand hier „↩ Warteraum" fuer den Rueckfallraum —
            gelesen wurde es als Ort. Wohin sie danach geht, steht nur im Tooltip. */}
        {/* RELATIV, NICHT WANDUHR: "11 h" beantwortet "ist das neu?", "17:42" nicht. */}
        {!ruhig && <span style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>{dauer(letzteRegung(b)) ?? ''}</span>}
      </div>}
      {ungepflegt && !kompakt && (
        <div style={{ ...EINZEILER, color: 'var(--tn-text-muted)' }}>
          ⏻ Bühnenmodus aus seit {uhr(b.buehnenmodus_ungepflegt_seit!)} — keine Pflicht mehr, bleibt aber stehen
        </div>
      )}
      {!gehoben && raumgrund && !kompakt && (
        <div title={raumgrund.grund} style={{ ...EINZEILER, color: 'var(--tn-text-muted)' }}>
          Assistent · {uhr(raumgrund.am)}: {raumgrund.grund}
        </div>
      )}
    </button>
  );
  /*
   * DERSELBE KNOPF AUCH HIER (Rafael, 21.09.2026: *"praktisch auch im normalen Guide,
   * damit ich wirklich auch händisch in den Pausenraum verschieben kann"*). Er steht
   * NEBEN der Karte, nicht darin: die Karte ist selbst ein <button>, und ein Knopf im
   * Knopf waere ungueltiges Markup — der Klick auf die Karte oeffnet weiterhin die
   * Buehne, unveraendert.
   *
   * Nur fuer offene Buehnen und nicht fuer die, die schon dort liegen: ein Knopf, der
   * nichts mehr tun kann, ist ein Angebot, das sich als Arbeit ausgibt.
   */
  const knoepfe = (
    <>
      {!(b.abschluss || (b.raum_gesetzt ?? b.raum) === 'pausenraum') && <ZeileAufSpaeter b={b} />}
      <InfoKnopf id={b.id} offen={info} onUmschalten={() => setInfo(o => !o)} />
    </>
  );
  /* RUHIGE ZEILE (Ansicht „Nach Oberthema“): drei feste Bereiche — Karte, Marken-Block, rechts eine Spalte fester Breite fuer Knoepfe und Alter. Die Spalte ruht an derselben Stelle, egal wie breit die Marken sind. */
  const mitSpaeter = (inhalt: React.ReactNode) => ruhig ? (
    <div style={{ minWidth: 0 }}>
      <div className={`ob-raster${kompakt ? ' ob-raster--kompakt' : ''}`}>
        <div className="ob-raster-karte">{inhalt}</div>
        {marken && <div className="ob-raster-marken">{marken}</div>}
        <div className="ob-raster-rechts">
          <div className="ob-raster-knoepfe">{knoepfe}</div>
          {!kompakt && <span className="ob-raster-alter" data-ai-id={`guide-alter-${b.id}`}>{dauer(letzteRegung(b)) ?? ''}</span>}
        </div>
      </div>
      {info && <InfoText id={`guide-karte-info-text-${b.id}`} text={infoText} zeilen={kompakt ? zusatz : undefined} />}
      <UnteraufgabenDetails b={b} />
    </div>
  ) : (
    <div style={{ minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4, minWidth: 0 }}>
        <div style={{ flex: 1, minWidth: 0 }}>{inhalt}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flexShrink: 0 }}>
          {!(b.abschluss || (b.raum_gesetzt ?? b.raum) === 'pausenraum') && <ZeileAufSpaeter b={b} />}
          <InfoKnopf id={b.id} offen={info} onUmschalten={() => setInfo(o => !o)} />
        </div>
      </div>
      {info && <InfoText text={infoText} />}
      <UnteraufgabenDetails b={b} />
    </div>
  );
  if (!checkbox) return mitSpaeter(zeile);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 5, marginBottom: 3 }}>
      <label className="guide-touch-check" style={{ flexShrink: 0, cursor: 'pointer', paddingTop: 7 }}>
        <input
          type="checkbox"
          checked={checkbox.ausgewaehlt}
          onChange={checkbox.onToggle}
          data-ai-id={`guide-stapel-check-${b.id}`}
          title="fuer den Stapel-Abschluss auswaehlen"
          aria-label={`${b.titel} fuer den Stapel-Abschluss auswaehlen`}
          style={{ margin: 0, cursor: 'pointer' }}
        />
      </label>
      <div style={{ flex: 1, minWidth: 0 }}>{mitSpaeter(zeile)}</div>
    </div>
  );
}

/**
 * DAS FELD FUER DEN STAPEL-ABSCHLUSS — nur der Sitzungssaal bekommt sie.
 *
 * `art:'erledigt'` setzt serverseitig Rafael selbst — per Anmeldung/Klick oder,
 * seit 16.09.2026, durch eine Sitzung auf sein Chat-Wort hin mit `beleg` (Wortlaut +
 * Sitzung, gegen den Eingabestrom geprueft; guide-buehnen.ts `pruefeAbgang`) —
 * diese Leiste aendert daran nichts. Sie buendelt nur SEINEN einen Klick zu mehreren
 * Aufrufen von `POST /:id/abschluss`, genau denen, die er bisher einzeln haette
 * ausloesen muessen. `auswaehlbar` laesst nur Buehnen ohne offene Entscheidung
 * zu (`b.offen.entscheidungen === 0`) — dieselbe Bedingung, unter der
 * `ErsteReiheKarte` eine Buehne schon als "erledigt" anzeigt.
 *
 * Rafael, 12.09.2026 14:12:00Z im Kanzler-Chat: "Ja, mach das" — auf den
 * zweimal wiederholten Vorschlag, die fertigen Sitzungssaal-Buehnen als EINEN
 * Stapel vorzulegen. Anlass war die Kanzler-Messung vom selben Tag 14:04:47Z:
 * von 88 geschlossenen Buehnen waren 85 "abgeloest" und nur 3 "erledigt" — die
 * Sperre staut fertige Arbeit auf, sie verhindert sie nicht.
 */
interface StapelSteuerung {
  auswaehlbar: (b: BuehneAnsicht) => boolean;
  ausgewaehlt: Set<string>;
  toggle: (id: string) => void;
  grund: string;
  setGrund: (g: string) => void;
  sendet: boolean;
  ergebnis: { ok: number; fehler: string[] } | null;
  absenden: () => void;
}

function StapelLeiste({ stapel, zeilen }: { stapel: StapelSteuerung; zeilen: BuehneAnsicht[] }) {
  const kandidaten = zeilen.filter(stapel.auswaehlbar);
  if (kandidaten.length === 0) return null;
  const anzahl = stapel.ausgewaehlt.size;
  return (
    <div data-ai-id="guide-stapel-abschluss" style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed var(--tn-border)' }}>
      <div style={{ fontSize: 10, color: 'var(--tn-text-muted)', marginBottom: 4 }}>
        {kandidaten.length} ohne offene Frage — anhaken und zusammen als erledigt melden
      </div>
      {anzahl > 0 && (
        <>
          <input
            data-ai-id="guide-stapel-grund"
            value={stapel.grund}
            onChange={e => stapel.setGrund(e.target.value)}
            placeholder="Grund (Pflicht) — gilt fuer alle ausgewaehlten"
            disabled={stapel.sendet}
            style={{ width: '100%', fontSize: 11, padding: '4px 6px', marginBottom: 5, borderRadius: 5, border: '1px solid var(--tn-border)', background: 'var(--tn-bg)', color: 'var(--tn-text)' }}
          />
          <button
            type="button"
            data-ai-id="guide-stapel-absenden"
            disabled={stapel.sendet || !stapel.grund.trim()}
            onClick={stapel.absenden}
            style={{
              fontSize: 11, padding: '4px 10px', borderRadius: 6,
              cursor: stapel.sendet || !stapel.grund.trim() ? 'default' : 'pointer',
              background: 'var(--tn-accent,var(--tn-link))22', border: '1px solid var(--tn-accent,var(--tn-link))', color: 'var(--tn-text)',
              opacity: stapel.sendet || !stapel.grund.trim() ? 0.6 : 1,
            }}
          >
            {stapel.sendet ? 'schliesst …' : `${anzahl} als erledigt melden`}
          </button>
        </>
      )}
      {stapel.ergebnis && (
        <div style={{ fontSize: 10.5, marginTop: 5, color: stapel.ergebnis.fehler.length ? 'var(--tn-orange)' : 'var(--tn-green)' }}>
          {stapel.ergebnis.ok} erledigt
          {stapel.ergebnis.fehler.length > 0 && ` · ${stapel.ergebnis.fehler.length} fehlgeschlagen: ${stapel.ergebnis.fehler.join(' · ')}`}
        </div>
      )}
    </div>
  );
}

/** „Kanzler · N Unteraufträge arbeiten" (Rafael 09.10.2026, Baustein 4) — Ämter haben keine Bühne,
 *  ihre arbeitenden Unteraufträge stehen deshalb hier und nicht in der Raumzahl. */
export function AemterArbeitZeile({ aemter }: { aemter?: Abgleich['aemter_arbeit'] }) {
  if (!aemter?.length) return null;
  return (
    <div data-ai-id="guide-aemter-arbeit" style={{ fontSize: 11, color: 'var(--tn-green)', margin: '0 0 5px' }}>
      {aemter.map(a => `${a.amt} · ${a.arbeitende} ${a.arbeitende === 1 ? 'Unterauftrag arbeitet' : 'Unteraufträge arbeiten'}`).join(' · ')}
    </div>
  );
}

function RaumKasten({ raum, titel, zeilen, offeneId, onOeffnen, stapel, haus, eingeklappt, kopfZeile, zusatz, children }: {
  raum: string;
  /** Hinter der Zahl, z. B. „ · 3 davon still" (HOFDREI) — keine eigene Reihe, nur die Zählung im Kopf. */
  zusatz?: string;
  /** Der Name in der Arbeitsliste ("In Arbeit", "Zurückgestellt") — der Raumname steht im Untertitel. */
  titel: string;
  zeilen: BuehneAnsicht[]; offeneId: string | null; onOeffnen: (b: BuehneAnsicht) => void;
  haus: 'dev' | 'partner';
  /** Pausenraum: eine Zeile, aufklappbar (Arbeitsliste, 03.10.2026). */
  eingeklappt?: boolean;
  /** Steht direkt unter dem Raumkopf, zaehlt nicht in die Raumzahl (z. B. „Kanzler · N Unteraufträge arbeiten"). */
  kopfZeile?: ReactNode;
  /**
   * Was zusaetzlich in diesen Raum gehoert, ohne Buehne zu sein — die Chats ohne Buehne
   * im Sitzungssaal (REGEL 3, Rafael 15.09./21.09.2026: *„Eine Sitzung ist so lange im
   * Sitzungssaal, bis sie eine Bühne hat"*). Sie zaehlen NICHT in die Raumzahl der
   * Buehnen (Pruefsatz: Summe der Raumkoepfe == offene Buehnen).
   */
  children?: ReactNode;
  /** NUR fuer 'pausenraum' gesetzt (siehe `Hofansicht`) — Mehrfachauswahl fuer den
   *  Stapel-Abschluss, siehe `StapelLeiste`. Bis 13.09.2026 hing er am Sitzungssaal;
   *  dort wird gearbeitet, und ein Sammel-"erledigt" raeumt genau das ab. */
  stapel?: StapelSteuerung;
}) {
  /*
   * EIN LEERER RAUM SCHRUMPFT, ER VERSCHWINDET NICHT.
   *
   * Verschwinden duerfte er nicht: die Raeume SIND die Ordnung, und ein Raum, der nur
   * dann dasteht, wenn etwas drin ist, laesst Rafael raten, ob es ihn noch gibt. Seit
   * der Arbeitsliste (03.10.2026) ist er dabei keine leere Flaeche mehr, sondern EINE
   * gedaempfte Textzeile: Name, Zahl, Zweck.
   *
   * KEINE "FRAGEN AN DICH"-ZAEHLUNG MEHR AM RAUMKOPF (03.10.2026). Sie stand in jedem
   * Raum und liess offen, WO man antworten soll. Gezaehlt wird sie jetzt nur noch oben
   * unter „Wartet auf dich"; an der einzelnen Karte steht sie weiter.
   */
  const farbe = RAUM_FARBE[raum];
  const kopf = `${titel} · ${zeilen.length}${zusatz ?? ''}`;
  if (zeilen.length === 0 && !children) {
    return (
      <div data-ai-id={`guide-raum-${raum}`} className="guide-al-titel" style={{ fontSize: 12, color: 'var(--tn-text-muted)', margin: '2px 0 10px', opacity: 0.75 }}>
        <span style={{ fontWeight: 700, color: farbe ?? 'inherit' }}>{kopf}</span>
        <span> — {RAUM_WORT[raum] ?? raum}: {RAUM_SATZ[raum]}; gerade leer</span>
        {kopfZeile}
      </div>
    );
  }
  const zeigeZeilen = (liste: BuehneAnsicht[]) => liste.map(b => (
    <RaumZeile key={b.id} b={b} offen={offeneId === b.id} onOeffnen={onOeffnen}
      checkbox={stapel && stapel.auswaehlbar(b) ? { ausgewaehlt: stapel.ausgewaehlt.has(b.id), onToggle: () => stapel.toggle(b.id) } : undefined} />
  ));
  const inhalt = (
    <>
      {raum === 'sitzungssaal' ? <SaalBereiche zeilen={zeilen} zeigeZeilen={zeigeZeilen} /> : zeigeZeilen(zeilen)}
      {stapel && zeilen.length > 0 && <StapelLeiste stapel={stapel} zeilen={zeilen} />}
    </>
  );
  if (eingeklappt) {
    return (
      <div data-ai-id={`guide-raum-${raum}`} className="guide-al-klapp" style={{ marginBottom: 6 }}>
        <CollapsibleSection storageKey={`cui-guide-al-${haus}-${raum}`} defaultOpen={false}
          title={<span className="guide-al-titel" style={{ color: farbe, fontWeight: 700 }}>{kopf} <span style={{ fontWeight: 400, opacity: 0.8 }}>— {RAUM_WORT[raum] ?? raum}</span></span>}>
          <div style={{ color: 'var(--tn-text-muted)', fontSize: 11, margin: '2px 0 6px' }}>{RAUM_SATZ[raum]}</div>
          {inhalt}
          {children}
        </CollapsibleSection>
      </div>
    );
  }
  return (
    <section data-ai-id={`guide-raum-${raum}`} style={{ marginBottom: 10, minWidth: 0 }}>
      <ReihenTitel titel={kopf} satz={`${RAUM_WORT[raum] ?? raum}: ${RAUM_SATZ[raum]}`} farbe={farbe} />
      {kopfZeile}
      {inhalt}
      {children}
    </section>
  );
}

/**
 * DRINGLICHKEIT UND AUFWAND AN DER ZEILE (Rafael, 24.10.2026: „ich weiss nicht, was dringlicher ist und wie viel
 * Arbeit es kostet"). Alles aus dem Arbeitsplan; was dort nicht steht, steht hier als „ungeplant" bzw. „Aufwand offen".
 * Als Marken neben der Zeile (Darstellung: GuideOberthemen.css). Die Standard-Prioritaet „Zielarbeit" und „ungeplant" bekommen
 * KEINE Marke (Rauschen, Rafael 24.10.2026); Frist, „entblockt Arbeit" und „spaeter" stehen weiter da. Angezeigt wird nur anders,
 * die Daten bleiben dieselben. `kompakt`: alles in einer Zeile; die Energie steht dann im Tooltip der Aufwand-Marke.
 */
/**
 * STATUSMARKE JE ZEILE (Rafael, 24.10.2026: „dann sehe ich in den Zielen, was pausiert ist und warum“): ⏳ wartet auf dich / ▶ arbeitet / ⏸ pausiert / ✔ Ergebnis bereit.
 * Bei „pausiert“ steht der GRUND kurz dabei, aus einem echten Feld (siehe `pausenGrund`: Abhaengigkeit, `ruhend`, Verlauf, zuletzt die erste Satzgruppe des
 * Standtextes — Freitext, im Tooltip so benannt), sonst „Grund nicht erfasst“. Symbol UND Wort, nicht nur Farbe.
 */
function StatusMarke({ b }: { b: BuehneAnsicht }) {
  const s = statusVon(b);
  const klasse = s === 'wartet' ? 'ob-marke--braucht' : s === 'abgeschlossen' ? '' : s === 'ergebnis' ? '' : s === 'pausiert' ? 'ob-marke--pause' : s === 'anderes' ? 'ob-marke--anders' : s === 'ohneSchritt' ? 'ob-marke--schritt' : s === 'arbeitet' ? 'ob-marke--arbeit' : 'ob-marke--schritt';
  /* MESSUNG UND PLAN GETRENNT (Rafael 25.10.2026: es muss immer erkennbar sein, ob jetzt jemand arbeitet,
   * unabhaengig von Ergebnis oder Frage). Die Hauptmarke folgt dem Vorrang von `statusVon`; daneben steht,
   * was die Messung sagt („arbeitet gerade“), und was der Plan sagt (Ergebnis bzw. unterbrochen). */
  const arbeitetGerade = s !== 'arbeitet' && b.arbeit_laeuft === true;
  const auchErgebnis = s !== 'wartet' && ergebnisDa(b);
  const planUnterbrochen = s !== 'pausiert' && b.arbeitsplan?.phase === 'unterbrochen';
  return (
    <>
      <span className={`ob-marke ob-status-marke ${klasse}`} data-ai-id={`guide-status-${b.id}`} data-status={s} title={STATUS_WORT[s]}>
        <span aria-hidden="true">{STATUS_SYMBOL[s]}</span> {STATUS_ZEILE_WORT[s]}
      </span>
      {arbeitetGerade && (
        <span className="ob-marke ob-status-marke ob-marke--arbeit" data-ai-id={`guide-arbeitet-gerade-${b.id}`} title="Gemessen: an dieser Bühne arbeitet gerade eine Sitzung">
          <span aria-hidden="true">▶</span> arbeitet gerade
        </span>
      )}
      {planUnterbrochen && (
        <span className="ob-marke ob-status-marke ob-marke--pause" data-ai-id={`guide-plan-unterbrochen-${b.id}`} title="Laut Arbeitsplan ist die Arbeit unterbrochen">
          <span aria-hidden="true">⏸</span> Plan: unterbrochen
        </span>
      )}
      {auchErgebnis && (
        <span className="ob-marke ob-status-marke" data-ai-id={`guide-ergebnis-da-${b.id}`} title="Zielabdeckung und Beratung sind belegt; der Auftrag ist nicht automatisch abgeschlossen">
          <span aria-hidden="true">ⓘ</span> Beratung liegt vor
        </span>
      )}
    </>
  );
}

/**
 * DER GRUND UNTER DEM TITEL (Rafael, 24.10.2026: „was pausiert ist und warum“): bei „pausiert“ immer (echtes Feld oder „Grund nicht erfasst“), bei „wartet“ nur,
 * wenn es einen gibt. `voll` ist die ungekuerzte Fassung fuer das ⓘ; `quelle` sagt, ob es Freitext aus dem Standtext ist.
 */
function statusGrund(b: BuehneAnsicht): { zeile: string; voll: string; hinweis: string } | null {
  const s = statusVon(b);
  if (b.arbeitsinformation) return {zeile:b.arbeitsinformation, voll:b.arbeitsinformation, hinweis:'Arbeitsstand'};
  if (s === 'pausiert') {
    const q = { arbeitsplan: b.arbeitsplan ?? null, ruhendWort: b.ruhend ? ruhendText(b.ruhend) : null, verlauf: b.verlauf, standText: standText(b) };
    const kurz = pausenGrund(q, 140); const voll = pausenGrund(q, 600);
    return { zeile: kurz.quelle === 'keiner' ? kurz.text : `Grund: ${kurz.text}`, voll: `Pausiert — ${voll.quelle === 'keiner' ? voll.text : `Grund: ${voll.text}`}`,
      hinweis: voll.quelle === 'standtext' ? 'Freitext aus dem Standtext' : voll.quelle === 'keiner' ? 'kein Grund erfasst' : 'aus dem Arbeitsplan bzw. Verlauf' };
  }
  if (s === 'wartet') {
    const q = { offeneFragen: (b.entscheidungen ?? []).filter(istOffeneFrage).map(e => e.titel) };
    const kurz = wartetGrund(q, 140); const voll = wartetGrund(q, 600);
    return kurz && voll ? { zeile: '', voll: `Wartet auf dich — ${voll.text}`, hinweis: 'offene Frage' } : null;
  }
  if (s === 'unbekannt') {
    const m = (b as { berater_messfehler?: string }).berater_messfehler;
    const t = m ? `Messung des Hauses fehlt: ${m}` : 'Keine Tätigkeitsmessung für diese Bühne';
    return { zeile: t.length > 140 ? `${t.slice(0, 139)}…` : t, voll: `Tätigkeit unbekannt — ${t}`, hinweis: 'vom Server (Beratermessung des Hauses)' };
  }
  if (s === 'anderes') {
    const kurz = anderesGrund(b, 140); const voll = anderesGrund(b, 600);
    const alle = offeneAbhaengigkeiten(b).map(a => abhaengigkeitWort(a.wartetAuf));
    if ((!kurz || !voll) && b.saal_bereich === 'zuarbeit') {
      const wer = b.arbeitsplan?.zustaendig?.trim();
      const t = `Wartet auf Zuarbeit${wer ? `: ${wer}` : ' (vom Server belegt)'}`;
      return { zeile: t.length > 140 ? `${t.slice(0, 139)}…` : t, voll: t, hinweis: 'Zuarbeit extern bzw. bei einem Amt, vom Server belegt' };
    }
    if (!kurz || !voll) return null;
    return { zeile: `Wartet auf: ${kurz.text}${kurz.mehr > 0 ? ` (+${kurz.mehr})` : ''}`,
      voll: `Wartet auf anderes — ${alle.length ? alle.join(' · ') : voll.text}${b.ruhend && alle.length ? ` · ${anderesGrund({ ...b, arbeitsplan: undefined }, 600)?.text ?? ''}` : ''}`,
      hinweis: alle.length ? 'aus den offenen Abhängigkeiten des Arbeitsplans' : 'Ruhe, vom Server gesetzt' };
  }
  if (s === 'ergebnis') {
    const z = ergebnisZusatz(b);
    return z ? { zeile: z.zeile, voll: z.voll, hinweis: 'Ruhe bzw. offene Abhängigkeit aus dem Arbeitsplan' } : null;
  }
  if (s === 'ohneSchritt') {
    const z = ohneSchrittZeile(b);
    return { zeile: z.zeile, voll: z.voll, hinweis: 'Der Server misst: niemand arbeitet; der Berater führt bis zum Abschluss (Vertrag 25.10.2026)' };
  }
  if (s === 'imBau') {
    return { zeile: 'Im Bau: der Berater richtet die Fenster noch ein', voll: 'Im Bau — die Bühne hat noch kein Rezept, also keine Fenster zum Öffnen. Sobald der Berater sie eingerichtet hat, steht hier ihr Stand.', hinweis: 'Rezept fehlt (Server-Feld rezept)' };
  }
  return null;
}

export function ZielMarken({ b, kompakt }: { b: BuehneAnsicht; kompakt?: boolean }) {
  const p = b.arbeitsplan;
  const frist = fristDatum(p?.frist);
  const kurz = frist ? `${frist.slice(0, 6)}` : null;
  const zeigePrio = p?.prioritaet === 'frist' || p?.prioritaet === 'entblocken' || p?.prioritaet === 'spaeter';
  const prioKlasse = p?.prioritaet === 'frist' ? 'ob-marke--frist' : p?.prioritaet === 'entblocken' ? 'ob-marke--frei' : '';
  return (
    <div className={`ob-marken${kompakt ? ' ob-marken--eine-zeile' : ''}`} data-ai-id={`guide-zielmarken-${b.id}`}>
      {kompakt ? <StatusMarke b={b} /> : (brauchtDich(b) && <span className="ob-marke ob-marke--braucht">braucht dich</span>)}
      {zeigePrio && p?.prioritaet && (
        <span className={`ob-marke ${prioKlasse}`} title={frist ? `Frist ${frist}` : undefined}>{PRIORITAET_WORT[p.prioritaet]}{kurz ? ` bis ${kurz}` : ''}</span>
      )}
      <span className={`ob-marke${p?.minuten ? '' : ' ob-marke--offen'}`} title={p?.energie ? `Energie: ${p.energie}` : undefined}>{aufwandWort(b)}</span>
      {!kompakt && p?.energie && <span className="ob-marke">Energie: {p.energie}</span>}
    </div>
  );
}

/** Eine Stoerung im Sinn der Farbleiste: niemand arbeitet gemessen ueber der Wachschwelle (gestoert), Messfehler, oder gar kein Berater (kein). */
export const IST_STOERUNG = (b: BuehneAnsicht): boolean => ['gestoert', 'kein'].includes(zustandsKlasse(b));

type Gruppe = Oberthemagruppe<BuehneAnsicht>;

/** Bedarf- und Stoerungsmarke einer Gruppe (kommen in Kachel und grossem Kopf vor; die Kennungen bleiben dieselben). */
function BedarfMarken({ g }: { g: Gruppe }) {
  const braucht = g.braucht > 0;
  const bedarfUnklar = g.unterziele.flatMap(u=>u.buehnen).some(b=>b.antwortbedarf_status?.zustand==='ungewiss');
  const vorbereitet = g.unterziele.flatMap(u=>u.buehnen).filter(b=>b.hauptstatus==='fuer_dich_vorbereitet').length;
  return (
    <>
      <span data-ai-id={`guide-oberthema-bedarf-${g.schluessel}`} className={braucht ? 'ob-marke ob-marke--braucht' : 'ob-marke ob-marke--still'}>
        {braucht ? `${g.braucht} mit persönlichem Klärungsbedarf` : bedarfUnklar ? 'Persönlicher Klärungsbedarf noch ungeklärt' : 'Keine persönliche Frage hinterlegt'}
      </span>
      <span className="ob-marke ob-marke--still" title="Geprüfte Vorbereitung und persönlicher Klärungsbedarf sind unabhängig. Fehlende Prüfung belegt nicht, dass keine persönliche Klärung nötig ist.">
        {vorbereitet ? `${vorbereitet} geprüft vorbereitet` : 'Vorbereitung noch nicht vollständig geprüft'}
      </span>
      {g.ohneSchritt > 0 && (
        <span data-ai-id={`guide-oberthema-ohne-schritt-${g.schluessel}`} className="ob-marke ob-marke--schritt"
          title="Nichts läuft, nichts wartet, kein Termin: Der Berater hat keinen nächsten Schritt eingetragen.">
          ❓ {g.ohneSchritt} ohne Schritt
        </span>
      )}
      {g.stoerungen > 0 && (
        <span data-ai-id={`guide-oberthema-stoerung-${g.schluessel}`} className="ob-marke ob-marke--stoerung"
          title="Niemand arbeitet gemessen daran, oder es ist kein Berater eingetragen — Einzelheiten in der Gruppe (Randfarbe) und unter „Nach Raum“.">
          ⚠ {g.stoerungen} gestört
        </span>
      )}
    </>
  );
}

/** Die Marken im grossen Kopf eines gewaehlten Oberthemas (Bedarf, Stoerung, Summe der Minuten, Ziel). */
function GruppenMarken({ g, jetzt }: { g: Gruppe; jetzt: number }) {
  return (
    <span className="ob-marken ob-marken--eine-zeile">
      <BedarfMarken g={g} />
      {/* Ohne eine einzige Schaetzung steht im Kopf nichts („Aufwand offen (n)“ waere nur Rauschen); jede Zeile zeigt ihr „Aufwand offen“ weiter. */}
      {g.zeit.mit > 0 && <span className="ob-marke">{zeitsummeWort(g.zeit)}</span>}
      <span className="ob-marke">{zielstandWort(g.ziel, jetzt)}</span>
    </span>
  );
}

/**
 * KACHEL IN DER UEBERSICHT „ALLE“ (Rafael, 24.10.2026: „das ist aber endlos zu scrollen"): Name, Anzahl, Bedarf, Stoerung, naechster Termin,
 * Summe der Minuten und hoechstens die zwei dringendsten Buehnen, die dich brauchen — je eine Zeile. Klick auf die Kachel oeffnet den Tab,
 * Klick auf eine Zeile oeffnet die Buehne.
 */
function Kachel({ g, onOeffneTab, onOeffnen }: { g: Gruppe; onOeffneTab: () => void; onOeffnen: (b: BuehneAnsicht) => void }) {
  const termin = naechsterTermin(g);
  const minuten = zeitsummeKurz(g.zeit);
  const oben = dringendsteBedarf(g, 2);
  return (
    <div className={`ob-kachel${g.braucht > 0 ? ' ob-kachel--braucht' : ''}${g.stoerungen > 0 ? ' ob-kachel--stoerung' : ''}`}
      data-ai-id={`guide-oberthema-${g.schluessel}`} onClick={onOeffneTab}>
      <button type="button" className="ob-kachel-kopf" onClick={e => { e.stopPropagation(); onOeffneTab(); }}
        title={`${g.name}: ${g.anzahl} ${g.anzahl === 1 ? 'Bühne' : 'Bühnen'} — öffnet den Tab. ${zeitsummeWort(g.zeit)}`}>
        <span className="ob-kachel-name">{g.name}{g.imBoard || g.divers ? '' : <span className="ob-name-leise"> (nicht im Board)</span>}</span>
        <span className="ob-kachel-anzahl">{g.anzahl}</span>
      </button>
      <div className="ob-marken ob-marken--eine-zeile">
        <BedarfMarken g={g} />
        {termin && <span className={`ob-marke${termin.art === 'frist' ? ' ob-marke--frist' : ''}`} title={termin.art === 'frist' ? 'Nächste Frist einer Bühne' : 'Nächstes Zieldatum eines Unterziels'}>{termin.art === 'frist' ? 'Frist' : 'Ziel'} {termin.text}</span>}
        {minuten && <span className="ob-marke">{minuten}</span>}
      </div>
      {g.divers && <div className="ob-divers-erklaerung" data-ai-id="guide-divers-erklaerung">{DIVERS_ERKLAERUNG}</div>}
      {oben.length > 0 && (
        <ul className="ob-kachel-zeilen">
          {oben.map(b => (
            <li key={b.id}>
              <button type="button" className="ob-kachel-zeile" title={b.titel} onClick={e => { e.stopPropagation(); onOeffnen(b); }}>{b.titel}</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Ein Unterziel im gewaehlten Oberthema: schmale Trennzeile (Kuerzel, Name gekuerzt, Termin, Anzahl), darunter je Buehne EINE Zeile. */
function UnterzielTabelle({ u, jetzt, offeneId, onOeffnen }: {
  u: Unterzielgruppe<BuehneAnsicht>; jetzt: number; offeneId: string | null; onOeffnen: (b: BuehneAnsicht) => void;
}) {
  const k = unterzielKurz(u.name, 56);
  const ohne = u.name === 'ohne Unterziel';
  const tage = u.zielIso ? tageBis(u.zielIso, jetzt) : null;
  return (
    <div className={`ob-uz-block${u.braucht > 0 ? ' ob-uz-block--braucht' : ''}`} data-ai-id={`guide-unterziel-${u.schluessel}`}>
      <div className="ob-trenner" title={u.name}>
        <span className="ob-trenner-name">{ohne ? (u.schluessel.startsWith(DIVERS_SCHLUESSEL) ? 'noch einzuordnen' : 'ohne Unterziel') : <>{k.kuerzel && <b>{k.kuerzel}</b>} {k.kurz}</>}</span>
        <span className="ob-trenner-meta">
          {!ohne && (u.zielIso ? `Ziel ${datumDe(u.zielIso).slice(0, 6)} · ${tage! < 0 ? `${-tage!} Tage überfällig` : `in ${tage} Tagen`} · ` : u.zielText ? `Ziel ${u.zielText} · ` : '')}
          {u.buehnen.length} {u.buehnen.length === 1 ? 'Bühne' : 'Bühnen'}
          {u.braucht > 0 && <span className="ob-marke ob-marke--braucht ob-marke--klein">{u.braucht} für dich</span>}
        </span>
      </div>
      {u.buehnen.map(b => (
        <div key={b.id} className={`ob-zeile ob-zeile--kompakt${brauchtDich(b) ? ' ob-zeile--braucht' : ''}`}>
          <RaumZeile b={b} offen={offeneId === b.id} onOeffnen={onOeffnen} ruhig kompakt marken={<ZielMarken b={b} kompakt />} />
        </div>
      ))}
    </div>
  );
}

/**
 * Meldet dem Schnellweg-Store die Oberthemen/Unterziele (nur Namen), auch wenn die Ziele-Ansicht gerade nicht gezeigt wird — die Dock-Knoepfe und Kurzbefehle brauchen sie
 * in jeder Ansicht. Stellt nichts dar. Der Store vergleicht den Inhalt, ein neuer Array pro Render loest also nichts aus.
 */
export function ZielePublizist({ zeilen }: { zeilen: BuehneAnsicht[] }) {
  const ziele = useMemo(() => zieleAusGruppen(gruppiere(zeilen, IST_STOERUNG)), [zeilen]);
  /* Auch nach einem Leeren des Stores (Nutzerwechsel) wieder melden: die gemeldeten Ziele im Store sind Teil der Abhaengigkeiten. */
  const gemeldet = useGuideAnsichtSteuerung().ziele;
  useEffect(() => { guideAnsichtSteuerung.veroeffentliche({ ziele }); }, [ziele, gemeldet]);
  return null;
}

/**
 * DIE LISTE NACH OBERTHEMA (Rafael, 24.10.2026): „nach Oberthemen sortiert, in Gruppen" und „ich lese jedes Mal alle Buehnen, obwohl ich nur
 * bei bestimmten weitermachen muss". TABS: oben „Alle" + ein Tab je Oberthema, bei einem gewaehlten Oberthema eine zweite Leiste „Alle Unterziele"
 * + je Unterziel. Die Auswahl lebt NUR im State (kein Browserspeicher); gibt es das Oberthema nicht mehr, steht wieder „Alle" — mit sichtbarem Hinweis.
 * KOMPAKT (Rafael, 24.10.2026: „das ist aber endlos zu scrollen"): „Alle" ist eine Kachelübersicht (3–4 Spalten, je Oberthema eine Kachel), ein
 * Oberthema ist eine dichte Tabelle (eine Zeile je Buehne), die Tab-Leiste klebt oben und traegt rechts In Arbeit / Zurückgestellt / Ansichtswahl.
 * Der erklaerende Satz steht nur noch im Tooltip. Siehe GuideOberthemen.css.
 */
export function OberthemenListe({ zeilen, offeneId, onOeffnen, kopfZeile, inArbeit, zurueckgestellt, zurRaumAnsicht, nutzerKennung }: {
  zeilen: BuehneAnsicht[]; offeneId: string | null; onOeffnen: (b: BuehneAnsicht) => void; kopfZeile?: ReactNode;
  /** Wem „Heute dran“ gehoert (Schluessel mit dem Tag). */
  nutzerKennung: string;
  /** Was die Raumansicht im Kopf von „In Arbeit“ zeigt (Anzahl und „n davon still“) — hier nicht verloren. */
  inArbeit: { anzahl: number; zusatz: string }; zurueckgestellt: number; zurRaumAnsicht: () => void;
}) {
  const gruppen = useMemo(() => gruppiere(zeilen, IST_STOERUNG), [zeilen]);
  /* Auswahl und Statusfilter liegen im Schnellweg-Store (guideAnsichtSteuerung): so setzen Dock-Knoepfe und Kurzbefehle sie ohne Modell, und „Rueckgaengig“ kennt den Stand davor. */
  const steuer = useGuideAnsichtSteuerung();
  const auswahl: Auswahl = steuer.auswahl;
  const setAuswahl = guideAnsichtSteuerung.setzeAuswahl;
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [erklaerungOffen, setErklaerungOffen] = useState(false);
  const letzterName = useRef<Record<string, string>>({});
  for (const g of gruppen) letzterName.current[g.schluessel] = g.name;
  const wirksam = wirksameAuswahl(gruppen, auswahl);
  /* RAEUME SIND JETZT FILTER (Rafael, 24.10.2026: „eigentlich ist das ja nur eine Art Filter“): ein Statusfilter ueber der Liste, der in „Alle“, im Reiter und in der
     Unterziel-Leiste gemeinsam wirkt. Die Tabs und die Auswahl richten sich nach dem UNGEFILTERTEN Bestand (ein Filter laesst keinen Reiter verschwinden), die Zahlen
     und Zeilen nach dem gefilterten. */
  const statusFilter = aktuellerStatusfilter(steuer.filter);
  const setStatusFilter = guideAnsichtSteuerung.setzeFilter;
  const zaehler = useMemo(() => statusZaehler(zeilen), [zeilen]);
  const gefiltert = useMemo(() => filtereNachStatus(zeilen, statusFilter), [zeilen, statusFilter]);
  const gruppenF = useMemo(() => gruppiere(gefiltert, IST_STOERUNG), [gefiltert]);
  const fMap = new Map(gruppenF.map(g => [g.schluessel, g]));
  /* Gibt es das gewaehlte Oberthema/Unterziel nicht mehr, wird das einmal sichtbar gesagt und die Auswahl zurueckgesetzt. */
  useEffect(() => {
    if (wirksam.zurueckAufAlle) {
      setHinweis(`Das Oberthema „${auswahl.gruppe ? (letzterName.current[auswahl.gruppe] ?? auswahl.gruppe) : ''}“ ist nicht mehr in der Liste — es steht wieder „Alle“.`);
      setAuswahl(ALLE_AUSWAHL);
    } else if (wirksam.unterzielVerloren) {
      setHinweis('Das gewählte Unterziel ist nicht mehr in diesem Oberthema — es stehen wieder alle Unterziele.');
      setAuswahl(wirksam.auswahl);
    }
  }, [wirksam.zurueckAufAlle, wirksam.unterzielVerloren]);  // eslint-disable-line react-hooks/exhaustive-deps
  const waehle = (neu: Auswahl) => { setHinweis(null); setAuswahl(neu); };
  const jetzt = Date.now();
  const bedarf = gefiltert.filter(brauchtDich).length;
  const bedarfGesamt = zeilen.filter(brauchtDich).length;
  const stoerungenGesamt = gruppenF.reduce((n, g) => n + g.stoerungen, 0);
  const still = inArbeit.zusatz.replace(/^\s*·\s*/, '');
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const tabs: Array<{ schluessel: string | null; name: string; anzahl: number; braucht: number; stoerungen: number; ohneSchritt: number }> = [
    { schluessel: null, name: 'Alle', anzahl: gefiltert.length, braucht: bedarf, stoerungen: stoerungenGesamt, ohneSchritt: gruppenF.reduce((n, g) => n + g.ohneSchritt, 0) },
    ...gruppen.map(g => { const f = fMap.get(g.schluessel); return { schluessel: g.schluessel, name: g.name, anzahl: f?.anzahl ?? 0, braucht: f?.braucht ?? 0, stoerungen: f?.stoerungen ?? 0, ohneSchritt: f?.ohneSchritt ?? 0 }; }),
  ];
  const aktivIndex = Math.max(0, tabs.findIndex(x => x.schluessel === wirksam.auswahl.gruppe));
  const tastatur = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const neu = tabNachTaste(aktivIndex, tabs.length, e.key);
    if (neu === null) return;
    e.preventDefault();
    waehle({ gruppe: tabs[neu].schluessel, unterziel: null });
    tabRefs.current[neu]?.focus();
  };
  const erklaerung = `Nach Oberthema · ${gruppen.length} Gruppen · ${zeilen.length} Bühnen · ${bedarfGesamt} brauchen dich. Große Ziele aus dem Planungsboard; darunter das Unterziel, darunter die Bühne. Zugeklappte Gruppen enthalten nichts, was auf dich wartet; Störungen (gestört oder ohne Berater) zählt jede Kachel extra.`;
  /* `gewaehltOhneFilter` heisst: die ganze gewaehlte Gruppe (alle Unterziele), aber nach dem Statusfilter. Gibt es darin nichts, steht ein Satz da — der Reiter bleibt. */
  const gewaehltOhneFilter = wirksam.auswahl.gruppe === null ? null : (fMap.get(wirksam.auswahl.gruppe) ?? null);
  const gewaehltName = wirksam.auswahl.gruppe === null ? null : (gruppen.find(g => g.schluessel === wirksam.auswahl.gruppe)?.name ?? null);
  const sichtbar = gewaehltOhneFilter ? sichtbareGruppen(gruppenF, wirksam.auswahl) : [];
  const gewaehlt = gewaehltOhneFilter ? (sichtbar[0] ?? null) : null;
  return (
    <section data-ai-id="guide-oberthemen" style={{ marginBottom: 10, minWidth: 0 }}
      title={erklaerung}>
      <div className="ob-liste">
        {kopfZeile}
        <div className="ob-kopfleiste">
          <div className="ob-tabs" role="tablist" aria-label="Oberthema" data-ai-id="guide-oberthemen-tabs" onKeyDown={tastatur}>
            {tabs.map((x, i) => {
              const aktiv = i === aktivIndex;
              return (
                <button key={x.schluessel ?? 'alle'} type="button" role="tab" id={`ob-tab-${x.schluessel ?? 'alle'}`} aria-selected={aktiv}
                  aria-controls="ob-tabpanel" tabIndex={aktiv ? 0 : -1} ref={el => { tabRefs.current[i] = el; }}
                  data-ai-id={`guide-oberthemen-tab-${x.schluessel ?? 'alle'}`} className={`ob-tab${aktiv ? ' ob-tab--aktiv' : ''}${x.anzahl === 0 && statusFilter !== null ? ' ob-tab--leer' : ''}`}
                  onClick={() => waehle({ gruppe: x.schluessel, unterziel: null })}>
                  <span className="ob-tab-name" title={x.name}>{tabKurzname(x.name)}</span>
                  <span className="ob-tab-zahl">{x.anzahl}</span>
                  {x.braucht > 0 && <span className="ob-marke ob-marke--braucht ob-marke--klein" title={`${x.braucht} ${x.braucht === 1 ? 'braucht' : 'brauchen'} dich`}>{x.braucht}</span>}
                  {x.ohneSchritt > 0 && <span className="ob-marke ob-marke--schritt ob-marke--klein" title={`${x.ohneSchritt} ohne nächsten Schritt`}>❓{x.ohneSchritt > 1 ? ` ${x.ohneSchritt}` : ''}</span>}
                  {x.stoerungen > 0 && <span className="ob-marke ob-marke--stoerung ob-marke--klein" title={`${x.stoerungen} gestört`}>⚠{x.stoerungen > 1 ? ` ${x.stoerungen}` : ''}</span>}
                </button>
              );
            })}
          </div>
        </div>
        {erklaerungOffen && <div id="ob-erklaerung" className="ob-hinweis ob-hinweis--info" role="note" data-ai-id="guide-oberthemen-erklaerung-text">{erklaerung}</div>}
        {hinweis && <div className="ob-hinweis" role="status" data-ai-id="guide-oberthemen-hinweis">{hinweis}</div>}
        <div className="ob-filterzeile">
          <div className="ob-filter" role="group" aria-label="Status" data-ai-id="guide-oberthemen-filter">
            <button type="button" className="ob-fchip" aria-pressed={statusFilter === null} data-ai-id="guide-oberthemen-filter-alle" onClick={() => setStatusFilter(null)}>Alle <span className="ob-uz-zahl">{zaehler.alle}</span></button>
            {sichtbareStatus(zaehler, statusFilter).map(s => (
              <button key={s} type="button" className="ob-fchip" aria-pressed={statusFilter === s} data-ai-id={s === 'arbeitet' ? 'guide-oberthemen-in-arbeit' : `guide-oberthemen-filter-${s}`}
                title={s === 'arbeitet' && still ? `${zaehler.arbeitet} in Arbeit — ${still}` : s === 'pausiert' ? `${zaehler.pausiert} pausiert (Pausenraum: ${zurueckgestellt})` : s === 'anderes' ? 'Wartet auf anderes: nichts läuft, es gibt eine offene Abhängigkeit oder einen Prüftermin — nicht auf dich' : s === 'ohneSchritt' ? 'Kein nächster Schritt: nichts läuft, nichts wartet, kein Termin — der Berater hat keinen Schritt eingetragen' : undefined}
                onClick={() => setStatusFilter(f => (f === s ? null : s))}>
                <span aria-hidden="true">{STATUS_SYMBOL[s]}</span> {STATUS_FILTER_WORT[s]} <span className="ob-uz-zahl">{zaehler[s]}</span>
              </button>
            ))}
          </div>
          <div className="ob-rechts">
            <button type="button" className="ob-info-knopf" data-ai-id="guide-oberthemen-erklaerung" aria-expanded={erklaerungOffen} aria-controls={erklaerungOffen ? 'ob-erklaerung' : undefined}
              aria-label="Erklärung zur Ansicht Nach Oberthema" title="Erklärung zur Ansicht" onClick={() => setErklaerungOffen(o => !o)}>ⓘ</button>
            <span data-ai-id="guide-oberthemen-zum-raum" style={{ display: 'contents' }}>
              <button type="button" className="ob-status-link" onClick={zurRaumAnsicht} title="Zurück zur Ansicht „Räume (bisher)“ — dort stehen Warteraum, Sitzungssaal, Raumköpfe und der Stapel-Abschluss">Räume (bisher)</button>
            </span>
          </div>
        </div>
        <div role="tabpanel" id="ob-tabpanel" aria-labelledby={`ob-tab-${wirksam.auswahl.gruppe ?? 'alle'}`} className="ob-panel">
          {wirksam.auswahl.gruppe !== null && !(gewaehlt && gewaehltOhneFilter) ? (
            <div className="ob-leer" data-ai-id="guide-oberthemen-leer">Keine Bühnen mit diesem Status in „{gewaehltName}“.</div>
          ) : gewaehlt && gewaehltOhneFilter ? (
            <section className={`ob-gruppe ob-gruppe--gross${gewaehltOhneFilter.braucht > 0 ? ' ob-gruppe--braucht' : ''}${gewaehltOhneFilter.stoerungen > 0 ? ' ob-gruppe--stoerung' : ''}`}
              data-ai-id={`guide-oberthema-${gewaehltOhneFilter.schluessel}`}>
              <div className="ob-kopf ob-kopf--gross">
                <span className="ob-kopf-links">
                  <b className="ob-name ob-name--gross">{gewaehltOhneFilter.name}{gewaehltOhneFilter.imBoard || gewaehltOhneFilter.divers ? '' : <span className="ob-name-leise"> (nicht im Board)</span>}</b>
                  <span className="ob-anzahl">{gewaehltOhneFilter.anzahl} {gewaehltOhneFilter.anzahl === 1 ? 'Bühne' : 'Bühnen'} · {gewaehltOhneFilter.unterziele.length} {gewaehltOhneFilter.unterziele.length === 1 ? 'Unterziel' : 'Unterziele'}</span>
                </span>
                <GruppenMarken g={gewaehltOhneFilter} jetzt={jetzt} />
              </div>
              {gewaehltOhneFilter.divers && <div className="ob-divers-erklaerung" data-ai-id="guide-divers-erklaerung">{DIVERS_ERKLAERUNG}</div>}
              {!gewaehltOhneFilter.divers && <div className="ob-unterziel-leiste" role="group" aria-label="Unterziel" data-ai-id="guide-oberthemen-unterziele">
                <button type="button" aria-pressed={wirksam.auswahl.unterziel === null} className={`ob-uz${wirksam.auswahl.unterziel === null ? ' ob-uz--aktiv' : ''}`}
                  data-ai-id="guide-oberthemen-uz-alle" onClick={() => waehle({ gruppe: gewaehltOhneFilter.schluessel, unterziel: null })}>
                  Alle Unterziele <span className="ob-uz-zahl">{gewaehltOhneFilter.anzahl}</span>
                </button>
                {gewaehltOhneFilter.unterziele.map(u => {
                  const k = unterzielKurz(u.name);
                  const aktiv = wirksam.auswahl.unterziel === u.schluessel;
                  return (
                    <button key={u.schluessel} type="button" aria-pressed={aktiv} title={u.name} data-ai-id={`guide-oberthemen-uz-${u.schluessel}`}
                      className={`ob-uz${aktiv ? ' ob-uz--aktiv' : ''}${u.braucht > 0 ? ' ob-uz--braucht' : ''}`}
                      onClick={() => waehle({ gruppe: gewaehltOhneFilter.schluessel, unterziel: u.schluessel })}>
                      {k.kuerzel && <b>{k.kuerzel}</b>} {k.kurz} <span className="ob-uz-zahl">{u.buehnen.length}</span>
                    </button>
                  );
                })}
              </div>}
              <div className="ob-tabelle">
                {gewaehlt.unterziele.map(u => <UnterzielTabelle key={u.schluessel} u={u} jetzt={jetzt} offeneId={offeneId} onOeffnen={onOeffnen} />)}
              </div>
            </section>
          ) : (
            <>
            <HeuteDran zeilen={zeilen} nutzer={nutzerKennung} onOeffnen={onOeffnen} />
            <div className="ob-kacheln" data-ai-id="guide-oberthemen-kacheln">
              {gruppenF.length === 0 && <div className="ob-leer" data-ai-id="guide-oberthemen-leer">{statusFilter ? 'Keine Bühnen mit diesem Status.' : 'Keine Bühnen.'}</div>}
              {gruppenF.map(g => <Kachel key={g.schluessel} g={g} onOeffneTab={() => waehle({ gruppe: g.schluessel, unterziel: null })} onOeffnen={onOeffnen} />)}
            </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

/**
 * WARTET AUF DICH — der Block ganz oben (Rafael 29.09.2026: „Vor mir" und Warteraum zusammengelegt). Seit 24.10.2026 laesst sich die
 * Reihenfolge umschalten (Rafael: „auch ganz oben der Warteraum soll nach Zielen geordnet werden koennen"): „Dringlichkeit" ist das
 * bisherige Verhalten und die Vorgabe, „Nach Zielen" gruppiert dieselben Karten nach Oberthema und Unterziel: die Oberthemen in der
 * Reihenfolge der Tabs, die Unterziele darin in der Ordnung der Tabs (Bedarf, Zieltermin, Name). Nur Darstellung; der Zustand lebt im
 * React-State, nicht im Browserspeicher. Der Umschalter steht auch bei leerem Block da, damit der gewaehlte Modus sichtbar bleibt.
 * KOMPAKT (Rafael, 24.10.2026: „das ist aber endlos zu scrollen"): „Dringlichkeit" zeigt hoechstens DREI Karten, dahinter „weitere n".
 * „Nach Zielen" begrenzt NACH dem Gruppieren: JEDE Gruppe mit wartenden Buehnen steht da, je Gruppe hoechstens drei Buehnen, der Rest hinter
 * demselben „weitere n" (Zweitpruefung 24.10.2026: sonst fehlten Gruppen, die auf Rafael warten). Die Karten tragen keinen Standtext
 * (Tooltip / ⓘ), der erklaerende Satz steht nur noch im Tooltip der Titelzeile.
 */
export const WARTERAUM_SICHTBAR = 3;
export function WartetAufDichBlock({ ersteReihe, lebend, mitFrage, mitVorschlag, offeneId, onOeffnen }: {
  ersteReihe: BuehneAnsicht[]; lebend: BuehneAnsicht[]; mitFrage: number; mitVorschlag: number; offeneId: string | null; onOeffnen: (b: BuehneAnsicht) => void;
}) {
  const [reihenfolge, setReihenfolge] = useState<WartReihenfolge>('dringlichkeit');
  const [alleZeigen, setAlleZeigen] = useState(false);
  const volleGruppen = useMemo(() => reihenfolge === 'ziele' ? wartendeNachZielen(ersteReihe, lebend, IST_STOERUNG) : [], [reihenfolge, ersteReihe, lebend]);
  const gruppen = useMemo(() => begrenzeGruppen(volleGruppen, alleZeigen ? Number.POSITIVE_INFINITY : WARTERAUM_SICHTBAR), [volleGruppen, alleZeigen]);
  const sichtbar = alleZeigen ? ersteReihe : ersteReihe.slice(0, WARTERAUM_SICHTBAR);
  const begrenzbar = reihenfolge === 'ziele' ? volleGruppen.some(g => g.anzahl > WARTERAUM_SICHTBAR) : ersteReihe.length > WARTERAUM_SICHTBAR;
  const versteckt = reihenfolge === 'ziele' ? gruppen.reduce((n, g) => n + g.ausgeblendet, 0) : ersteReihe.length - sichtbar.length;
  return (
    <section style={{ marginBottom: 6 }} data-ai-id="guide-warteraum">
      <div className="ob-wart-kopf-zeile" title={`Warteraum: ${RAUM_SATZ.warteraum}`}>
        <ReihenTitel titel={`Für dich vorbereitet · ${ersteReihe.length}${mitFrage ? ` · ${mitFrage} mit Frage an dich` : ''}${mitVorschlag ? ` · ${mitVorschlag} mit freiwilligen Vorschlägen` : ''}`} farbe={RAUM_FARBE.vor_dem_koenig} />
        <div className="ob-wart-aktionen">
          {begrenzbar && (
            <button type="button" className="ob-wart-mehr" data-ai-id="guide-warteraum-mehr" aria-expanded={alleZeigen} aria-controls="ob-wart-liste" onClick={() => setAlleZeigen(a => !a)}>
              {alleZeigen ? 'weniger zeigen' : `weitere ${versteckt}`}
            </button>
          )}
          <div className="ob-wart-umschalter" role="group" aria-label="Reihenfolge" data-ai-id="guide-warteraum-reihenfolge">
            <span>Reihenfolge:</span>
            <Chip id="guide-warteraum-dringlichkeit" an={reihenfolge === 'dringlichkeit'} onClick={() => setReihenfolge('dringlichkeit')} titel="Wie bisher: Fragen an dich zuerst, die am längsten wartende vorn">Dringlichkeit</Chip>
            <Chip id="guide-warteraum-ziele" an={reihenfolge === 'ziele'} onClick={() => setReihenfolge('ziele')} titel="Gruppiert nach Oberthema und Unterziel, Oberthemen und Unterziele in der Reihenfolge der Tabs">Nach Zielen</Chip>
          </div>
        </div>
      </div>
      {ersteReihe.length === 0 ? (
        <div data-ai-id="guide-raum-vor_dem_koenig" style={{ fontSize: 12, color: 'var(--tn-text-muted)', opacity: 0.8 }}>
          Gerade wartet nichts auf dich — was in Arbeit ist, steht darunter.
        </div>
      ) : reihenfolge === 'dringlichkeit' ? (
        <div id="ob-wart-liste" data-ai-id="guide-raum-vor_dem_koenig" className="guide-al-raster ob-wart-raster">
          {sichtbar.map(b => <ErsteReiheKarte key={b.id} b={b} offen={offeneId === b.id} onOeffnen={onOeffnen} kompakt />)}
        </div>
      ) : (
        <div id="ob-wart-liste" data-ai-id="guide-raum-vor_dem_koenig" className="ob-wart-gruppen">
          {gruppen.map(g => (
            <div key={g.schluessel} className="ob-wart-gruppe" data-ai-id={`guide-warteraum-oberthema-${g.schluessel}`}>
              <div className="ob-wart-kopf">
                <b className="ob-name">{g.name}{g.imBoard || g.divers ? '' : <span className="ob-name-leise"> (nicht im Board)</span>}</b>
                <span className="ob-marke ob-marke--braucht ob-marke--klein">{g.anzahl} {g.anzahl === 1 ? 'wartet' : 'warten'} auf dich</span>
                {g.ausgeblendet > 0 && <span className="ob-wart-ausgeblendet">· {g.ausgeblendet} weitere ausgeblendet</span>}
              </div>
              {g.unterziele.map(u => (
                <div key={u.schluessel} className="ob-wart-uz" data-ai-id={`guide-warteraum-unterziel-${u.schluessel}`}>
                  <div className="ob-wart-uz-kopf">
                    <span className="ob-unterziel-name">{u.name}</span>
                    <span className="ob-marke ob-marke--klein">{u.buehnen.length} {u.buehnen.length === 1 ? 'Bühne' : 'Bühnen'}</span>
                  </div>
                  <div className="guide-al-raster ob-wart-raster">
                    {u.buehnen.map(b => <ErsteReiheKarte key={b.id} b={b} offen={offeneId === b.id} onOeffnen={onOeffnen} kompakt />)}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/**
 * DER UMSCHALTER GANZ OBEN IM GUIDE (Rafael, 24.10.2026): „mach oben einen Umschalter zwischen den Ansichten hinzu und eine neue Beschreibung beim
 * ersten Leser, damit man sieht, warum es das gibt“ und „einen Stern oder sowas dazumachen, damit jeder der Partner sieht, dass es den Zielmodus gibt
 * und warum“. Zwei Knoepfe „Räume (bisher)“ | „Ziele (neu)“ (aria-pressed), daneben der Stern „NEU“ (nur solange die Ziele-Ansicht nicht geoeffnet und der
 * Hinweis nicht geschlossen wurde; Stern UND Wort, nicht nur Farbe) und ein ⓘ, das den Hinweis „Was ist neu und warum“ immer wieder abruft.
 * Reine Darstellung/Zustand; die Logik steht in guide-oberthemen.ts (Vorgabe je Nutzerart, Hinweis-Zustand).
 */
export function AnsichtLeiste({ ansicht, onAnsicht, hinweis, onHinweis, kartenModus = false }: {
  ansicht: Ansicht; onAnsicht: (a: Ansicht) => void; hinweis: AnsichtsHinweis; onHinweis: (e: HinweisEreignis) => void;
  /** Partner: Stern und ⓘ oeffnen die Erklaerkarte „So funktioniert dein Guide“ statt eines eigenen Feldes. */
  kartenModus?: boolean;
}) {
  const stern = sternSichtbar(hinweis);
  /* Schliesst „Verstanden“/„Ziele ansehen“ den Hinweis, verschwindet der gedrueckte Knopf samt Fokus — Tastaturnutzer landeten sonst auf der Seite. Der Fokus geht
     auf den Umschalter der gewaehlten Ansicht, sobald der Hinweis weg ist. */
  const raumKnopf = useRef<HTMLButtonElement>(null);
  const zieleKnopf = useRef<HTMLButtonElement>(null);
  const fokusNachSchliessen = useRef(false);
  useEffect(() => {
    if (hinweis.offen || !fokusNachSchliessen.current) return;
    fokusNachSchliessen.current = false;
    (ansicht === 'oberthema' ? zieleKnopf : raumKnopf).current?.focus();
  }, [hinweis.offen, ansicht]);
  const hinweisMitFokus = (e: HinweisEreignis) => { if (e === 'verstanden' || e === 'ziele-ansehen') fokusNachSchliessen.current = true; onHinweis(e); };
  return (
    <div className="ob-ansicht-wrap" data-ai-id="guide-ansicht-schalter">
      <div className="ob-ansicht-leiste">
        <div className="ob-seg-gruppe" role="group" aria-label="Ansicht der Bühnen">
          <button type="button" ref={raumKnopf} className="ob-seg" aria-pressed={ansicht === 'raum'} data-ai-id="guide-ansicht-raum" onClick={() => onAnsicht('raum')}
            title="Wie bisher: Bühnen nach Räumen (Für dich vorbereitet, In Arbeit, Von dir zurückgestellt, Abgeschlossen)">Räume (bisher)</button>
          <button type="button" ref={zieleKnopf} className="ob-seg" aria-pressed={ansicht === 'oberthema'} data-ai-id="guide-ansicht-oberthema" onClick={() => onAnsicht('oberthema')}
            title="Neu: Bühnen nach Zielen — Oberthema, Unterziel, Bühne">Ziele (neu)</button>
        </div>
        {stern && (
          <button type="button" className="ob-neu" data-ai-id="guide-ansicht-neu" aria-label="Neu: Ansicht nach Zielen" aria-expanded={kartenModus ? undefined : hinweis.offen}
            aria-controls={!kartenModus && hinweis.offen ? 'ob-neu-hinweis' : undefined} onClick={() => onHinweis('stern')}
            title={kartenModus ? 'Neu: Ansicht nach Zielen — öffnet die Erklärung „So funktioniert dein Guide“' : 'Neu: Ansicht nach Zielen — was ist das und warum?'}>
            <span aria-hidden="true">★</span> NEU
          </button>
        )}
        <button type="button" className="ob-info-knopf" data-ai-id="guide-ansicht-hinweis-knopf" aria-expanded={kartenModus ? undefined : hinweis.offen}
          aria-controls={!kartenModus && hinweis.offen ? 'ob-neu-hinweis' : undefined} aria-label={kartenModus ? 'Erklärung zum Guide öffnen' : 'Was ist neu und warum'}
          title={kartenModus ? 'Erklärung zum Guide öffnen' : 'Was ist neu und warum'}
          onClick={() => onHinweis('info')}>ⓘ</button>
      </div>
      {hinweis.offen && (
        <section id="ob-neu-hinweis" className="ob-neu-hinweis" aria-label="Was ist neu und warum" data-ai-id="guide-ansicht-hinweis">
          <div className="ob-neu-kopf">
            <b>Was ist neu und warum</b>
            <span className="ob-neu-knoepfe">
              {ansicht !== 'oberthema' && <button type="button" className="ob-neu-knopf ob-neu-knopf--haupt" data-ai-id="guide-ansicht-ziele-ansehen" onClick={() => hinweisMitFokus('ziele-ansehen')}>Ziele ansehen</button>}
              <button type="button" className="ob-neu-knopf" data-ai-id="guide-ansicht-verstanden" onClick={() => hinweisMitFokus('verstanden')}>Verstanden</button>
            </span>
          </div>
          <div className="ob-neu-punkte">
            <p><b>1 · Warum</b> Bühnen hingen bisher nach Räumen, das Wichtige ging im Rauschen unter. Jetzt gehört jede Bühne zu einem Unterziel und das zu einem großen Ziel aus dem Plan — so siehst du, was dich diese Woche weiterbringt.</p>
            <p><b>2 · Wie</b> „Alle“ ist die Kachelübersicht, ein Reiter zeigt ein Ziel, ⓘ zeigt Details. Die vier Status sind In Arbeit, Für dich vorbereitet, Von dir zurückgestellt und Abgeschlossen. Blockaden, Zuarbeit und Prüfungen stehen als Zusatz an der Arbeit.</p>
            <p><b>3 · Zurück</b> Mit „Räume (bisher)“ kommst du jederzeit zur gewohnten Ansicht zurück — mit Warteraum, Sitzungssaal und Stapel-Abschluss. Dieses ⓘ ruft die Erklärung wieder auf.</p>
          </div>
        </section>
      )}
    </div>
  );
}

function Chip({ id, an, onClick, children, ton, titel }: {
  id: string; an?: boolean; onClick: () => void; children: React.ReactNode; ton?: 'warn'; titel?: string;
}) {
  return (
    <button
      type="button"
      className="guide-touch-chip"
      data-ai-id={id}
      aria-pressed={an}
      title={titel}
      onClick={onClick}
      style={{
        fontSize: 12, padding: '4px 11px', borderRadius: 13, cursor: 'pointer',
        background: an ? 'var(--tn-accent,var(--tn-link))22' : 'transparent',
        color: ton === 'warn' ? 'var(--tn-orange)' : an ? 'var(--tn-text)' : 'var(--tn-text-muted)',
        border: `1px solid ${an ? 'var(--tn-accent,var(--tn-link))' : 'var(--tn-border)'}`,
      }}
    >{children}</button>
  );
}

// ── Der Tagesplan: EINE Zeile ────────────────────────────────────────

/**
 * WAS HEUTE ANSTEHT — in einer Zeile, unter den Raeumen.
 *
 * Rafael, 11.09.2026, nachdem er die erste Fassung gesehen hat: der Tagesplan soll
 * hinein, aber NICHT nach oben. "Der obere Rand gehoert dem, was auf ihn wartet; ein
 * Block dort schiebt die Raeume nach unten und erzeugt genau das Scrollen, das er
 * vermeiden will." Also eine Zeile, kein Kasten — die offenen Ziele laufen im Text
 * mit, das Ganze steht in der Sprechblase.
 *
 * Quelle ist der Fokus-Speicher (`/api/partner/planning`, Eintraege mit
 * `scope: 'day'` und heutigem Datum) — derselbe, den Rafael selbst pflegt. KEIN
 * eigener Zaehler, keine zweite Liste: die Projektverwaltung entfaellt als eigene
 * Flaeche, ihr Tagesteil bleibt als diese Zeile.
 *
 * FEHLER WERDEN GESAGT. Auf dem Partner antwortet die Route mit einem Fehler, wenn
 * das Haupthaus nicht erreichbar ist — ausdruecklich, "damit keine zweite Wahrheit
 * entsteht". Dann steht dieser Fehler hier, nicht "keine Ziele".
 */
function Tagesplan() {
  const [zeile, setZeile] = useState<{ offen: string[]; erledigt: number } | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    let lebt = true; let timer: ReturnType<typeof setTimeout>; let controller: AbortController | null = null; let failures = 0;
    const laden = async () => {
      controller = new AbortController();
      try {
        const heute = new Date();
        const tag = `${heute.getFullYear()}-${String(heute.getMonth() + 1).padStart(2, '0')}-${String(heute.getDate()).padStart(2, '0')}`;
        const r = await fetch(`${API}/partner/planning/tag?datum=${tag}`, { signal: AbortSignal.any([controller.signal, AbortSignal.timeout(15000)]) });
        const d = await r.json();
        if (!r.ok || !Array.isArray(d.focus)) throw Error(d.error ?? `HTTP ${r.status}`);
        if (!lebt) return;
        setZeile({ offen: d.focus.filter((e: any) => !e.done).map((e: any) => e.text), erledigt: d.focus.filter((e: any) => e.done).length });
        setFehler(null); failures = 0;
      } catch (e) {
        if (lebt) { failures++; setFehler('Tagesplan wird erneut geladen.'); }
      } finally { if (lebt) timer = setTimeout(() => void laden(), failures ? Math.min(3000 * failures, 15000) : 120000); }
    };
    void laden();
    return () => { lebt = false; clearTimeout(timer); controller?.abort(); };
  }, []);

  const stil: React.CSSProperties = {
    fontSize: 11, padding: '5px 9px', borderRadius: 7, marginBottom: 10,
    border: '1px solid var(--tn-border)', color: 'var(--tn-text-muted)',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  };
  if (fehler && !zeile) return <div data-ai-id="guide-tagesplan" role="status" style={stil}>{fehler}</div>;
  if (!zeile) return <div data-ai-id="guide-tagesplan" style={stil}>Tagesplan …</div>;
  if (zeile.offen.length === 0 && zeile.erledigt === 0) {
    return <div data-ai-id="guide-tagesplan" style={stil}>{fehler && '↻ letzter Stand · '}Heute steht noch nichts im Tagesplan.</div>;
  }
  return (
    <div data-ai-id="guide-tagesplan" style={stil}
      title={zeile.offen.length ? `Heute offen:\n· ${zeile.offen.join('\n· ')}` : 'Heute ist alles abgehakt.'}>
      {fehler && <span title="Die Verbindung wird automatisch erneut geprüft; angezeigt ist der zuletzt geladene Stand.">↻ letzter Stand · </span>}<b style={{ color: 'var(--tn-text)' }}>Heute</b> · {zeile.offen.length} offen, {zeile.erledigt} erledigt
      {zeile.offen.length > 0 && <> — {zeile.offen.join(' · ')}</>}
    </div>
  );
}

// ── Sitzungen, die keinem Berater zugeordnet sind ────────────────────

/**
 * EINE WARNZEILE, KEINE KATEGORIE.
 *
 * Rafael, 11.09.2026: *"de facto interessieren mich die Chats ja gar nicht mehr — die
 * muessen eh alle mit Buehnen in Verbindung stehen. Wenn nicht, muss der Assistent
 * erinnert werden, sie einzuordnen."*
 *
 * Hier stand vorher die ganze Sitzungsliste, nach Zustaenden gruppiert ("arbeitet
 * gerade", "ohne Aktivitaet", "nicht gestartet"). Sein Einwand traf den Kern: ein Chat
 * ist im Hofstaat kein Berater, also hat er keinen Raum — und eine Liste ohne Raum
 * neben den Raeumen ist wieder eine zweite Welt. Ob ein Prozess laeuft, ist
 * Maschinenzustand; fuer Rafael zaehlt, ob ein Berater steht und wo.
 *
 * Also bleibt EINE Zeile mit der Zahl. Wer sie aufmacht, sieht welche — zum
 * Nachsehen, nicht zum Abarbeiten: zustaendig ist der Assistent, und die Dienst-Wache
 * meldet ihm denselben Befund ohnehin.
 *
 * AEMTER ZAEHLEN NICHT MIT. Kanzler, Vizekanzler und Assistent haben per Definition
 * keine Buehne. Sie stehen in `board.konfig` und werden von dort ausgenommen — nicht
 * ueber eine Kennung im Code: eine fest eingetragene UUID ist genau die Kruecke, die
 * in der Dienst-Wache schon einmal noetig wurde und dort als Kruecke benannt ist.
 * Eine Sitzung, die sich als Koordination ausweist, aber in keinem Amt der
 * Konfiguration steht, wird deshalb GEZAEHLT und dabei benannt — das ist ein Befund
 * ueber die Konfiguration, kein Grund, sie stillschweigend zu uebergehen.
 */
/**
 * WER GERADE ARBEITET, OHNE IM HOF ZU STEHEN — als feste Sektion, nicht als Klapptext.
 *
 * DREI AENDERUNGEN AM 13.09.2026, alle aus Rafaels Beobachtungen:
 *
 * 1. SIE HOLT NICHTS MEHR SELBST. Bis heute fragte sie `GET /guide/sitzungen?haeuser=
 *    beide` und rechnete sich ihre eigene Vorstellung von „ohne Berater" zusammen —
 *    neben `abgleich.chats_ohne_buehne`, das der Server seit heute frueh aus der
 *    Sitzungslage rechnet. Zwei Messungen derselben Sache laufen auseinander, und die
 *    im Client war die schwaechere: sie kannte weder Arbeiter (Sub-Sitzungen) noch
 *    Modell noch Konto. Jetzt liest sie die eine Messung.
 * 2. RUHENDE CHATS BLEIBEN DRIN. Der alte Filter warf `taetigkeit.art === 'tot'` weg —
 *    genau das, was Rafael sehen wollte („dann kann der Assistent beschliessen ob sie
 *    gefinished werden"). Sie stehen jetzt getrennt und farblich abgesetzt.
 *    ZUM RAUM sagt dieses Zitat seit dem 21.09.2026 nichts mehr: sie stehen im
 *    SITZUNGSSAAL wie jeder Chat ohne Buehne, nicht im Pausenraum. Und sie heissen an
 *    der Zeile „ruht, weckbar" statt „tot" — `tot` ist serverseitig nur
 *    `processAlive === false` bei offener Sitzung, beide am 21.09. gemessenen Faelle
 *    trugen woertlich „kann mit der nächsten Nachricht fortgesetzt werden".
 * 3. SIE IST OFFEN. Sie war ein zugeklappter Einzeiler und deshalb in keinem einzigen
 *    Screenshot zu sehen — Rafael hat sie sich ausdruecklich „unten auf der Seite"
 *    gewuenscht und nicht gewusst, dass es sie schon gibt. Ein Befund, den man
 *    aufklappen muss, ist fuer den, der ihn nicht sucht, nicht vorhanden.
 */
/** Die Knöpfe der Platz-Blöcke: ohne Rahmen sahen sie im Probe-Bild (09.10.2026) wie bloßer Text aus. */
const PLATZ_KNOPF: React.CSSProperties = { fontSize: 10.5, padding: '1px 7px', marginLeft: 6, borderRadius: 5,
  border: '1px solid var(--tn-border)', background: 'var(--tn-bg-highlight, transparent)', color: 'var(--tn-text)', cursor: 'pointer' };
/** Eine Zeile fuer eine Sitzung ohne Platz — der Titel oeffnet den Chat, daneben die zwei
 *  Entscheidungen (Rafael 09.10.2026, b-guide-jede-sitzung-ein-platz-20261009): „Bühne daraus
 *  machen" und „beenden". Was der Knopf bewirkt hat, steht AN der Zeile, nie still. */
function ChatOhneBuehneZeile({ z, haus, hierHaus, onOeffnen }: {
  z: Abgleich['chats_ohne_buehne'][number]; haus: 'dev' | 'partner'; hierHaus: string | null;
  onOeffnen: (z: SitzungOhneBerater) => void;
}) {
  const [laeuft, setLaeuft] = useState<'buehne' | 'beenden' | null>(null);
  const [ergebnis, setErgebnis] = useState<PlatzAktionErgebnis | null>(null);
  const fuehreAus = async (art: 'buehne' | 'beenden') => {
    if (laeuft) return;
    if (art === 'beenden' && !window.confirm(`„${z.titel || z.sitzung.slice(0, 8)}" beenden?`)) return;
    setLaeuft(art); setErgebnis(null);
    setErgebnis(art === 'buehne' ? await fordereBuehneAn(z.sitzung) : await beendeOhnePlatz(z.sitzung));
    setLaeuft(null);
  };
  /* Nur das eigene Haus: der finish-Weg ist lokal (mission), die Sitzung eines anderen Hauses
   * waere hier eine unbekannte Kennung. Der Abgleich kommt ohnehin nur fuer `hierHaus`. */
  const bedienbar = !hierHaus || haus === hierHaus;
  const knopf: React.CSSProperties = { ...PLATZ_KNOPF, cursor: laeuft ? 'wait' : 'pointer' };
  return (
    <div data-ai-id={`guide-platz-zeile-${z.sitzung}`} style={{ padding: '2px 0' }}>
    <button
      type="button"
      data-guide-aktion="sitzung"
      data-guide-ziel={z.sitzung}
      data-ai-id={`guide-ohne-berater-zeile-${z.sitzung}`}
      className="guide-touch-zeile"
      title={[z.zustand, z.konto ? `Konto ${z.konto}` : null].filter(Boolean).join(' · ')}
      onClick={() => onOeffnen({
        sessionId: z.sitzung, name: z.titel || z.sitzung.slice(0, 8),
        haus, eigenesHaus: hierHaus,
      })}
      style={{
        display: 'inline', textAlign: 'left', cursor: 'pointer',
        background: 'none', border: 'none', padding: 0, color: 'inherit', font: 'inherit',
      }}
    >
      · {z.titel || z.sitzung.slice(0, 8)}
      {/*
        "RUHT" STATT "TOT" — Rafael, 21.09.2026, zusammen mit der Raumregel.
        `tot` heisst serverseitig nur `processAlive === false` bei OFFENER Sitzung
        (guide.ts: `lebt: c.processAlive === true && c.manualFinished !== true`).
        Beide am 21.09. 07:21Z gemessenen Faelle trugen als Zustand woertlich
        "Sitzung ruht seit 16:49Z — kann mit der nächsten Nachricht fortgesetzt
        werden". Das ist kein Endzustand, sondern ein weckbarer Chat.

        Eine Zeile, die "tot" sagt, wo "ruht, weckbar" stimmt, ist dieselbe Sorte
        Fehler wie ein roter Sendestatus auf erledigter Arbeit: sie laesst etwas
        verloren aussehen, das nur schlaeft. Der gemessene Wortlaut des Servers
        steht im `title` daneben, damit die Zeile nichts behauptet, was er nicht
        gemessen hat.
      */}
      {z.tot && <span style={{ color: 'var(--tn-orange)' }} title={z.zustand ?? undefined}> · derzeit kein laufender Prozess</span>}
      {z.modell && <span style={{ color: 'var(--tn-text-muted)' }}> [{z.modell}]</span>}
      {z.konto && <span style={{ color: 'var(--tn-text-muted)' }}> · {z.konto}</span>}
      {z.seit && <span style={{ color: 'var(--tn-text-muted)' }}> · seit {dauer(z.seit)}</span>}
      {/*
        DER ROTE DEFEKT NACH DREI ANLAEUFEN — Rafael, 21.09.2026, woertlich: „alle
        sessions, auch tote, gehoeren so lange aufgeweckt im sitzungssaal bis sie eine
        buehne haben - maximal drei mal, dann muessen sie einen roten defekt zeigen".

        ER STEHT UNTER DER ZEILE, NICHT STATT IHRER, und die Zeile bleibt im
        Sitzungssaal: der Defekt sagt, dass die Buehnenpflicht unerfuellt UND der Weg
        dorthin verstopft ist — er sortiert nichts weg. Genau daran ist der
        Buehnen-Automat am 20.09. gescheitert.

        DER GRUND STEHT DABEI, nicht nur die Zahl. Er kam bis heute nur in `job.fehler`
        vor, das keine Oberflaeche liest — wer nur im Protokoll laut wird, ist still.
        Was nachgesehen wurde, steht im `title`, auch die Nullbefunde: eine sicher
        klingende, falsche Ursache beendet die Suche.
      */}
      {z.grund_text && z.grund !== 'ohne-buehne' && (
        <span data-ai-id={`guide-platz-grund-${z.sitzung}`} style={{ color: 'var(--tn-red)' }}> · {z.grund_text}</span>
      )}
    </button>
      {bedienbar && (
        <>
          <button type="button" data-ai-id={`guide-platz-buehne-${z.sitzung}`} disabled={!!laeuft} style={knopf}
            onClick={() => void fuehreAus('buehne')}>{laeuft === 'buehne' ? 'stellt zu …' : z.grund === 'zuordnung-gescheitert' ? 'erneut zuordnen' : 'Bühne daraus machen'}</button>
          <button type="button" data-ai-id={`guide-platz-beenden-${z.sitzung}`} disabled={!!laeuft} style={knopf}
            onClick={() => void fuehreAus('beenden')}>{laeuft === 'beenden' ? 'beendet …' : z.erneut_beenden ? 'erneut beenden' : 'beenden'}</button>
        </>
      )}
      {ergebnis && (
        <div data-ai-id={`guide-platz-ergebnis-${z.sitzung}`} role="status"
          style={{ marginLeft: 12, color: ergebnis.ok ? 'var(--tn-green)' : 'var(--tn-red)', whiteSpace: 'normal' }}>
          {ergebnis.ok ? '✓' : '⛔'} {ergebnis.satz}
        </div>
      )}
      {z.weckdefekt && (
        <div
          data-ai-id={`guide-weckdefekt-${z.sitzung}`}
          title={z.weckdefekt.nachgesehen.join('\n')}
          style={{ color: 'var(--tn-red)', marginLeft: 12, whiteSpace: 'normal', lineHeight: 1.45 }}
        >
          ⛔ {z.weckdefekt.versuche} Klärungsversuche ohne Lösung — {z.weckdefekt.satz}
        </div>
      )}
    </div>
  );
}

/**
 * „BEENDET OHNE ERGEBNIS-NACHWEIS" (Rafael 09.10.2026, Baustein 6): eine Maschine hat eine Sitzung
 * ohne Platz beendet, ohne zu sagen, wo ihr Ergebnis steht. Steht im Platz-Block, bis jemand die
 * Zeile wegklickt (der Server vermerkt wer/wann). Liest die lokale Route dieses Hauses selbst;
 * ein Lesefehler steht als Zeile da, nicht als leere Liste.
 */
/** „START GESCHEITERT" (Baustein 8): die Bühne steht, ihr Berater nicht. Knopf „erneut starten"
 *  nimmt denselben Weg wie „Berater einsetzen"; das Ergebnis steht an der Zeile. */
export function StartGescheitertBlock({ liste }: { liste?: StartGescheitert[] }) {
  const [laeuft, setLaeuft] = useState<string | null>(null);
  const [ergebnis, setErgebnis] = useState<Record<string, PlatzAktionErgebnis>>({});
  if (!liste?.length) return null;
  const starte = async (b: StartGescheitert) => {
    if (laeuft) return;
    setLaeuft(b.buehne);
    const r = await starteBuehneErneut(b);
    setErgebnis(e => ({ ...e, [b.buehne]: r }));
    setLaeuft(null);
  };
  return (
    <div data-ai-id="guide-start-gescheitert" role="status" style={{ marginTop: 6, marginBottom: 12, padding: '6px 9px', borderRadius: 7, border: '1px solid var(--tn-red)', fontSize: 11 }}>
      <div style={{ color: 'var(--tn-red)', fontWeight: 600 }}>{liste.length === 1 ? '1 Bühne: Start gescheitert' : `${liste.length} Bühnen: Start gescheitert`}</div>
      {liste.map(b => (
        <div key={b.buehne} data-ai-id={`guide-start-gescheitert-${b.buehne}`} style={{ padding: '1px 0' }}>
          · {b.titel}<span style={{ color: 'var(--tn-text-muted)' }}> · vor {dauer(b.am)} · {b.fehler.slice(0, 200)}</span>
          <button type="button" data-ai-id={`guide-start-erneut-${b.buehne}`} disabled={!!laeuft}
            style={{ fontSize: 10.5, padding: '1px 7px', marginLeft: 6 }} onClick={() => void starte(b)}>
            {laeuft === b.buehne ? 'startet …' : 'erneut starten'}</button>
          {ergebnis[b.buehne] && <div style={{ marginLeft: 12, color: ergebnis[b.buehne].ok ? 'var(--tn-green)' : 'var(--tn-red)' }}>
            {ergebnis[b.buehne].ok ? '✓' : '⛔'} {ergebnis[b.buehne].satz}</div>}
        </div>
      ))}
    </div>
  );
}

export function BeendetOhneErgebnisBlock({ hierHaus, onOeffnen }: {
  hierHaus: string | null; onOeffnen: (z: SitzungOhneBerater) => void;
}) {
  /*
   * ABRUFFEHLER SIND KEIN ERGEBNIS (Rafael 25.10.2026: „Fehler: 0 Sitzungen beendet ohne Ergebnis-Nachweis
   * ⛔ Haus partner nicht lesbar: HTTP 502“). Ein nicht erreichbares Haus ist „derzeit nicht verfügbar,
   * zuletzt gemessen …“ — seine letzten gueltigen Zeilen bleiben, als alt gekennzeichnet, stehen; eine leere
   * Liste heisst nie „fehlerfrei“. Die rote Ueberschrift gibt es nur, wenn es wirklich Vermerke gibt. Solange
   * ein Haus fehlt, wird alle 15 s neu gefragt, damit die Erholung sofort sichtbar ist (sonst 60 s).
   */
  const [liste, setListe] = useState<Array<BeendetOhneErgebnis & { altVon?: string }>>([]);
  const [ausfall, setAusfall] = useState<Record<string, { grund: string; zuletzt: string | null }>>({});
  const [fehler, setFehler] = useState<string | null>(null);
  const letzteGueltig = useRef<Record<string, { eintraege: BeendetOhneErgebnis[]; am: string }>>({});
  /* Folgenummer: eine langsamere, aeltere Antwort darf eine neuere nicht ueberschreiben. */
  const folge = useRef(0);
  const [zeilenFehler, setZeilenFehler] = useState<Record<string, string>>({});
  const laden = useCallback(async () => {
    const nr = ++folge.current;
    try {
      const d = await ladeBeendetOhneErgebnis();
      if (nr !== folge.current) return;
      const jetzt = new Date().toISOString();
      for (const h of ['dev', 'partner']) {
        if (!(h in d.fehler)) letzteGueltig.current[h] = { eintraege: d.eintraege.filter(e => e.haus === h), am: jetzt };
      }
      const neuAusfall: Record<string, { grund: string; zuletzt: string | null }> = {};
      const alte: Array<BeendetOhneErgebnis & { altVon?: string }> = [];
      for (const [h, grund] of Object.entries(d.fehler)) {
        const letzt = letzteGueltig.current[h];
        neuAusfall[h] = { grund, zuletzt: letzt?.am ?? null };
        if (letzt) alte.push(...letzt.eintraege.map(e => ({ ...e, altVon: letzt.am })));
      }
      setListe([...d.eintraege, ...alte]);
      setAusfall(neuAusfall);
      setFehler(null);
    }
    catch (e) {
      if (nr !== folge.current) return;
      /* Gesamtausfall: die bisherigen Zeilen bleiben, aber als alter Stand (zuletzt gemessen …). */
      const grund = guideNetzFehler(e);
      const zuletzt = Object.values(letzteGueltig.current).map(x => x.am).sort().pop() ?? null;
      setListe(l => l.map(z => (z.altVon ? z : { ...z, altVon: letzteGueltig.current[z.haus]?.am ?? zuletzt ?? undefined })));
      setFehler(`Status derzeit nicht verfügbar (${grund}) — ${zuletzt ? `zuletzt gemessen ${new Date(zuletzt).toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit' })}` : 'in diesem Fenster noch nie gemessen'}; neuer Versuch alle 15 s.`);
    }
  }, []);
  const gestoert = !!fehler || Object.keys(ausfall).length > 0;
  useEffect(() => {
    void laden();
    const t = setInterval(() => void laden(), gestoert ? 15_000 : 60_000);
    return () => clearInterval(t);
  }, [laden, gestoert]);
  const uhr = (iso: string) => new Date(iso).toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit' });
  const hausWort = (h: string) => (h === 'partner' ? 'Partner' : h === 'dev' ? 'Dev' : h);
  if (!liste.length && !fehler && !Object.keys(ausfall).length) return null;
  const weg = async (haus: string, sid: string) => {
    const r = await klickeBeendetWeg(haus, sid);
    if (!r.ok) { setZeilenFehler(f => ({ ...f, [sid]: r.satz })); return; }
    await laden();
  };
  return (
    <div data-ai-id="guide-beendet-ohne-ergebnis" role="status" style={{ marginTop: 6, marginBottom: 12, padding: '6px 9px', borderRadius: 7, border: `1px solid ${liste.some(z => !z.altVon) ? 'var(--tn-red)' : 'var(--tn-orange)'}`, fontSize: 11 }}>
      {liste.length > 0 && (liste.some(z => !z.altVon) ? (
        <div style={{ color: 'var(--tn-red)', fontWeight: 600 }}>
          {/* Rafael 09.10.2026 12:19Z: nach drei Erinnerungen ein FEHLER, rot bis zum Wegklicken. */}
          {liste.length === 1 ? 'Fehler: 1 Sitzung beendet ohne Ergebnis-Nachweis' : `Fehler: ${liste.length} Sitzungen beendet ohne Ergebnis-Nachweis`}
        </div>
      ) : (
        <div style={{ color: 'var(--tn-orange)', fontWeight: 600 }}>
          {liste.length === 1 ? '1 Vermerk „beendet ohne Ergebnis-Nachweis“ (alter Stand)' : `${liste.length} Vermerke „beendet ohne Ergebnis-Nachweis“ (alter Stand)`}
        </div>
      ))}
      {Object.entries(ausfall).map(([h, a]) => (
        <div key={h} data-ai-id={`guide-beendet-ausfall-${h}`} style={{ color: 'var(--tn-orange)' }}>
          {hausWort(h)}-Status derzeit nicht verfügbar ({a.grund}) — {a.zuletzt ? `zuletzt gemessen ${uhr(a.zuletzt)}` : 'in diesem Fenster noch nie gemessen'}; neuer Versuch alle 15 s.
        </div>
      ))}
      {fehler && <div data-ai-id="guide-beendet-ausfall-gesamt" style={{ color: 'var(--tn-orange)' }}>{fehler}</div>}
      {liste.map(e => (
        <div key={`${e.sitzung}-${e.am}`} data-ai-id={`guide-beendet-ohne-ergebnis-${e.sitzung}`} style={{ padding: '1px 0' }}>
          <button type="button" className="guide-touch-zeile"
            style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', font: 'inherit', cursor: 'pointer', textAlign: 'left' }}
            onClick={() => onOeffnen({ sessionId: e.sitzung, name: e.titel || e.sitzung.slice(0, 8), haus: e.haus, eigenesHaus: hierHaus })}>
            · {e.titel || e.sitzung.slice(0, 8)}
          </button>
          {e.fehler && e.erinnert ? <span style={{ color: 'var(--tn-red)' }}> · {e.erinnert}× erinnert</span> : null}
          <span style={{ color: 'var(--tn-text-muted)' }}> · beendet von {e.von} · vor {dauer(e.am)}{e.haus !== hierHaus ? ` · Haus ${e.haus === 'partner' ? 'Partner' : 'Dev'}` : ''}{e.altVon ? ` · alter Stand von ${uhr(e.altVon)}` : ''}</span>
          {e.grund && <span style={{ color: 'var(--tn-text-muted)' }}> · {e.grund}</span>}
          <button type="button" data-ai-id={`guide-beendet-weg-${e.sitzung}`} style={PLATZ_KNOPF}
            onClick={() => void weg(e.haus, e.sitzung)}>wegklicken</button>
          {zeilenFehler[e.sitzung] && <div style={{ color: 'var(--tn-red)', marginLeft: 12 }}>⛔ {zeilenFehler[e.sitzung]}</div>}
        </div>
      ))}
    </div>
  );
}

/** Unter dieser Zeit seit dem ersten Sichten gilt ein laufender Chat ohne Buehne als „gerade gestartet". */
const FRISCH_MS = 10 * 60_000;
const LS_ERSTGESEHEN = 'cui-guide-ohne-buehne-erstgesehen';
const zuSchluessel = (haus: string) => `cui-guide-al-${haus}-ohne-buehne-zu`;

/** Merkt, seit wann dieser Client einen Chat ohne Buehne sieht — der Server liefert keine Startzeit. */
function erstGesehen(ids: string[]): Record<string, number> {
  let karte: Record<string, number> = {};
  try { karte = JSON.parse(localStorage.getItem(LS_ERSTGESEHEN) ?? '{}') ?? {}; } catch { karte = {}; }
  const jetzt = Date.now();
  const neu: Record<string, number> = {};
  for (const id of ids) neu[id] = typeof karte[id] === 'number' ? karte[id] : jetzt;
  try { localStorage.setItem(LS_ERSTGESEHEN, JSON.stringify(neu)); } catch { /* nur Anzeige */ }
  return neu;
}

/** Wurde die Liste geleert, gilt ein frueheres Zuklappen nicht mehr (siehe `OhneBerater`). */
export function vergissZugeklapptOhneBuehne(haus: string): void {
  try { localStorage.removeItem(zuSchluessel(haus)); } catch { /* egal */ }
}

export function OhneBerater({ abgleich, hierHaus, onOeffnenOhneBerater, kompakt }: {
  abgleich: Abgleich | undefined;
  hierHaus: string | null;
  onOeffnenOhneBerater: (z: SitzungOhneBerater) => void;
  /** „Ziele (neu)“: nur die Titelzeile mit Zaehler; der erklaerende Absatz und die Einzelheiten stehen beim Aufklappen (Inhalt bleibt erreichbar). */
  kompakt?: boolean;
}) {
  const alle = abgleich?.chats_ohne_buehne ?? [];
  const ids = alle.map(z => z.sitzung).sort();
  const idsText = ids.join(',');
  const haus = abgleich?.haus ?? 'dev';
  /*
   * OFFEN, WEIL ES EIN BEFUND IST — Rafael, 03.10.2026 08:41Z: *„chats ohne bühne sollte es
   * ja konstruktionsbedingt nicht geben außer sie haben grad erst im sitzungsaal gestartet"*,
   * und 13.09.: *„Ein Befund, den man aufklappen muss, ist fuer den, der ihn nicht sucht,
   * nicht vorhanden."* Die Zeile steht nur, wenn es welche gibt, und dann aufgeklappt.
   *
   * ZUKLAPPEN VERSTECKT NUR, WAS ER GESEHEN HAT: gespeichert werden die Kennungen, die beim
   * Zuklappen dastanden. Kommt ein neuer Chat dazu — oder war die Liste zwischendurch leer
   * (`vergissZugeklapptOhneBuehne`) —, steht die Meldung wieder offen.
   */
  const [zuBei, setZuBei] = useState<string[] | null>(() => {
    try { const v = JSON.parse(localStorage.getItem(zuSchluessel(haus)) ?? 'null'); return Array.isArray(v) ? v : null; } catch { return null; }
  });
  const offen = zuBei !== null && zuBei.length === 0;
  const erst = useMemo(() => erstGesehen(ids), [idsText]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!abgleich || alle.length === 0) return null;

  const jetzt = Date.now();
  /* Frisch = juenger als 10 min seit dem Start (Server-Feld `gestartet`). Nur wenn der
   * Server keine Startzeit kennt, gilt die Schaetzung: seit wann DIESER Client ihn sieht
   * und wie alt seine aktuelle Taetigkeit ist. */
  const frisch = (z: Abgleich['chats_ohne_buehne'][number]) => {
    if (z.weckdefekt) return false;
    const start = z.gestartet ? Date.parse(z.gestartet) : NaN;
    if (Number.isFinite(start)) return jetzt - start < FRISCH_MS;
    return !z.tot && jetzt - (erst[z.sitzung] ?? jetzt) < FRISCH_MS
      && (!z.seit || jetzt - Date.parse(z.seit) < FRISCH_MS);
  };
  const neue = alle.filter(frisch);
  const alte = alle.filter(z => !frisch(z));
  const ruhend = alte.filter(z => z.tot).length;
  const defekt = alle.filter(z => z.weckdefekt).length;
  const gelb = 'var(--tn-orange)';

  const zeile = (z: Abgleich['chats_ohne_buehne'][number]) => (
    <ChatOhneBuehneZeile key={z.sitzung} z={z} haus={abgleich.haus} hierHaus={hierHaus} onOeffnen={onOeffnenOhneBerater} />
  );
  const umschalten = () => {
    const ziel = offen ? ids : [];
    setZuBei(ziel);
    try { if (ziel) localStorage.setItem(zuSchluessel(haus), JSON.stringify(ziel)); else localStorage.removeItem(zuSchluessel(haus)); } catch { /* nur Anzeige */ }
  };

  return (
    <div data-ai-id="guide-ohne-berater" role="status" style={{
      marginTop: 6, padding: kompakt ? '1px 9px' : '6px 9px', borderRadius: 7,
      border: `1px solid ${alte.length ? gelb : 'var(--tn-border)'}`,
      background: alte.length ? 'rgba(224,175,104,.08)' : 'transparent',
    }}>
      <button type="button" className="cui-collapse-toggle" aria-expanded={offen} onClick={umschalten}
        style={kompakt ? { padding: '2px 0', width: '100%', textAlign: 'left', background: 'transparent', border: 0, boxShadow: 'none' } : { padding: '2px 0' }}>
        <span aria-hidden="true">{offen ? '▾' : '▸'}</span>
        <span className="guide-al-titel" style={{ color: alte.length ? gelb : 'var(--tn-text-muted)', fontWeight: 600 }}>
          {defekt ? `Klärung festgefahren: ${defekt} ${defekt === 1 ? 'Fall' : 'Fälle'}` : 'Interne Zuordnung noch offen'}
          <span style={{ fontWeight: 400 }}> · Zuständig: {haus === 'dev' ? 'Kanzler' : 'Vizekanzler'}{kompakt ? ` · Details (${alle.length})` : ''}</span>
        </span>
      </button>
      {(!kompakt || offen) && <div style={{ fontSize: 12, lineHeight: 1.5 }}>
        {defekt ? 'Nach drei Klärungsversuchen fehlt noch eine Lösung. Der Verantwortliche muss den Grund und den nächsten Schritt klären.' : 'Ergebnisse und Zuständigkeiten müssen zugeordnet werden. Dafür ist keine Entscheidung von dir angefordert.'}
        <span style={{ color: 'var(--tn-text-muted)' }}> Technische Details ({alle.length}) aufklappen.</span>
      </div>}
      <div data-ai-id="guide-raum-sitzungssaal-chats-ohne-buehne" className="guide-karte-meta" hidden={!offen} style={{ fontSize: 11 }}>
        {alte.length > 0 && (
          <div data-ai-id="guide-ohne-berater-alt" style={{ color: gelb, marginTop: 2 }}>
            <strong>Offene Zuordnungen — Ergebnis sichern und zuständig übergeben</strong>
            <div style={{ color: 'var(--tn-text)', lineHeight: 1.5 }}>{alte.map(zeile)}</div>
          </div>
        )}
        {neue.length > 0 && (
          <div data-ai-id="guide-ohne-berater-neu" style={{ color: 'var(--tn-text-muted)', marginTop: 5 }}>
            Gerade gestartet — legt gleich selbst eine Bühne an.
            <div style={{ lineHeight: 1.5 }}>{neue.map(zeile)}</div>
          </div>
        )}
        <div style={{ marginTop: 4, color: 'var(--tn-text-muted)' }}>
          Jede Sitzung braucht genau einen Platz: Amt, Unteraufgabe eines offenen Auftraggebers oder Bühne. Tippen öffnet den Chat.
        </div>
      </div>
    </div>
  );
}

// ── Beendet: das Archiv, ein Knopf in der Statusleiste ────────────────

/**
 * BEENDET — die abgegangenen Berater, nachlesbar mit Grund, Urheber und Bild.
 *
 * Bis 23.09.2026 hing dieser Zugang als Knopf „Beendet · N" an der Liste „Alle Berater"
 * unter dem Tagesplan. Rafael hat die Liste entfernen lassen (*„diese unnötige ALLE berater
 * ansicht ... die braucht niemand, sie zeigt das gleiche wie die karten oben"*). Die
 * Beendeten stehen aber in keinem Raum — ohne diesen Knopf waeren sie unerreichbar.
 * Deshalb steht er jetzt als eigener Eintrag in der Statusleiste, wie „Hinweise".
 *
 * Das Archiv wird erst beim Aufklappen geladen (`alle=1`), der Zaehler kommt schon mit
 * dem schlanken Abruf (`je_haus.gesamt - lebend`).
 */
export function BeendetArchiv({ beendete, anzahl, laedt, onArchiv }: {
  beendete: BuehneAnsicht[];
  anzahl: number;
  laedt: boolean;
  onArchiv: (an: boolean) => void;
}) {
  const gezeigt = [...beendete].sort((x, y) => (y.abschluss?.am ?? '').localeCompare(x.abschluss?.am ?? ''));
  return (
    <details name="guide-status" data-ai-id="guide-sprung-beendet" onToggle={e => onArchiv(e.currentTarget.open)}>
      <summary title="Abgegangene Berater bleiben nachlesbar — mit Grund, Urheber und Bild">Abgeschlossen · {anzahl}</summary>
      <div>
        {/* Der 20-s-Takt laedt neu; die Meldung nur, solange noch nichts da ist. */}
        {laedt && gezeigt.length === 0 && <div role="status" style={{ fontSize: 10.5, color: 'var(--tn-text-muted)' }}>Abgeschlossene Bühnen werden geladen …</div>}
        {!laedt && gezeigt.length === 0 && (
          <div style={{ fontSize: 10.5, color: 'var(--tn-text-muted)' }}>Keine beendeten Bühnen in diesem Haus.</div>
        )}
        {gezeigt.map(b => (
          <div key={b.id} data-ai-id={`guide-beendet-${b.id}`} style={{ fontSize: 10.5, padding: '4px 0', borderBottom: '1px solid var(--tn-border)', opacity: 0.85 }}>
            <div style={{ color: 'var(--tn-text)' }}>{b.titel}</div>
            <div style={{ color: 'var(--tn-text-muted)', fontSize: 10 }}>
              {b.thema} · {b.abschluss?.art === 'abgeloest'
                ? `abgelöst durch ${[b.nachfolger, ...(b.weitere_nachfolger ?? [])].filter(Boolean).join(', ')}`
                : 'erledigt'}
              {' · '}{b.abschluss?.von} · {uhr(b.abschluss?.am)} — {b.abschluss?.grund}
            </div>
            <div style={{ color: b.bild && 'pfad' in b.bild ? 'var(--tn-text-muted)' : 'var(--tn-orange)', fontSize: 10 }}>
              {b.bild
                ? ('pfad' in b.bild ? `Bild: ${b.bild.pfad}` : `ohne Bild: ${b.bild.fehlt}`)
                : 'ohne Bild und ohne Begründung — das darf es nicht geben.'}
            </div>
          </div>
        ))}
      </div>
    </details>
  );
}

// ── Der Umschalter: Dev ODER Partner, ganz oben ──────────────────────

/**
 * DER HAUS-UMSCHALTER — zweiwertig, ganz oben in der Hofansicht.
 *
 * Rafael, 12.09.2026: kein drittes "Beide Häuser" mehr (das war der gemeldete
 * Fehler: beide Häuser mischten sich in seiner Fläche). Vorher lag der Filter
 * unten in "Alle Berater" versteckt — jetzt steht er dort, wo er als erstes
 * gesehen wird, weil er entscheidet, was der ganze Rest der Fläche zeigt.
 */
/**
 * DER HOFSTAAT-SCHALTER — auch hier, wo Rafael die Raeume sieht (Rafael, 12.09.2026:
 * "mach in den guide einen button mit dem der hofstaat ein und ausgeschaltet werden
 * kann - der muss alle von den session wrappers und so umfassen"). Zustand kommt vom
 * Server (GET /api/hofstaat), das Umschalten geht ueber POST /api/hofstaat in beide
 * Haeuser; Semantik in server/lib/hofstaat-schalter.ts. Ein Ladefehler wird als
 * "?!" gezeigt, nie als "an" geraten.
 */
export function HofSchalter() {
  const [zustand, setZustand] = useState<{ an: boolean; laufend: number } | { fehler: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const laden = useCallback(() => {
    fetch(`${API}/hofstaat`, { signal: AbortSignal.timeout(10000) })
      .then(async r => {
        const j = await r.json().catch(() => ({})) as { an?: boolean; laufend?: number; error?: string };
        if (!r.ok || typeof j.an !== 'boolean') throw new Error(j.error || `HTTP ${r.status}`);
        setZustand({ an: j.an, laufend: j.laufend ?? 0 });
      })
      .catch((e: Error) => setZustand({ fehler: e.message }));
  }, []);
  useEffect(() => { laden(); const t = setInterval(laden, 30_000); return () => clearInterval(t); }, [laden]);
  const umschalten = () => {
    if (!zustand || 'fehler' in zustand || busy) return;
    const ziel = !zustand.an;
    const frage = ziel
      ? 'Hofstaat EINSCHALTEN? Danach sind Maschinen-Starts und Sitzungen im Koordinations-Workspace wieder erlaubt (beide Haeuser). Der Versorger bleibt aus, bis du ihn einschaltest.'
      : `Hofstaat AUSSCHALTEN? Beendet alle ${zustand.laufend} Hof-Sitzungen, stoppt Wachen und Versorger, sperrt jeden Maschinen-Start — in beiden Haeusern.`;
    if (!window.confirm(frage)) return;
    setBusy(true);
    fetch(`${API}/hofstaat`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ an: ziel, beideHaeuser: true, grund: 'Guide-Knopf' }) })
      .then(async r => { const j = await r.json().catch(() => ({})) as { error?: string }; if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`); })
      .catch((e: Error) => window.alert(`Hofstaat-Schalter: ${e.message}`))
      .finally(() => { setBusy(false); laden(); });
  };
  /* SELBSTERKLAEREND (Rafael, 03.10.2026 17:16: „Mach es auch mehr selbsterklärend"): „Hofstaat
     AN (1)" sagte einem Aussenstehenden nichts. Sichtbar steht jetzt, WAS da an ist — die
     Automatik im Hintergrund — und die Zahl als „läuft"; der Fachbegriff steht im Tooltip. */
  const text = busy ? 'Automatik …' : !zustand ? 'Automatik ?' : 'fehler' in zustand ? 'Automatik ?!'
    : zustand.an ? <>Automatik an<span className="guide-kopf-wort"> · {zustand.laufend} läuft</span></> : 'Automatik aus';
  const titel = zustand && !('fehler' in zustand)
    ? `Hintergrund-Automatik (Hofstaat: Assistent, Wachen, Versorger) ist ${zustand.an ? 'AN' : 'AUS'} — ${zustand.laufend} Hintergrund-Sitzung(en) laufen. Klick schaltet sie ${zustand.an ? 'aus' : 'ein'} (mit Rückfrage), in beiden Servern.`
    : `Zustand der Hintergrund-Automatik nicht lesbar: ${zustand && 'fehler' in zustand ? zustand.fehler : 'lade …'}`;
  return (
    <Chip id="guide-hofstaat-schalter" an={!!zustand && !('fehler' in zustand) && zustand.an} onClick={umschalten} ton={zustand && !('fehler' in zustand) && zustand.an ? 'warn' : undefined} titel={titel}>
      {text}
    </Chip>
  );
}

/**
 * DER PARTNER WAEHLT KEIN HAUS — Rafael, 22.09.2026, woertlich: *„Der Stil sollte ja
 * gleich sein, nur dass er halt nicht zwischen Dev und Partner waehlen kann. Da
 * brauche ich nicht zwei Stile."*
 *
 * Derselbe Guide, dieselbe Darstellung — nur ohne diese Zeile. Weg faellt damit auch
 * der Hofstaat-Schalter, der daneben steht: der beendet Sitzungen und sperrt
 * Maschinen-Starts in BEIDEN Haeusern und ist ein Betriebsweg des Eigentuemers,
 * kein Teil der Arbeit eines Partners.
 *
 * Und der Filter steht fuer ihn fest auf dem eigenen Haus. Das ist nicht nur Kosmetik:
 * solange er nicht umschalten kann, fragt seine Oberflaeche gar nicht erst nach dem
 * Bestand des anderen Hauses.
 */
/*
 * SEIT DER ARBEITSLISTE (03.10.2026) STEHT DER UMSCHALTER IN DER KOPFZEILE DER SPALTE
 * (GuidePanel), links; der Hofstaat-Schalter steht rechts daneben. Dieselbe Regel wie
 * oben: ein Partner sieht beides nicht.
 */
export function HausUmschalter() {
  const { user, authEnabled } = useAuth();
  const hausfilter = useGuideHausfilter();
  const istBetreiber = !authEnabled || user?.role === 'admin';
  if (!istBetreiber) return null;
  const haus = hausfilter ?? 'dev';
  return (
    /* EIN SEGMENT STATT ZWEI CHIPS (Rafael, 03.10.2026: Guide-Kopf kompakter) — zwei
       Haelften in einem Rahmen, die gewaehlte gefuellt. Weniger Breite, und man sieht auf
       einen Blick, dass es EINE Wahl zwischen zweien ist. */
    /* SELBSTERKLAEREND (03.10.2026 17:16): „Dev | Partner" allein liess offen, WOVON. Davor
       steht „Server", wo Platz ist (responsive.css), und jeder Knopf sagt im Tooltip, was er zeigt. */
    <div data-ai-id="guide-haus-umschalter" role="group" aria-label="Bühnen welches Servers zeigen" className="guide-segment">
      <span className="guide-segment-etikett" aria-hidden="true">Server</span>
      {(['dev', 'partner'] as const).map(h => {
        const satz = h === 'dev' ? 'Bühnen vom Dev-Server zeigen (Haupthaus: Entwicklung und Betrieb)'
          : 'Bühnen vom Partner-Server zeigen (dort arbeiten die Partner, z. B. Kurt und Sahori)';
        return <button key={h} type="button" data-ai-id={`guide-haus-umschalter-${h}`} aria-pressed={haus === h}
          title={satz} aria-label={satz}
          onClick={() => setzeGuideHausfilter(h)}>{h === 'dev' ? 'Dev' : 'Partner'}</button>;
      })}
    </div>
  );
}

// ── Die Ansicht ───────────────────────────────────────────────────────

export function Hofansicht({ onOeffnen, offeneId, aemter, onOeffnenOhneBerater, statusInfo, standZeile, onBestand, mehrZiel, onWarnung }: {
  onBestand?: (b: BuehneAnsicht[], hierHaus: 'dev' | 'partner' | null) => void;
  /**
   * DAS „⋯ MEHR"-MENUE DER KOPFZEILE (Arbeitsliste, Rafaels Wahl a, 03.10.2026). Die
   * Betriebsaufklapper (Assistent, Hinweise, Beendet, Heute, CUI-Stand, Info) und die
   * Farblegende werden dorthin portiert. `undefined` = kein Menue vorhanden, die Leiste
   * steht wie frueher inline; `null` = Menue noch nicht montiert.
   */
  mehrZiel?: HTMLElement | null;
  /** Meldet, ob im Menue etwas Warnwuerdiges liegt (Farbe des Punkts am „⋯"), sonst null. */
  onWarnung?: (farbe: string | null) => void;
  statusInfo?: ReactNode;
  /**
   * CUI-Stand beider Haeuser — bewusst NICHT im `statusInfo`-Toggle versteckt.
   *
   * Rafael, 19.09.2026: Dev lief auf dem Stand von heute frueh, Partner auf dem vom
   * Vortag, und niemand hat es gesehen, bis ein Betriebs-Check es zufaellig meldete.
   * Die Daten und das Urteil gab es schon (`StandDerHaeuser`/`standVonHaus`,
   * 1e26adba8) — nur stand die Zeile hinter einem Klick auf "Info". Genau das war die
   * Luecke: eine Anzeige, die man erst suchen muss, ist keine Anzeige.
   */
  standZeile?: ReactNode;
  onOeffnen: (b: BuehneAnsicht) => void;
  offeneId: string | null;
  /**
   * Die Sitzungen der AEMTER (Kanzler, Vizekanzler, Assistent) aus `board.konfig`.
   * Sie haben per Definition keine Buehne und zaehlen deshalb nicht als Befund.
   * Als Eingabe von aussen, weil das Board ohnehin in der Spalte geladen wird — ein
   * zweiter Abruf waere eine zweite, spaeter abweichende Wahrheit.
   */
  aemter: string[] | null;
  /** Regel 6, zweite Haelfte: der Klick auf eine Sitzung ohne Berater. */
  onOeffnenOhneBerater: (z: SitzungOhneBerater) => void;
}) {
  /* Wer ist das? Ein Partner bekommt dieselbe Oberflaeche, aber ohne die
   * Betriebsdiagnostik des Eigentuemers (Rafael, 22.09.2026: „Weglassen, was ihm
   * nicht gehoert"). */
  const { user: nutzerHier, authEnabled: anmeldungAn } = useAuth();
  const istBetreiberHier = !anmeldungAn || nutzerHier?.role === 'admin';
  const hausfilter = useGuideHausfilter();
  const { jobs: sendeAuftraege } = useGuideSend();
  const [mitBeendeten, setMitBeendeten] = useState(false);
  const archivFilter = useGuideAnsichtSteuerung().filter === 'abgeschlossen';
  useEffect(() => { if (archivFilter) setMitBeendeten(true); }, [archivFilter]);
  /* ANSICHT DER BUEHNENLISTE (Rafael, 24.10.2026: „standardmaessig soll es fuer alle im alten Modus bleiben ausser bei mir“): Vorgabe „Räume (bisher)“;
     nur der Betreiber (Rafael) startet in „Ziele (neu)“. Jeder kann umschalten; die Wahl und der Zustand des Hinweises gelten fuer die geoeffnete Ansicht
     (Speicher des Skripts je Nutzerkennung, siehe `ansichtSitzung`); ueber ein Neuladen hinaus merkt sie `useUiPreferences` (nur die zwei kleinen Werte, siehe
     lib/guide-ansicht-merk), nicht der Server (der Nutzerspeicher ersetzt bei jeder Aenderung den ganzen Stand). */
  const sitzungsNutzer = nutzerHier?.id ?? '__ohne_anmeldung__';
  /* Gemerkt ueber `useUiPreferences` (lib/useGuideAnsichtSitzung): Hinweis gesehen und die bewusst gewaehlte Ansicht, je Nutzerkennung. */
  const { sitzung, ansicht, setAnsicht, hinweisEreignis } = useGuideAnsichtSitzung(sitzungsNutzer, istBetreiberHier);
  /* Schnellweg: die Ansicht, die dieses Fenster zeigt, wird gemeldet; ein Wunsch aus dem Dock (Knopf/Kurzbefehl/Klick auf den Assistenten) wechselt sie — nur im eigenen Fenster. */
  useAnsichtKopplung(ansicht, setAnsicht, sitzungsNutzer);
  const [archivLaedt, setArchivLaedt] = useState(false);
  const ladeGeneration = useRef(0);
  const laufenderAbruf = useRef<AbortController | null>(null);
  const wiederholung = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fehlversuche = useRef(0);
  /** Wann eine Karte wegen eines quittierten Abschluss-Klicks verschoben wurde —
   *  siehe die Geduldsgrenze bei `geschlossenUnterwegs`. */
  const verstecktSeit = useRef(new Map<string, number>());
  /** Karten, die Rafaels eigene Eingabe schon in ihren Zielraum gezogen hat — siehe
   *  guide-antwort-sofort.ts. */
  const antwortVerschoben = useRef(new Map<string, Verschoben>());
  /** Nur damit die Geduldsgrenze von selbst ablaeuft: ohne diesen Takt kaeme eine
   *  haengengebliebene Karte erst beim naechsten Abruf zurueck. */
  const [, takt] = useState(0);
  useEffect(() => {
    if (!verstecktSeit.current.size && !antwortVerschoben.current.size) return;
    const t = setInterval(() => takt(n => n + 1), 5_000);
    return () => clearInterval(t);
  });
  const [letzterStand, setLetzterStand] = useState<Date | null>(null);
  const [alle, setAlle] = useState<BuehneAnsicht[] | null>(null);
  const [hierHaus, setHierHaus] = useState<'dev' | 'partner' | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  /* Die Zaehlung je Haus, allein wegen `eins_zu_eins` — der gemessene Verstoss gegen
   * "eine Buehne, ein Berater". Er stand seit dem 12.09. in der Antwort und wurde
   * nirgends gezeigt (Rafael, 13.09. 07:12:27Z: "da muss das cui sonst fehler
   * ausgeben oder im guide anzeigen damit der assistent das problem erkennt"). */
  const [jeHaus, setJeHaus] = useState<Record<string, { eins_zu_eins?: EinsZuEins; gesamt?: number; lebend?: number; invarianten?: InvariantenVerstoss[] }> | null>(null);
  /* Der Zaehlabgleich gilt fuer das Haus, das GEANTWORTET hat — die Sitzungsliste des
   * anderen Hauses liegt nicht auf diesem Rechner. Deshalb ein eigener Zustand statt
   * eines Eintrags in `jeHaus`: das waere die Behauptung, es gaebe ihn fuer beide. */
  const [abgleich, setAbgleich] = useState<Abgleich | null>(null);

  /*
   * ── Stapel-Abschluss (siehe StapelLeiste) — Zustand liegt hier, weil ein Nachladen
   * nach dem Absenden den ganzen Hof betrifft, nicht nur den Kasten.
   *
   * ER HING BIS ZUM 13.09.2026 AM SITZUNGSSAAL, und das war falsch herum. Rafael dazu
   * (13.09. 06:2xZ): *"Stapel-Abschluss fuer den Sitzungssaal - braucht niemand -
   * versteh auch nicht wieso im Sitzungssaal - wenn dann im Pausenraum oder im
   * Warteraum oder? Im Sitzungssaal sind ja nur welche die wirklich arbeiten?"*
   *
   * Er hat recht: der Sitzungssaal ist der Raum, in dem gearbeitet WIRD. Dort ein
   * Sammel-"als erledigt melden" anzubieten heisst, laufende Arbeit im Dutzend
   * abzuraeumen — das Gegenteil dessen, wofuer der Raum da ist.
   *
   * ER STEHT JETZT IM PAUSENRAUM, nicht im Warteraum: der Pausenraum haelt das
   * Zurueckgestellte, und genau dort ist "das brauchen wir nicht mehr" der normale
   * Ausgang. Im Warteraum arbeitet ein Berater gerade die letzte Antwort ein
   * (deshalb ist er gesperrt) — ihn im Stapel zu schliessen waere derselbe Fehler
   * eine Tuer weiter.
   *
   * NICHT GELOESCHT, obwohl "braucht niemand" das nahelegt: Rafaels Satz nennt im
   * selben Atemzug zwei Raeume, in denen er ihn sich vorstellen koennte. Ein Wort von
   * ihm, und die Leiste ist weg — eine geloeschte Funktion wieder aufzubauen ist
   * teurer als eine, die am richtigen Ort steht und nicht benutzt wird.
   */
  const [saalAuswahl, setSaalAuswahl] = useState<Set<string>>(new Set());
  const [saalGrund, setSaalGrund] = useState('');
  const [saalSendet, setSaalSendet] = useState(false);
  const [saalErgebnis, setSaalErgebnis] = useState<{ ok: number; fehler: string[] } | null>(null);
  const saalToggle = useCallback((id: string) => {
    setSaalAuswahl(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }, []);

  const laden = useCallback(async () => {
    // Polling und Live-Ereignisse teilen sich einen Abruf.
    if (laufenderAbruf.current) return;
    if (wiederholung.current) clearTimeout(wiederholung.current);
    const controller = new AbortController();
    laufenderAbruf.current = controller;
    /* Mit Grund abbrechen (31.10.2026): ohne ihn meldet fetch „signal is aborted without reason“, und die Zeile
     * stand als „Verbindung unterbrochen“ da, obwohl der Server nur langsam war. */
    const timeout = setTimeout(() => controller.abort(new DOMException('keine Antwort in 15 s', 'TimeoutError')), 15000);
    const generation = ++ladeGeneration.current;
    setArchivLaedt(mitBeendeten);
    try {
      // Das Archiv erst nach dem Aufklappen laden; die Zähler kommen bereits mit.
      // `schlank=1`: der Hof liest aus `verlauf`/`nachrichten` nur Kurzformen (siehe
      // GuideHof unten) — die volle Buchhaltung holt sich erst die geoeffnete Buehne
      // selbst (`GET /guide/buehnen/:id`). Gemessen 17.09.2026: ~2,87 MB -> ~0,2 MB.
      // Hintergrundabfrage: Rafaels Eingaben gehen vor (siehe mitVorrang, GuideSendQueue.ts).
      const r = await fetch(`${API}/guide/buehnen?schlank=1${mitBeendeten ? "&alle=1" : ""}`, { signal: controller.signal, priority: 'low' });
      if (!r.ok) throw new GuideHttpFehler(r.status);
      const d = await r.json() as { buehnen?: BuehneAnsicht[]; hier_haus?: 'dev' | 'partner';
        je_haus?: Record<string, { eins_zu_eins?: EinsZuEins; gesamt?: number; lebend?: number; invarianten?: InvariantenVerstoss[] }>; abgleich?: Abgleich };
      if (generation !== ladeGeneration.current) return;
      if (!Array.isArray(d.buehnen)) throw new Error('Antwort ohne Feld "buehnen"');
      setAlle(d.buehnen);
      setLetzterStand(new Date());
      fehlversuche.current = 0;
      setJeHaus(d.je_haus ?? null);
      setAbgleich(d.abgleich ?? null);
      if (d.hier_haus === 'dev' || d.hier_haus === 'partner') setHierHaus(d.hier_haus);
      setFehler(null);
    } catch (e) {
      if (generation !== ladeGeneration.current) return;
      // Keine leere Liste bei einem Fehler: "kein Berater" und "nicht gefragt" sind
      // zwei verschiedene Aussagen, und die erste ist die beunruhigende.
      const roh = e instanceof Error || e instanceof DOMException ? e.message : String(e);
      setFehler(`${guideNetzFehler(e)} (${roh})`);
      const pause = Math.min(3000 * 2 ** Math.min(fehlversuche.current++, 3), 20000);
      wiederholung.current = setTimeout(() => void laden(), pause);
    } finally {
      clearTimeout(timeout);
      if (generation === ladeGeneration.current) {
        laufenderAbruf.current = null;
        setArchivLaedt(false);
      }
    }
  }, [mitBeendeten, istBetreiberHier, hausfilter]);
  useEffect(() => { void laden(); }, [offeneId, laden]);
  useEffect(() => {
    void laden();
    const t = setInterval(() => void laden(), 20000);
    const online = () => void laden();
    window.addEventListener('online', online);
    return () => {
      clearInterval(t);
      if (wiederholung.current) clearTimeout(wiederholung.current);
      ++ladeGeneration.current;
      laufenderAbruf.current?.abort();
      laufenderAbruf.current = null;
      window.removeEventListener('online', online);
    };
  }, [laden]);

  /**
   * DER STAPEL SELBST: EIN Klick, mehrere `POST /:id/abschluss`.
   *
   * SEQUENTIELL, NICHT PARALLEL: `bildVomAbgang` (Server) fotografiert vor jedem
   * Abgang den letzten Stand der jeweiligen Buehne — mehrere gleichzeitige
   * Aufnahmen ueber denselben Browser-Kanal wuerden sich gegenseitig die Kacheln
   * umlenken (siehe koordination/CLAUDE.md: "Ein Browser-Panel ... ist nie
   * meiner"). Ein Fehlschlag bei einer Buehne bricht die anderen nicht ab —
   * "so viel wie sauber geht", nicht alles oder nichts.
   *
   * `von: 'rafael'` ist hier so hart codiert wie ueberall sonst in dieser Datei
   * (Antwort, Notiz, freies Wort): dieser Knopf steht nur in RAFAELS eigenem
   * Guide, sein Klick IST die Erklaerung. Der Server prueft das zusaetzlich
   * gegen die echte Anmeldung (`wirklichRafael`) bei jedem einzelnen Aufruf.
   */
  const saalAbsenden = useCallback(async () => {
    if (saalSendet || saalAuswahl.size === 0 || !saalGrund.trim()) return;
    setSaalSendet(true);
    setSaalErgebnis(null);
    const ids = [...saalAuswahl];
    const grund = saalGrund.trim();
    const fehlgeschlagen: string[] = [];
    let ok = 0;
    for (const id of ids) {
      try {
        const r = await fetch(`${API}/guide/buehnen/${encodeURIComponent(id)}/abschluss`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ art: 'erledigt', von: 'rafael', grund }),
          signal: AbortSignal.timeout(90000),
        });
        const d: unknown = await r.json().catch(() => null);
        if (!r.ok) {
          const nachricht = (d && typeof d === 'object' && 'error' in d) ? String((d as { error: unknown }).error) : `HTTP ${r.status}`;
          throw new Error(nachricht);
        }
        ok += 1;
      } catch (e) {
        fehlgeschlagen.push(`${id}: ${e instanceof Error ? e.message : String(e)}`);
      }
    }
    setSaalErgebnis({ ok, fehler: fehlgeschlagen });
    setSaalAuswahl(new Set());
    setSaalGrund('');
    setSaalSendet(false);
    void laden();
  }, [saalAuswahl, saalGrund, saalSendet, laden]);

  /*
   * SOFORT-NACHLADEN BEIM VORZIEHEN, statt bis zu 20s auf den Poll zu warten.
   * Der Kanzler/Vizekanzler zieht eine Buehne aus dem Vorrat vor den Koenig, und genau
   * dieser Moment soll ohne Wartezeit sichtbar werden — sonst wirkt "vorziehen" wie
   * nichts passiert waere, bis der naechste Tick kommt. `guide-updated` faengt der
   * generische Poll ohnehin schon mit ab; er wird hier nochmal mitgehoert, weil es
   * derselbe Kanal ist und ein zweites Ereignis fuer denselben Zustand keinen Schaden
   * anrichtet (laden() ist idempotent).
   */
  useLiveChannel({
    label: 'Guide-Hof',
    onMessage: (msg: any) => {
      if (msg?.type === 'guide-buehne-vorgezogen' || msg?.type === 'guide-updated') void laden();
    },
    onResync: laden,
  });

  // Noch nie umgeschaltet (hausfilter === null): das eigene Haus ist die einzig
  // sinnvolle Vorgabe. Sobald der erste Abruf da ist, wird die Wahl explizit
  // gemacht und gespeichert — von da an entscheidet nur noch der Umschalter.
  useEffect(() => {
    if (hausfilter === null && hierHaus !== null) setzeGuideHausfilter(hierHaus);
  }, [hausfilter, hierHaus]);

  useEffect(() => { if (alle) onBestand?.(teileNachEigentum(alle.filter(b => b.haus === (hausfilter ?? hierHaus ?? 'dev') || !!b.geteilt_von), istBetreiberHier).eigene.sort((a,b) => nachVorlageRang(a, b) || seitImRaum(a).localeCompare(seitImRaum(b))), hierHaus); }, [alle, hausfilter, hierHaus, onBestand, istBetreiberHier]);

  /* Der Punkt am „⋯": dieselben Farben, die die Zeile „Hinweise" im Menue traegt. */
  const warnHaus = hausfilter ?? hierHaus ?? 'dev';
  const warnFarbe = jeHaus?.[warnHaus]?.invarianten?.length ? 'var(--tn-rosa)'
    : (abgleich?.haus === warnHaus && abgleich.chats_ohne_buehne.some(z => z.weckdefekt)) ? 'var(--tn-orange)' : null;
  useEffect(() => { onWarnung?.(warnFarbe); }, [warnFarbe, onWarnung]);
  /* Leere Liste: ein frueheres Zuklappen der Zeile „Chats ohne Bühne" gilt nicht mehr. */
  const ohneBuehneLeer = abgleich?.haus === warnHaus && abgleich.chats_ohne_buehne.length === 0;
  useEffect(() => { if (ohneBuehneLeer) vergissZugeklapptOhneBuehne(warnHaus); }, [ohneBuehneLeer, warnHaus]);

  const verbindungshinweis = fehler ? (
    <div data-ai-id="guide-buehnen-fehler" role="status" style={{ fontSize: 11, color: 'var(--tn-text-muted)', marginBottom: 8 }}>
      {/* Ehrlich benennen, was passiert ist: ein Zeitablauf unter Last ist keine unterbrochene Verbindung (31.10.2026). */}
      {alle ? `${fehler.startsWith('Antwort dauert zu lange') ? 'Server antwortet gerade langsam' : fehler.startsWith('Server antwortet mit HTTP') ? 'Server meldet einen Fehler' : 'Verbindung unterbrochen'} · letzter Stand ${letzterStand?.toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit' })}.` : fehler.startsWith('Antwort dauert zu lange') ? 'Die Bühnen antworten gerade zu langsam.' : fehler.startsWith('Server antwortet mit HTTP') ? 'Der Server meldet beim Lesen der Bühnen einen Fehler.' : 'Die Bühnen sind gerade nicht erreichbar.'}
      {' '}Der Abruf wird automatisch wiederholt.
      <button type="button" onClick={() => void laden()} style={{ marginLeft: 6 }}>Erneut versuchen</button>
      <details><summary>Technische Details</summary><div style={{ overflowWrap: 'anywhere' }}>{fehler}</div></details>
    </div>
  ) : null;
  if (!alle) return verbindungshinweis ?? <div style={{ color: 'var(--tn-text-muted)', fontSize: 11, marginBottom: 8 }}>Der Hof wird gelesen …</div>;

  const effektivesHaus = hausfilter ?? hierHaus ?? 'dev';
  // Partner-Buehnen stehen nicht in Rafaels Hof — kein Raum, keine Zaehlung (28./30.09.2026).
  // Warum die Flaeche das selbst prueft: guide-partner-eigentum.ts.
  /* Eine MIT DIR GETEILTE Buehne steht in deiner Liste, egal in welchem Haus sie gefuehrt wird (Rafael
   * 30.10.2026: „wie eigene buehnen links im guide"). Rafaels Dev-Buehne, mit Mo im Haus Partner geteilt,
   * fiel sonst am Hausfilter durch. */
  const imHaus = teileNachEigentum(alle.filter(b => b.haus === effektivesHaus || !!b.geteilt_von), istBetreiberHier).eigene;
  /*
   * JEDE OFFENE BUEHNE STEHT IN GENAU EINEM RAUM UND IST DORT SICHTBAR — Rafael,
   * 15.09.2026, dreimal an einem Abend: „ist es wirklich so schwer dass alle buehnen in
   * einem raum sind? im richtigen raum?" Bis 15.09. stand hier zusaetzlich
   * `!sendeQueue.isHidden(b)` und ein Filter auf laufende Abschluss-Auftraege: eine
   * Buehne verschwand aus den Raeumen, sobald Rafaels Eingabe unterwegs war — und
   * blieb verschwunden, wenn die Eingabe im Sendespeicher haengen blieb (Verbindung
   * abgebrochen, Server-Auftrag blockiert: b-works-app-anlage, Antwort vom 14.09. nach
   * drei Versuchen `blocked`) oder der spaetere Abgleich mit dem Serverstand nicht
   * mehr passte (`confirmed`). Gemessen 15.09. 19:13Z: 19 offene Buehnen im Haus Dev,
   * 12 in den Raumkoepfen; sieben lagen in keinem Raum.
   *
   * Der Sendestatus ist eine MARKE AN DER ZEILE (`sendung` in `RaumZeile`), kein
   * Raum und kein Filter. Der Server bleibt die einzige Wahrheit darueber, ob eine
   * Buehne offen ist. Pruefsatz des Kanzlers: Summe der Raumkoepfe == offene Buehnen.
   */
  const sendungen = new Map(sendeAuftraege.filter(j => j.stage.haus === effektivesHaus || !j.stage.haus).map(j => [j.stage.id, j] as const));
  /*
   * RAFAELS KLICK VERSCHIEBT DIE KARTE SOFORT (21.09.2026) — der Absatz darueber gilt
   * unveraendert fuer Antworten und Raumzuege, NICHT mehr fuer seinen Abschluss-Klick,
   * sobald der Server ihn mit `gespeichert: true` quittiert hat.
   *
   * Warum das nicht der Filter vom 15.09. ist (Serverquittung, nie ein blockierter,
   * Geduldsgrenze) und was er damals angerichtet hat: `guide-abschluss-sofort.ts`.
   *
   * PREIS, offen benannt: waehrend dieses Fensters ist die Summe der Raumkoepfe um die
   * verschobenen Karten kleiner als die Zahl der offenen Buehnen am Server — der
   * Pruefsatz des Kanzlers aus dem Absatz darueber gilt dann kurzzeitig nicht. Das ist
   * gewollt und zeitlich begrenzt; Rafael hat die sofortige Verschiebung ausdruecklich
   * verlangt.
   */
  const geschlossenUnterwegs = verschobeneAbschluesse(sendungen, verstecktSeit.current, Date.now());
  /*
   * RAFAELS ANTWORT RAEUMT "VOR DIR" SOFORT (23.09.2026) — dieselbe Idee wie der Abschluss
   * darueber, aber die Karte verlaesst keinen Raum: sie steht sofort dort, wo der Server
   * sie beim Eingang dieser Eingabe hinsetzt. Warum und wann sie zurueckgeht:
   * guide-antwort-sofort.ts.
   */
  merkeEingaben(sendungen, antwortVerschoben.current, Date.now());
  const lebend = imHaus.filter(b => !b.abschluss && !geschlossenUnterwegs(b.id)).map(b => {
    const raum = lokalerRaum(b, seitImRaum(b), antwortVerschoben.current, Date.now());
    return raum === b.raum ? b : { ...b, raum, raum_gesetzt: raum, hauptstatus:'in_arbeit' as const, hauptstatus_text:'In Arbeit', arbeitsinformation:'Deine gespeicherte Antwort wird eingearbeitet' };
  });
  /*
   * NEUESTE REGUNG OBEN — in den vier hinteren Raeumen.
   *
   * Bis 12.09. wurde ueberall aufsteigend nach `seitImRaum` sortiert: die aelteste
   * Zeile stand oben. Genau das hat Rafael um 04:51Z beanstandet. Gemessen 04:55Z im
   * Sitzungssaal: oben 11,0 h und 10,9 h, waehrend 13 der 17 Buehnen juenger als 0,7 h
   * waren und unten standen.
   *
   * "VOR DIR" BLEIBT UMGEKEHRT, und das ist kein Versehen: die erste Reihe ist nicht
   * die Neuigkeitenliste, sondern seine eigene Schuld. Was dort am laengsten wartet,
   * gehoert nach oben — sonst sinkt genau das weg, was ohnehin schon liegen bleibt
   * (die aelteste Zeile dort war am 12.09. eine Post-Buehne mit acht Tagen).
   */
  const jeRaum = (raum: string) => lebend.filter(b => b.raum === raum)
    .sort((a, b) => letzteRegung(b).localeCompare(letzteRegung(a)));
  /*
   * „VOR MIR" = vor_dem_koenig UND vorraum (Rafael, 23.09.2026, siehe RAUM_WORT). Erst
   * was vor dem Koenig steht (aelteste zuerst, siehe oben), dahinter der Vorrat — in
   * derselben Reihe, mit derselben Karte und einer dezenten Marke „Vorrat".
   */
  /*
   * EIN WARTERAUM OBEN (Rafael 29.09.2026 07:07Z: „wir können Vor mir und warteraum
   * zusammenlegen“; 07:09Z: „der block oben "vor mir" sowohl warte- als auch vor mir
   * [umfasst] - dann ist unten mehr platz für den sitzungsaal und den pausenraum und oben
   * mehr platz für die einzelnen bühnen in der kurzanzeige“). Fragen an ihn zuerst, jeweils
   * die am laengsten wartende vorn; die alten Werte vor_dem_koenig/vorraum zaehlen mit.
   */
  const imWarteraum = (b: BuehneAnsicht) => b.raum === 'warteraum' || b.raum === 'vor_dem_koenig' || b.raum === 'vorraum';
  /* Innerhalb der Fragen-Gruppe zuerst der Vorlagerang des Amts (BUEH1, 11.10.2026) — dieselbe
   * Reihe, die `naechste-vorziehen` am Server waehlt. */
  const ersteReihe = lebend.filter(istAktiveVorlage)
    .sort((a, b) => Number(fragenAnDich(b) > 0) - Number(fragenAnDich(a) > 0) || nachVorlageRang(a, b) || seitImRaum(a).localeCompare(seitImRaum(b)));
  const mitFrage = ersteReihe.filter(b => (b.offen?.entscheidungen ?? 0) > 0).length;
  const mitVorschlag = ersteReihe.filter(b => (b.vorschlaege ?? []).some(istOffenerVorschlag)).length;

  /*
   * DIE BETRIEBSLEISTE WOHNT IM „⋯"-MENUE (Arbeitsliste, 03.10.2026). Bis hier stand sie
   * als Zeile mit sieben Aufklappern ueber den Buehnen, und ein aufgeklappter Kasten
   * (Assistent) legte sich UEBER die Karten — die waren dann nicht mehr erreichbar. Im
   * Menue klappen die Eintraege im normalen Fluss auf und ueberdecken nichts.
   *
   * DIE FARBLEGENDE GEHT MIT (Rafael 28.09.2026 wollte sie „unten" — siehe
   * `ZustandsLegende`). Auf der Arbeitsliste stand sie dauerhaft und kostete eine Zeile;
   * jetzt steht sie im Menue, und der Wortlaut des Zustands ist an jeder Karte per ⓘ
   * erreichbar statt nur per Maus.
   */
  const statusleiste = (
    <div className={`guide-statusleiste${mehrZiel !== undefined ? ' guide-statusleiste--menue' : ''}`} data-ai-id="guide-statusleiste" onKeyDown={e => {
      if (e.key === 'Escape') e.currentTarget.querySelectorAll('details[open]').forEach(d => d.removeAttribute('open'));
    }}>
      {/* Der Sammler ist ein Betriebsweg des Eigentuemers; sein Zustand sagt einem
          Partner nichts und stand bei ihm als „Status unbekannt" (Rafael, 22.09.2026). */}
      {istBetreiberHier && <AssistentStatus haus={effektivesHaus} kompakt />}
      <details name="guide-status" data-ai-id="guide-status-hinweise">
        <summary style={{ color: jeHaus?.[effektivesHaus]?.invarianten?.length ? 'var(--tn-rosa)' : (abgleich?.haus === effektivesHaus && abgleich.chats_ohne_buehne.length) ? 'var(--tn-orange)' : 'var(--tn-text-muted)' }}>
          Hinweise{jeHaus?.[effektivesHaus]?.invarianten?.length ? ` · Zielbild verletzt ${jeHaus?.[effektivesHaus]?.invarianten?.length}` : ''}{abgleich?.haus === effektivesHaus && abgleich.chats_ohne_buehne.length ? ` · ${abgleich.chats_ohne_buehne.length}` : ''}
        </summary>
        <div>
          <InvariantenBand verstoesse={jeHaus?.[effektivesHaus]?.invarianten} />
          <EinsZuEinsBand befund={jeHaus?.[effektivesHaus]?.eins_zu_eins} />
          {abgleich?.haus === effektivesHaus && <AbgleichZeile abgleich={abgleich} />}
        </div>
      </details>
      <BeendetArchiv beendete={imHaus.filter(b => b.abschluss)} laedt={archivLaedt} onArchiv={setMitBeendeten}
        anzahl={Math.max(0, (jeHaus?.[effektivesHaus]?.gesamt ?? imHaus.length) - (jeHaus?.[effektivesHaus]?.lebend ?? imHaus.filter(b => !b.abschluss).length))} />
      <Zeitstrahl buehnen={imHaus} />
      {standZeile}
      {statusInfo && <details name="guide-status"><summary>Info</summary><div>{statusInfo}</div></details>}
    </div>
  );
  const sitzungssaal = lebend.filter(istImSitzungssaal).sort((a, b) => letzteRegung(b).localeCompare(letzteRegung(a)));
  const pausenraum = jeRaum('pausenraum');

  return (
    <div data-ai-id="guide-buehnen" style={{ marginBottom: 10 }}>
      <AnsichtLeiste ansicht={ansicht} onAnsicht={setAnsicht} hinweis={sitzung.hinweis} onHinweis={hinweisEreignis} kartenModus={!istBetreiberHier} />
      {verbindungshinweis}
      {mehrZiel === undefined ? statusleiste
        : mehrZiel ? createPortal(<>{statusleiste}<ZustandsLegende /></>, mehrZiel) : null}

      {/*
        WARTET AUF DICH — der Warteraum, Fragen an Rafael vorn (Rafael 29.09.2026: „Vor mir"
        und Warteraum zusammengelegt). Seit der Arbeitsliste (03.10.2026) heisst die Reihe
        so, wie sie gelesen wird; die Zahl der Fragen steht NUR hier, nicht mehr an jedem Raum.
      */}
      {/*
        OHNE PLATZ — OBEN UND LAUT (Rafael 09.10.2026, b-guide-jede-sitzung-ein-platz-20261009:
        „es soll eigentlich nur 3 modi geben - wartet auf dich, in arbeit und pausenraum - nur
        subsessions haben keine bühne und die kanzler und vize"). Bis heute stand dieselbe Liste
        als „Chats ohne Bühne" im Sitzungssaal und wurde dort mitgelesen wie Arbeit. Sie ist ein
        Defekt und steht deshalb vor allem anderen — nur wenn es welche gibt.
      */}
      {/* Platz-Block und Knöpfe nur für den Betreiber (Zweitprüfung 1, Befund 8). */}
      {istBetreiberHier && abgleich?.haus === effektivesHaus && (
        <section data-ai-id="guide-ohne-platz" style={{ marginBottom: abgleich.chats_ohne_buehne.length ? 12 : 0 }}>
          {abgleich.chats_ohne_buehne.length > 0 && <OhneBerater abgleich={abgleich} hierHaus={hierHaus} onOeffnenOhneBerater={onOeffnenOhneBerater} kompakt={ansicht === 'oberthema'} />}
          <StartGescheitertBlock liste={abgleich.buehnen_start_gescheitert} />
          <BeendetOhneErgebnisBlock hierHaus={hierHaus} onOeffnen={onOeffnenOhneBerater} />
        </section>
      )}
      {/* Freie Partner-Chats: eigener Abschnitt, kein Defekt — für Rafael (alle Partner) wie für den
          Partner selbst (das Tor liefert nur seine). Siehe `FreieChats`. */}
      {abgleich?.haus === effektivesHaus && <FreieChats abgleich={abgleich} />}

      {/* Der Warteraum steht NUR in „Räume (bisher)“ (Rafael, 24.10.2026); in „Ziele (neu)“ ersetzt ihn der Statusfilter „Wartet auf dich“. */}
      {ansicht === 'raum' && <WartetAufDichBlock ersteReihe={ersteReihe} lebend={lebend} mitFrage={mitFrage} mitVorschlag={mitVorschlag} offeneId={offeneId} onOeffnen={onOeffnen} />}

      {/*
        IN ARBEIT (Sitzungssaal) in voller Breite, darunter EINE Zeile fuer die Chats ohne
        Buehne (Regel 3: sie stehen im Sitzungssaal, bis sie eine Buehne haben). Bis
        03.10.2026 standen Sitzungssaal und Pausenraum als gleich hohe Kaesten nebeneinander;
        der kuerzere wurde auf die Hoehe des laengeren gestreckt, ein leerer Raum belegte
        die halbe Breite. Der Vorraum hat seit 23.09.2026 keinen eigenen Kasten mehr.
      */}
      <ZielePublizist zeilen={lebend} />
      {ansicht === 'oberthema' ? (
        <OberthemenListe zeilen={archivFilter ? imHaus.filter(b=>!!b.abschluss) : lebend} offeneId={offeneId} onOeffnen={onOeffnen} nutzerKennung={sitzungsNutzer}
          inArbeit={{ anzahl: sitzungssaal.length, zusatz: stillZusatz(sitzungssaal) }} zurueckgestellt={pausenraum.length} zurRaumAnsicht={() => setAnsicht('raum')}
          kopfZeile={abgleich?.haus === effektivesHaus ? <AemterArbeitZeile aemter={abgleich.aemter_arbeit} /> : null} />
      ) : (
        <>
      <RaumKasten raum="sitzungssaal" titel="In Arbeit" zeilen={sitzungssaal} offeneId={offeneId} onOeffnen={onOeffnen}
        zusatz={stillZusatz(sitzungssaal)}
        haus={effektivesHaus}
        kopfZeile={abgleich?.haus === effektivesHaus ? <AemterArbeitZeile aemter={abgleich.aemter_arbeit} /> : null}>
        {/*
          EIN CHAT OHNE BUEHNE STEHT IMMER IM SITZUNGSSAAL — Rafael, 21.09.2026: *"Eine
          Sitzung ist so lange im Sitzungssaal, bis sie eine Bühne hat und dann kommen sie
          zurück in den Warteraum. Vom Warteraum aus können sie in den Pausenraum verschoben
          werden. Sonst gibt es da keine Wege."* Die aeltere Anweisung vom 13.09. (*"Tote
          chats muessen in den Pausenraum"*) ist damit aufgehoben; `tot` steht an der ZEILE
          („ruht, weckbar"), nicht als Raum. Bis 03.10. stand dieselbe Liste zusaetzlich
          ganz unten als „Nicht im Hofstaat" — jetzt nur noch hier, siehe `OhneBerater`.
        */}
      </RaumKasten>

      {/*
        ZURUECKGESTELLT (Pausenraum) als eine aufklappbare Zeile. Der Stapel-Abschluss
        haengt hier (siehe Kommentar an `saalAuswahl`).
      */}
      <RaumKasten raum="pausenraum" titel="Von dir zurückgestellt" zeilen={pausenraum} offeneId={offeneId} onOeffnen={onOeffnen}
        haus={effektivesHaus} eingeklappt
        stapel={{
          auswaehlbar: (b: BuehneAnsicht) => b.offen.entscheidungen === 0,
          ausgewaehlt: saalAuswahl, toggle: saalToggle,
          grund: saalGrund, setGrund: setSaalGrund,
          sendet: saalSendet, ergebnis: saalErgebnis, absenden: saalAbsenden,
        }} />
        </>
      )}

      {/*
        DER TAGESPLAN STEHT HIER — unter den Raeumen, als EINE Zeile.
        Rafael, 11.09.: der obere Rand gehoert dem, was auf ihn wartet.
      */}
      <div style={{ marginTop: 10 }}><Tagesplan /></div>
    </div>
  );
}
